// CR081: Flyer Pipeline Steps Panel - Candidate-First Workflow with Diagnostics
// CR085: Added Truth Table terminal step (1.n)
// CR087: Added client-side Tesseract OCR support
// CR088: Fixed signals not loading from DB - now uses DEFAULT_SIGNALS when missing
// CR092: Added Auto Mode for sequential step execution (S1.0 → S1.3) with stop-on-failure
// Provides explicit steps: Render PDFs, OCR Scan, Signals Scoring, AI Deep Scan (candidates only)
// CR111: Added client-safe localStorage access and ErrorBoundary wrapper
// CR: Added validated payload unwrapping utilities for proper field access
// Lives in the Steps tab, NOT in Staging tab
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback, useMemo, useRef, Component, ErrorInfo, ReactNode } from "react";
import { FlyerImageCapturePanel } from "./FlyerImageCapturePanel";
// CR554: CollapsibleStepSection import removed — duplicate S1.0C eliminated
import { FlyerStepRow } from "./FlyerStepRow";
import type { StepRowStatus, MetricBadge } from "./FlyerStepRow";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useClientSideOcr } from "@/hooks/useClientSideOcr";
import {
  ImageIcon, Search, Zap, Loader2, CheckCircle, AlertCircle,
  ChevronDown, FileText, Play, RefreshCw, Upload, XCircle, ExternalLink,
  FastForward, Square, Eye, Gem, Network, Bug
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { RenderAllAndContinueDialog } from "./RenderAllAndContinueDialog";
import { S12DiagnosticsPanel } from "./s12-diagnostics";
import { FlyerAiDiagnosticsPanel } from "./FlyerAiDiagnosticsPanel";
import { FlyerTruthTable } from "./flyer-truth-table";
// CR551: S5WorkflowPackSelector import removed (S1.5 deleted)
import { S14PromptPackSelector } from "./S14PromptPackSelector";
import { S14CropperPackSelector } from "./S14CropperPackSelector";
import { S14AttemptTimeline } from "./S14AttemptTimeline";
import { getDealType } from "@/lib/batch-runs/validatedPayloadUtils";
import { StepModeToggle } from "./StepModeToggle";
import type { CandidateSignal } from "@/lib/flyer-pipeline-v2/types";
import { getOcrEngineLabel, normalizeOcrEngine, type OcrEngineValue } from "@/lib/batch-runs/ocrEngine";
import {
  PipelineDisplayStatus,
  getPipelineDisplayStatus,
  getPipelineStatusHelperText,
} from "@/lib/batch-runs/pipelineDisplayStatus";
import {
  getEffectiveAttemptStatus,
  isTerminalAttemptStatus,
} from "@/lib/batch-runs/attemptStatus";
import {
  advanceSnapshotStability,
  createInitialSnapshotStabilityState,
  type SnapshotStabilityPhase,
} from "@/lib/batch-runs/snapshotStability";
import {
  preferCanonicalStepStatus,
  shouldLockSignalsWhileOcrRuns,
} from "@/lib/batch-runs/s1StepState";
import { getS1AutoModeStepSequence, type S1AutoModeStepKey } from "@/lib/batch-runs/s1AutoModeSequence";

// CR082: Feature flag to disable diagnostics panels if they cause issues
// Set to false temporarily if diagnostics are crashing the page
const ENABLE_FLYER_DIAGNOSTICS = true;

// CR088: DEFAULT_SIGNALS used when batch run has no signals in config_json
// This ensures OCR always has valid signals to work with
// These must match S0ScanSettingsAccordion.DEFAULT_SIGNALS for consistency
const DEFAULT_SIGNALS: CandidateSignal[] = [
  {
    id: 'sig_giftcard_term',
    name: "Contains 'gift card(s)'",
    type: 'KEYWORD',
    enabled: true,
    weight: 6,
    params: { terms: ['gift card', 'gift cards'], match_mode: 'ANY', case_sensitive: false }
  },
  {
    id: 'sig_currency_amount',
    name: 'Contains currency amount ($ + digits)',
    type: 'REGEX',
    enabled: true,
    weight: 1,
    params: { pattern: '\\$\\s*\\d{1,4}', min_matches: 1 }
  },
  {
    id: 'sig_verb_near_giftcard',
    name: "Qualifying verb near 'gift card'",
    type: 'PROXIMITY',
    enabled: true,
    weight: 6,
    params: {
      anchor_terms: ['gift card', 'gift cards'],
      near_terms: ['buy', 'purchase', 'spend', 'when you buy', 'when you purchase', 'get'],
      window_chars: 80
    }
  },
  {
    id: 'sig_spend_get_pattern',
    name: 'Spend/get promo pattern',
    type: 'REGEX',
    enabled: true,
    weight: 8,
    params: {
      pattern: '(spend|buy|purchase|with the purchase of)\\s*\\$?\\d{1,4}[\\s\\S]{0,120}(get|receive|earn)\\s*\\$?\\d{1,4}',
      min_matches: 1
    }
  },
  {
    id: 'sig_bonus_reward_language',
    name: 'Bonus/reward language',
    type: 'KEYWORD',
    enabled: true,
    weight: 2,
    params: { terms: ['bonus', 'get $', 'receive $', 'earn', 'with purchase'], match_mode: 'ANY', case_sensitive: false }
  },
  {
    id: 'sig_partner_offer_negative',
    name: 'Partner-offer language (negative)',
    type: 'NEGATIVE_KEYWORDS',
    enabled: true,
    weight: -6,
    params: {
      terms: ['partner offers', 'learn more', 'conditions apply', 'free trial', 'in value', 'streaming', 'hotels', 'travel', 'gas'],
      mode: 'SUBTRACT'
    }
  }
];

// CR092: Auto Mode step keys and sequence
type AutoModeStepKey = S1AutoModeStepKey;
type AutoModeStatus = "IDLE" | "RUNNING" | "STOPPED" | "COMPLETE";
type AutoModeStopReason = "FAILED" | "PARTIAL" | "ZERO_OUTPUT" | "USER_STOP" | "TIMEOUT" | null;

interface AutoModeState {
  enabled: boolean;
  status: AutoModeStatus;
  currentStepKey: AutoModeStepKey | null;
  lastCompletedStepKey: AutoModeStepKey | null;
  startedAt: string | null;
  stoppedAt: string | null;
  stopReason: AutoModeStopReason;
}

const AUTO_STEP_LABELS: Record<AutoModeStepKey, string> = {
  S1_CAPTURE: "1.0c Capture",
  S1_COLLECT: "1.0 Collect Flyers",
  S1_RENDER: "1.1 Render Pages",
  S1_OCR: "1.2 OCR Scan",
  S1_SIGNALS: "1.3 Signals (Candidate Scoring)",
  S1_AI: "1.4 Deep Scan",
  S1_4: "1.5 Locate & Crop",
};

export interface FlyerPipelineStats {
  flyersTotal: number;           // count of cr075_staging_deal_items (FLYER)
  pendingRenderFlyers: number;   // PDF path only
  renderedFlyers: number;        // PDF path only (render_status=RENDERED)
  renderedPages: number;         // BOTH paths: rendered page images available for OCR
  ocrPages: number;              // pages in terminal OCR state (SUCCEEDED + FAILED)
  ocrFailed: number;             // pages with OCR FAILED
  ocrQueued: number;             // pages currently QUEUED (stranded if no run active)
  scoredPages: number;           // pages with signal scoring complete
  signalHitPages: number;        // pages where one or more signals matched (candidate_score > 0)
  droppedPages: number;          // OCR/scored pages dropped below threshold
  candidatePages: number;        // pages passing candidate scoring
  aiFlyersComplete: number;      // flyers with AI extraction complete
  dealsExtracted: number;        // total deals extracted
  /** CR494: True when capture manifests indicate incomplete capture */
  captureIncomplete?: boolean;
  /** CR494: Expected total pages from manifests */
  captureExpectedPages?: number;
  /** CR494: Registry page count */
  registryPages?: number;
  /** CR_FIX: Count of pages with rendered_at populated in truth table */
  truthRenderedPages?: number;
}

/** CR455: Single source of truth for OCR readiness */
export const isOcrReady = (s: FlyerPipelineStats) => s.renderedPages > 0;

interface CollectStepInfo {
  status: "NOT_STARTED" | "RUNNING" | "COMPLETE" | "PARTIAL" | "FAILED";
  flyersQueued: number;
}

interface OcrNetworkTrace {
  capturedAt: string;
  request: {
    batch_run_id: string;
    force_reprocess: boolean;
    retry_failed_only: boolean;
  };
  response?: unknown;
  error?: {
    name?: string;
    message: string;
    stack?: string;
  };
}

interface AiNetworkTrace {
  capturedAt: string;
  request: {
    batch_run_id: string;
  };
  response?: unknown;
  error?: {
    name?: string;
    message: string;
    stack?: string;
  };
}

interface FlyerPipelineStepsPanelProps {
  batchRunId: string;
  onRefresh: () => void;
  onRunCollect?: () => Promise<void>;
  collectStepInfo?: CollectStepInfo;
  /** CR121: Persisted auto mode enabled state from parent */
  autoModeEnabled?: boolean;
  /** CR121: Callback when auto mode toggle changes */
  onAutoModeToggle?: (enabled: boolean) => void;
  /** CR121: Whether the parent is orchestrating (cross-group chaining) */
  isParentOrchestrating?: boolean;
  /** CR121: Callback to notify parent that S1 is complete */
  onS1Complete?: () => void;
  /** CR450: Flyer capture mode from batch run config */
  flyerCaptureMode?: string | null;
  /** Hide S1-local auto controls when top-level orchestrator owns auto-run. */
  hideLocalAutoControls?: boolean;
  /** Signal from parent to start S1 auto chain. */
  parentAutoStartSignal?: number;
}

// CR111: ErrorBoundary to prevent blank page crashes
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class FlyerPipelineErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[FlyerPipelineStepsPanel] Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-destructive">
              <XCircle className="h-4 w-4" />
              S1: Flyer Extraction - Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
              <p className="text-sm text-destructive font-medium mb-2">
                An error occurred in the Flyer Pipeline panel:
              </p>
              <pre className="text-xs text-muted-foreground overflow-auto max-h-24">
                {this.state.error?.message || "Unknown error"}
              </pre>
              <button
                className="mt-3 text-xs underline text-muted-foreground hover:text-foreground"
                onClick={() => this.setState({ hasError: false, error: null })}
              >
                Try again
              </button>
            </div>
          </CardContent>
        </Card>
      );
    }
    return this.props.children;
  }
}

// CR551: LegacySnippetCropTile removed — S1.4 handles both locate + crop

/** CR554: S1.4 state exposed for FlyerStepRow header integration */
interface SnippetLocatorState {
  status: StepRowStatus;
  badges: MetricBadge[];
  isRunning: boolean;
  handleRun: () => void;
  canRun: boolean;
  runLabel: string;
  runVariant: "default" | "outline" | "destructive";
  locatedCount: number;
  croppedCount: number;
}

const S14_RUNNING_STALE_MS = 5 * 60 * 1000;

function useSnippetLocatorState(
  batchRunId: string,
  options?: {
    allowManualTrigger?: boolean;
    aiComplete?: boolean;
    latestAiAttemptId?: string | null;
    defaultLocatorPackId?: string | null;
    defaultCropperPackId?: string | null;
    autoModeEnabled?: boolean;
    settingsRowFound?: boolean | null;
    onNetworkTrace?: (trace: {
      capturedAt: string;
      request: Record<string, unknown>;
      response?: Record<string, unknown>;
      error?: { name?: string; message: string; stack?: string };
    }) => void;
  }
): SnippetLocatorState {
  const { toast } = useToast();
  const [attemptInfo, setAttemptInfo] = useState<{
    status: string | null;
    artifactsAttempted: number;
    artifactsInserted: number;
    artifactErrors: number;
    // CR562: Raw error objects for inline display
    errors: { error: string; staging_item_id?: string }[];
    inputMissing: number;
    selfHealed: number;
    aiAttemptIdUsed: string | null;
    aiAttemptSource: string | null;
    filteredByAttempt: number;
    cropperAttempted: number;
    cropperUsed: number;
    effectiveRunPolicy: string | null;
    attemptNo: number | null;
    attemptId: string | null;
    skippedReason: string | null;
    copiedForwardCount: number | null;
    copiedFromAttemptId: string | null;
    oversizeGuardEnabledUsed: boolean | null;
    oversizeGuardThresholdMpUsed: number | null;
    oversizeGuardCount: number;
    oversizeGuardSkippedCropCount: number;
    oversizeGuardCandidates: Array<{
      staging_item_id: string;
      page_number: number;
      width: number;
      height: number;
      page_mp: number;
      threshold_mp: number;
    }>;
    oversizeGuardLastDecision: {
      staging_item_id?: string;
      page_number?: number;
      width?: number | null;
      height?: number | null;
      page_mp?: number | null;
      threshold_mp?: number | null;
      enabled?: boolean;
      triggered?: boolean;
      unknown_dims?: boolean;
      action?: string;
      reason?: string;
    } | null;
    // CR553: Locator progress
    s14Locator: {
      total_candidates: number;
      processed_candidates: number;
      success_count: number;
      failure_count: number;
      remaining_candidates: number;
      locator_complete: boolean;
    } | null;
    // CR552: Incremental progress
    s14Progress: {
      total_candidates: number;
      processed: number;
      located: number;
      cropped: number;
      filtered_out: number;
      skipped: number;
      pct_complete: number;
      last_candidate_page: number | null;
      last_candidate_id: string | null;
    } | null;
    s14StageStats: {
      candidate_started?: number;
      preprocess_started?: number;
      preprocess_completed?: number;
      preprocess_failed?: number;
      dims_check_started?: number;
      dims_check_failed?: number;
      oversize_guard_evaluated?: number;
      oversize_guard_triggered?: number;
      oversize_guard_unknown_dims?: number;
      locator_started?: number;
      locator_completed?: number;
      pass_b_started?: number;
      pass_b_completed?: number;
      crop_started?: number;
      crop_completed?: number;
      candidate_completed?: number;
      candidate_failed?: number;
      last_stage?: string | null;
      last_error?: string | null;
    } | null;
    // CR552: DB-level running check
    hasRunningAttemptInDb: boolean;
    // CR681: Stable summary counts from latest completed attempt (prevents 0-pill during fresh RUNNING attempt)
    summaryArtifactsInserted: number;
    summaryCropperUsed: number;
    summaryS14Locator: {
      total_candidates: number;
      processed_candidates: number;
      success_count: number;
      failure_count: number;
      remaining_candidates: number;
      locator_complete: boolean;
    } | null;
    summaryS14Progress: {
      total_candidates: number;
      processed: number;
      located: number;
      cropped: number;
      filtered_out: number;
      skipped: number;
      pct_complete: number;
      last_candidate_page: number | null;
      last_candidate_id: string | null;
    } | null;
    // CR607: No-work diagnostics
    noWork: { reason_code: string; detail: string;[k: string]: any } | null;
  }>({ status: null, artifactsAttempted: 0, artifactsInserted: 0, artifactErrors: 0, errors: [], inputMissing: 0, selfHealed: 0, aiAttemptIdUsed: null, aiAttemptSource: null, filteredByAttempt: 0, cropperAttempted: 0, cropperUsed: 0, effectiveRunPolicy: null, attemptNo: null, attemptId: null, skippedReason: null, copiedForwardCount: null, copiedFromAttemptId: null, oversizeGuardEnabledUsed: null, oversizeGuardThresholdMpUsed: null, oversizeGuardCount: 0, oversizeGuardSkippedCropCount: 0, oversizeGuardCandidates: [], oversizeGuardLastDecision: null, s14Locator: null, s14Progress: null, s14StageStats: null, hasRunningAttemptInDb: false, summaryArtifactsInserted: 0, summaryCropperUsed: 0, summaryS14Locator: null, summaryS14Progress: null, noWork: null });
  const [isRunning, setIsRunning] = useState(false);
  const [selectedLocatorPackId, setSelectedLocatorPackId] = useState<string | null>(options?.defaultLocatorPackId || null);
  const [selectedCropperPackId, setSelectedCropperPackId] = useState<string | null>(options?.defaultCropperPackId || null);

  // CRxxx: Sync defaults if local state is null, ensuring parent updates (e.g. from app_scan_settings)
  // are picked up without overwriting manual user selections.
  useEffect(() => {
    if (!selectedLocatorPackId && options?.defaultLocatorPackId) {
      setSelectedLocatorPackId(options.defaultLocatorPackId);
    }
  }, [options?.defaultLocatorPackId]);

  useEffect(() => {
    if (!selectedCropperPackId && options?.defaultCropperPackId) {
      setSelectedCropperPackId(options.defaultCropperPackId);
    }
  }, [options?.defaultCropperPackId]);

  const [aiComplete, setAiComplete] = useState(false);
  const [latestAiAttemptId, setLatestAiAttemptId] = useState<string | null>(null);

  const fetchAttempt = useCallback(async () => {
    const { data } = await supabase
      .from("cr075_batch_run_step_attempts")
      .select("id, attempt_no, status, diagnostics_json, records_out, started_at")
      .eq("batch_run_id", batchRunId)
      .in("step_key", ["S1_4", "S1_SNIPPET"])
      .not("status", "eq", "SUPERSEDED")
      .order("created_at", { ascending: false })
      .limit(5);

    if (data && data.length > 0) {
      const nowMs = Date.now();
      const isStaleRunning = (row: any): boolean => {
        const effectiveStatus = getEffectiveAttemptStatus(row);
        if (effectiveStatus !== "RUNNING") return false;
        const startedMs = row.started_at ? Date.parse(row.started_at) : NaN;
        if (!Number.isFinite(startedMs)) return true;
        return nowMs - startedMs > S14_RUNNING_STALE_MS;
      };
      const mapped = data.map((row: any) => ({ ...row, _effective_status: getEffectiveAttemptStatus(row) }));
      // CR608: Prefer only non-stale RUNNING attempt for active state.
      const running = mapped.find(d => d._effective_status === "RUNNING" && !isStaleRunning(d));
      const bestCompleted = mapped.find(d => {
        const diag = d.diagnostics_json as Record<string, unknown> | null;
        return diag?.artifacts_attempted != null;
      }) || mapped[0];
      const best = running ?? bestCompleted;
      const diag = best.diagnostics_json as Record<string, unknown> | null;
      const summaryDiag = ((bestCompleted?.diagnostics_json as Record<string, unknown> | null) ?? diag);
      const errArr = diag?.artifact_insert_errors as unknown[] | null;
      const effectiveBestStatus = (best as any)._effective_status ?? best.status;
      const statusGuardInput = String((diag as any)?.status_guard?.input_status ?? "").toUpperCase();
      const completionTerminalSemantic = String((diag as any)?.s14_completion_semantics?.terminal_semantic ?? "").toUpperCase();
      const semanticBestStatus = (
        effectiveBestStatus === "PARTIAL" &&
          (statusGuardInput === "COMPLETE_WITH_TAIL" || completionTerminalSemantic === "COMPLETE_WITH_TAIL")
      )
        ? "COMPLETE_WITH_TAIL"
        : effectiveBestStatus;
      setAttemptInfo({
        status: semanticBestStatus,
        artifactsAttempted: (diag?.artifacts_attempted as number) ?? 0,
        artifactsInserted: (diag?.artifacts_inserted as number) ?? 0,
        artifactErrors: errArr?.length ?? 0,
        // CR562: Pass raw error objects for inline display
        errors: (errArr as { error: string; staging_item_id?: string }[] | null) ?? [],
        inputMissing: (diag?.input_missing_count as number) ?? 0,
        selfHealed: (diag?.self_healed as number) ?? 0,
        aiAttemptIdUsed: (diag?.ai_attempt_id_used as string) ?? null,
        aiAttemptSource: (diag?.ai_attempt_source as string) ?? null,
        filteredByAttempt: (diag?.filtered_out_by_attempt as number) ?? 0,
        cropperAttempted: (diag?.cropper_attempted_count as number) ?? 0,
        cropperUsed: (diag?.cropper_used_count as number) ?? 0,
        effectiveRunPolicy: (diag?.effective_run_policy as string) ?? null,
        attemptNo: (best as any).attempt_no ?? null,
        attemptId: (best as any).id ?? null,
        skippedReason: (diag?.skipped_reason as string) ?? null,
        copiedForwardCount: (diag?.copied_forward_count as number) ?? null,
        copiedFromAttemptId: (diag?.copied_from_attempt_id as string) ?? null,
        oversizeGuardEnabledUsed: (diag?.oversize_guard_enabled_used as boolean) ?? null,
        oversizeGuardThresholdMpUsed: (diag?.oversize_guard_threshold_mp_used as number) ?? null,
        oversizeGuardCount: (diag?.oversize_guard_count as number) ?? 0,
        oversizeGuardSkippedCropCount: (diag?.oversize_guard_skipped_crop_count as number) ?? 0,
        oversizeGuardCandidates: (diag?.oversize_guard_candidates as any[]) ?? [],
        oversizeGuardLastDecision: (diag?.oversize_guard_last_decision as any) ?? null,
        s14Locator: (diag?.s14_locator as any) ?? null,
        s14Progress: (diag?.s14_progress as any) ?? null,
        s14StageStats: (diag?.s14_stage_stats as any) ?? null,
        hasRunningAttemptInDb: mapped.some(d => d._effective_status === "RUNNING" && !isStaleRunning(d)),
        summaryArtifactsInserted: (summaryDiag?.artifacts_inserted as number) ?? 0,
        summaryCropperUsed: (summaryDiag?.cropper_used_count as number) ?? 0,
        summaryS14Locator: (summaryDiag?.s14_locator as any) ?? null,
        summaryS14Progress: (summaryDiag?.s14_progress as any) ?? null,
        // CR607: No-work diagnostics
        noWork: (diag?.no_work as any) ?? null,
      });
      if (semanticBestStatus === "RUNNING") setIsRunning(true);
      else setIsRunning(false);
    }
  }, [batchRunId]);

  useEffect(() => { fetchAttempt(); }, [fetchAttempt]);
  useEffect(() => {
    const interval = setInterval(fetchAttempt, isRunning ? 3000 : 6000);
    return () => clearInterval(interval);
  }, [isRunning, fetchAttempt]);

  useEffect(() => {
    // CRxxx: S1.5 AI completion is now passed in via options.aiComplete
    setAiComplete(options?.aiComplete ?? false);
    setLatestAiAttemptId(options?.latestAiAttemptId ?? null);
  }, [options?.aiComplete, options?.latestAiAttemptId]);

  const effectiveCropperPackId = selectedCropperPackId ?? options?.defaultCropperPackId ?? null;
  const effectiveLocatorPackId = selectedLocatorPackId ?? options?.defaultLocatorPackId ?? null;

  const cropperMissing = !effectiveCropperPackId;

  const handleRun = useCallback(async () => {
    if (cropperMissing && options?.autoModeEnabled) {
      toast({ title: "Cropper pack required", description: "Select an S1.5 Cropper pack before running.", variant: "destructive" });
      return;
    }
    setIsRunning(true);
    let aiAttemptIdForRun: string | null = latestAiAttemptId;
    try {
      const { data: latestAiRow } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("id")
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S1_AI")
        .not("status", "eq", "SUPERSEDED")
        .in("status", ["COMPLETE", "COMPLETE_WITH_ERRORS"])
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (latestAiRow?.id) {
        aiAttemptIdForRun = latestAiRow.id;
        setLatestAiAttemptId(latestAiRow.id);
      }
    } catch (err) {
      console.warn("[S1.5] Failed to refresh latest S1_AI attempt id before run:", err);
    }

    const requestBody: Record<string, unknown> = { batch_run_id: batchRunId, mode: "MANUAL", auto_crop: true };
    if (effectiveLocatorPackId) requestBody.prompt_pack_id = effectiveLocatorPackId;
    if (effectiveCropperPackId) requestBody.cropper_prompt_pack_id = effectiveCropperPackId;
    if (aiAttemptIdForRun) requestBody.ai_attempt_id = aiAttemptIdForRun;
    try {
      options?.onNetworkTrace?.({
        capturedAt: new Date().toISOString(),
        request: requestBody,
      });
      const response = await supabase.functions.invoke("batch-run-flyer-snippet-s14", { body: requestBody });
      if (response.error) throw response.error;
      const result = response.data;
      options?.onNetworkTrace?.({
        capturedAt: new Date().toISOString(),
        request: requestBody,
        response: result,
      });
      const located = result?.artifacts_inserted ?? result?.located ?? result?.records_out ?? 0;
      const errors = result?.errors ?? 0;
      const cropperAttempted = result?.cropper_attempted_count ?? result?.cropper_attempted ?? 0;
      const cropperUsed = result?.cropper_used_count ?? result?.cropper_used ?? 0;
      const parts: string[] = [];
      if (located > 0) parts.push(`${located} located`);
      if (cropperAttempted > 0) parts.push(`Cropper: ${cropperUsed}/${cropperAttempted} refined`);
      if (errors > 0) parts.push(`${errors} errors`);
      toast({ title: "S1.4 Complete — Locate + Crop", description: parts.length > 0 ? parts.join(" · ") : "0 snippets processed" });
    } catch (err) {
      let latestStatus: string | null = null;
      let latestAttemptNo: number | null = null;
      try {
        const { data: latestAttempt } = await supabase
          .from("cr075_batch_run_step_attempts")
          .select("attempt_no, status")
          .eq("batch_run_id", batchRunId)
          .in("step_key", ["S1_4", "S1_SNIPPET"])
          .not("status", "eq", "SUPERSEDED")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        latestStatus = latestAttempt?.status ?? null;
        latestAttemptNo = latestAttempt?.attempt_no ?? null;
      } catch (statusErr) {
        console.warn("[S1.4] Failed to read latest attempt status after invoke error:", statusErr);
      }
      options?.onNetworkTrace?.({
        capturedAt: new Date().toISOString(),
        request: requestBody,
        error: {
          name: err instanceof Error ? err.name : "Error",
          message: err instanceof Error ? err.message : "Unknown error",
          stack: err instanceof Error ? err.stack : undefined,
        },
      });
      if (latestStatus === "RUNNING") {
        const attemptLabel = latestAttemptNo != null ? ` (attempt #${latestAttemptNo})` : "";
        toast({
          title: "S1.4 Request Interrupted",
          description: `Function call failed, but S1.4 is still RUNNING${attemptLabel}. Check live diagnostics before retrying.`,
          variant: "destructive",
        });
      } else {
        toast({ title: "S1.4 Failed", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
      }
    } finally {
      setIsRunning(false);
      fetchAttempt();
    }
  }, [batchRunId, cropperMissing, effectiveLocatorPackId, effectiveCropperPackId, latestAiAttemptId, fetchAttempt, options, toast]);

  // Compute status
  const isComplete =
    attemptInfo.status === "COMPLETE" ||
    attemptInfo.status === "COMPLETE_WITH_ERRORS" ||
    attemptInfo.status === "COMPLETE_WITH_TAIL";
  const isFailed = attemptInfo.status === "FAILED";
  const running = attemptInfo.status === "RUNNING" || isRunning || attemptInfo.hasRunningAttemptInDb;

  let status: StepRowStatus = "not_started";
  if (running) status = "running";
  else if (isFailed) status = "failed";
  else if (isComplete) status = "complete";
  else if (!aiComplete) status = "not_started";
  else status = "ready";

  // CR561: Show snippet count badge for any terminal status with artifacts (not just COMPLETE)
  const hasSnippets = (attemptInfo.artifactsInserted ?? 0) > 0;

  // Compute badges
  const badges: MetricBadge[] = [];
  if (isComplete || (isFailed && hasSnippets)) {
    badges.push({ label: `${attemptInfo.artifactsInserted} located` });
    if (attemptInfo.cropperAttempted > 0) badges.push({ label: `Cropper: ${attemptInfo.cropperUsed}/${attemptInfo.cropperAttempted}` });
  }
  if (attemptInfo.attemptNo != null) badges.push({ label: `Attempt #${attemptInfo.attemptNo}` });

  const runLabel = running ? "Running..." : isFailed ? "Retry Failed" : isComplete ? "Re-run" : "Run";
  const runVariant: "default" | "outline" | "destructive" = isFailed ? "destructive" : isComplete ? "outline" : "default";
  const canRun = !running; // CRxxx: Always allow manual run if not already running

  // Expose selectedLocatorPackId/selectedCropperPackId setters via a ref stored on the hook return
  // We attach them as extra properties accessed by the body component
  const stateRef = useRef({ selectedLocatorPackId, setSelectedLocatorPackId, selectedCropperPackId, setSelectedCropperPackId, attemptInfo, navigate: null as any, autoModeEnabled: options?.autoModeEnabled ?? false, settingsRowFound: options?.settingsRowFound ?? null });
  stateRef.current = { selectedLocatorPackId, setSelectedLocatorPackId, selectedCropperPackId, setSelectedCropperPackId, attemptInfo, navigate: null, autoModeEnabled: options?.autoModeEnabled ?? false, settingsRowFound: options?.settingsRowFound ?? null };

  // CR552: Prefer live s14_progress (written incrementally) over s14_locator (written at finalization)
  const liveProgress = (isRunning || running) && attemptInfo?.s14Progress ? attemptInfo.s14Progress : null;
  const hasLiveSummaryProgress =
    ((liveProgress?.processed ?? 0) > 0) ||
    ((liveProgress?.located ?? 0) > 0) ||
    ((liveProgress?.cropped ?? 0) > 0);
  const summaryProgress = attemptInfo?.summaryS14Progress;
  const summaryLocator = attemptInfo?.summaryS14Locator;
  const locatedFallback =
    summaryProgress?.located ??
    summaryLocator?.success_count ??
    attemptInfo?.summaryArtifactsInserted ??
    attemptInfo?.s14Locator?.success_count ??
    (isComplete ? (attemptInfo.artifactsInserted ?? 0) : 0);
  const croppedFallback =
    summaryProgress?.cropped ??
    attemptInfo?.summaryCropperUsed ??
    attemptInfo?.cropperUsed ??
    0;
  const locatedCount = hasLiveSummaryProgress ? (liveProgress?.located ?? locatedFallback) : locatedFallback;
  const croppedCount = hasLiveSummaryProgress ? (liveProgress?.cropped ?? croppedFallback) : croppedFallback;

  return { status, badges, isRunning: running, handleRun, canRun, runLabel, runVariant, locatedCount, croppedCount, _stateRef: stateRef } as any;
}

/** CR554: S1.4 body-only content (no header — header is in FlyerStepRow) */
function SnippetLocatorBody({ batchRunId, stateRef }: { batchRunId: string; stateRef: React.MutableRefObject<any> }) {
  const navigate = useNavigate();
  const { selectedLocatorPackId, setSelectedLocatorPackId, selectedCropperPackId, setSelectedCropperPackId, attemptInfo, autoModeEnabled, settingsRowFound } = stateRef.current;

  const isComplete =
    attemptInfo?.status === "COMPLETE" ||
    attemptInfo?.status === "COMPLETE_WITH_ERRORS" ||
    attemptInfo?.status === "COMPLETE_WITH_TAIL";
  const isFailed = attemptInfo?.status === "FAILED";
  const isCancelled = attemptInfo?.status === "CANCELLED";
  const isRunning = attemptInfo?.status === "RUNNING" || attemptInfo?.hasRunningAttemptInDb;
  // CR561: Allow viewing snippets even when step is FAILED
  const hasSnippets = (attemptInfo?.artifactsInserted ?? 0) > 0;
  const s14Locator = attemptInfo?.s14Locator;
  const s14Progress = attemptInfo?.s14Progress;
  const s14StageStats = attemptInfo?.s14StageStats;

  // CR552: Prefer live s14_progress (written incrementally) over s14_locator (written at finalization)
  const liveProgress = isRunning && s14Progress ? s14Progress : null;
  const hasLiveSummaryProgress =
    ((liveProgress?.processed ?? 0) > 0) ||
    ((liveProgress?.located ?? 0) > 0) ||
    ((liveProgress?.cropped ?? 0) > 0);
  const summaryProgress = attemptInfo?.summaryS14Progress;
  const summaryLocator = attemptInfo?.summaryS14Locator;
  const totalCandidates = (hasLiveSummaryProgress ? liveProgress?.total_candidates : null) ?? summaryProgress?.total_candidates ?? s14Locator?.total_candidates ?? 0;
  const processedCandidates = (hasLiveSummaryProgress ? liveProgress?.processed : null) ?? summaryProgress?.processed ?? s14Locator?.processed_candidates ?? 0;
  const locatedCount = (hasLiveSummaryProgress ? liveProgress?.located : null) ?? summaryProgress?.located ?? summaryLocator?.success_count ?? s14Locator?.success_count ?? 0;
  const failedCount = (hasLiveSummaryProgress ? liveProgress?.filtered_out : null) ?? summaryProgress?.filtered_out ?? summaryLocator?.failure_count ?? s14Locator?.failure_count ?? 0;
  const croppedCount = (hasLiveSummaryProgress ? liveProgress?.cropped : null) ?? summaryProgress?.cropped ?? attemptInfo?.summaryCropperUsed ?? 0;
  const locatorComplete = !isRunning && (s14Locator?.locator_complete === true || (isComplete && totalCandidates > 0));
  const progressPct = totalCandidates === 0
    ? (isRunning ? 0 : 100)
    : Math.round((processedCandidates / totalCandidates) * 100);

  // CR607: No-work detection
  const noWork = attemptInfo?.noWork;
  // CR613: "0 of 0" while RUNNING means still resolving or truly empty — show preparing state
  const isPreparingZeroWork = isRunning && totalCandidates === 0 && processedCandidates === 0;
  const oversizeGuardEnabled = attemptInfo?.oversizeGuardEnabledUsed;
  const oversizeGuardThreshold = attemptInfo?.oversizeGuardThresholdMpUsed;
  const oversizeGuardCount = attemptInfo?.oversizeGuardCount ?? 0;
  const oversizeGuardCandidates = attemptInfo?.oversizeGuardCandidates ?? [];
  const oversizeGuardLastDecision = attemptInfo?.oversizeGuardLastDecision ?? null;

  return (
    <div className="space-y-3">
      {settingsRowFound === false && (
        <Alert variant="destructive" className="py-2">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle className="text-xs">System Settings Missing</AlertTitle>
          <AlertDescription className="text-xs">
            The <code>app_scan_settings</code> row is missing in the database. S1.5 defaults cannot be loaded.
          </AlertDescription>
        </Alert>
      )}

      {settingsRowFound !== false && !selectedCropperPackId && autoModeEnabled && (
        <Alert variant="destructive" className="py-2">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle className="text-xs">Auto Mode Gated</AlertTitle>
          <AlertDescription className="text-xs">
            S1.5 Cropper configuration is missing. Select a cropper prompt pack to continue the automated pipeline.
          </AlertDescription>
        </Alert>
      )}

      <S14PromptPackSelector selectedSnapshotId={selectedLocatorPackId} onSelect={setSelectedLocatorPackId} />
      <S14CropperPackSelector selectedPackId={selectedCropperPackId} onSelect={setSelectedCropperPackId} />
      {/* CR607: No-work banner — shows when attempt completed with zero candidates */}
      {noWork && !isRunning && (
        <div className="rounded-md border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          <span className="font-medium">No candidates to process</span>
          <span className="ml-1.5">— {noWork.detail || noWork.reason_code}</span>
        </div>
      )}
      <div className="rounded-md border border-amber-300/50 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800/50 px-3 py-2 text-xs text-muted-foreground space-y-1">
        <div>
          <span className="font-medium text-foreground">Oversize Guard:</span>{" "}
          {oversizeGuardEnabled == null ? "unknown" : oversizeGuardEnabled ? "ON" : "OFF"}
          {oversizeGuardThreshold != null ? ` · threshold ${oversizeGuardThreshold} MP` : ""}
        </div>
        <div>
          <span className="font-medium text-foreground">Oversize guard hits this attempt:</span>{" "}
          {oversizeGuardCount}
        </div>
        {oversizeGuardLastDecision && (
          <div className="pt-0.5 text-[11px]">
            <span className="font-medium text-foreground">Current decision:</span>{" "}
            p{oversizeGuardLastDecision.page_number ?? "?"}{" "}
            {oversizeGuardLastDecision.width && oversizeGuardLastDecision.height
              ? `${oversizeGuardLastDecision.width}x${oversizeGuardLastDecision.height}`
              : "dims unknown"}{" "}
            ({oversizeGuardLastDecision.page_mp != null ? `${Number(oversizeGuardLastDecision.page_mp).toFixed(2)} MP` : "MP unknown"})
            {" · "}
            {oversizeGuardLastDecision.triggered
              ? `>${oversizeGuardLastDecision.threshold_mp ?? "?"} MP → Downscale Protected (${oversizeGuardLastDecision.action ?? "CONTINUE_WITH_DOWNSCALE_PROTECTION"})`
              : `<=${oversizeGuardLastDecision.threshold_mp ?? "?"} MP → run Pass B + crop`}
          </div>
        )}
        {oversizeGuardCandidates.length > 0 && (
          <div className="space-y-0.5 pt-0.5">
            {oversizeGuardCandidates.slice(0, 3).map((c) => (
              <div key={`${c.staging_item_id}:${c.page_number}`} className="text-[11px]">
                • p{c.page_number} {c.width > 0 && c.height > 0 ? `${c.width}x${c.height}` : "dims unknown"} ({Number(c.page_mp).toFixed(2)} MP) &gt; {c.threshold_mp} MP → Downscale Protected
              </div>
            ))}
            {oversizeGuardCandidates.length > 3 && (
              <div className="text-[11px] text-muted-foreground">+{oversizeGuardCandidates.length - 3} more</div>
            )}
          </div>
        )}
        {isRunning && (
          <div className="pt-1 text-[11px] text-muted-foreground space-y-0.5">
            <div>
              <span className="font-medium text-foreground">Live stage:</span>{" "}
              {s14StageStats?.last_stage ?? "unknown"}
            </div>
            {s14StageStats?.last_error ? (
              <div className="text-destructive/80 truncate" title={s14StageStats.last_error}>
                Last error: {s14StageStats.last_error}
              </div>
            ) : null}
            <div>
              locator {s14StageStats?.locator_started ?? 0}/{s14StageStats?.locator_completed ?? 0}
              {" · "}passB {s14StageStats?.pass_b_started ?? 0}/{s14StageStats?.pass_b_completed ?? 0}
              {" · "}crop {s14StageStats?.crop_started ?? 0}/{s14StageStats?.crop_completed ?? 0}
            </div>
          </div>
        )}
      </div>
      {/* CR613: Cancelled banner */}
      {isCancelled && !noWork && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-muted-foreground">
          <span className="font-medium">Attempt cancelled</span>
          {processedCandidates > 0 && (
            <span className="ml-1.5">— {processedCandidates} of {totalCandidates} candidates processed before stop</span>
          )}
        </div>
      )}
      {/* CR552: Progress bar — shows during RUNNING with live progress, and after completion */}
      {!noWork && !isPreparingZeroWork && (isRunning || (isComplete && totalCandidates > 0) || (isCancelled && totalCandidates > 0)) && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {isRunning
                ? `Locating snippets… ${processedCandidates} of ${totalCandidates}`
                : isCancelled
                  ? `Cancelled: ${processedCandidates} / ${totalCandidates} processed`
                  : `Located: ${processedCandidates} / ${totalCandidates}`}
            </span>
            {locatorComplete && <Badge variant="outline" className="text-[9px] px-1.5 py-0">Complete</Badge>}
          </div>
          <Progress value={progressPct} className="h-2" />
          <div className="text-[10px] text-muted-foreground">
            ✓ {locatedCount} located{croppedCount > 0 && ` · 🖼 ${croppedCount} cropped`} · ✗ {failedCount} filtered
            {isRunning && totalCandidates > processedCandidates && ` · ${totalCandidates - processedCandidates} remaining`}
          </div>
        </div>
      )}
      {/* CR613: "Preparing" state — RUNNING but 0 candidates (resolving inputs) */}
      {isPreparingZeroWork && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Resolving inputs…</span>
        </div>
      )}
      {/* CR676: Always show View Snippets so failed/no-work runs remain debuggable */}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5 text-xs"
          onClick={() => navigate(`/admin/flyer-snippets?batch_run_id=${batchRunId}`)}
        >
          <Eye className="h-3 w-3" />
          View Snippets
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 ml-1">{attemptInfo.artifactsInserted ?? 0}</Badge>
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          title="Open in new tab"
          onClick={() => window.open(`/admin/flyer-snippets?batch_run_id=${batchRunId}`, "_blank")}
        >
          <ExternalLink className="h-3 w-3" />
        </Button>
        {isFailed && attemptInfo.artifactErrors > 0 && (
          <Badge variant="destructive" className="text-[10px]">
            {attemptInfo.artifactErrors} error{attemptInfo.artifactErrors > 1 ? 's' : ''}
          </Badge>
        )}
      </div>
      {/* CR561: Show summary for any terminal status (COMPLETE, FAILED with output) */}
      {(isComplete || (isFailed && hasSnippets)) && (() => {
        const errorCount = attemptInfo.artifactErrors ?? 0;
        const errors = attemptInfo.errors ?? [];
        const firstError = errors[0]?.error ?? null;
        const truncate = (v: string, max: number) => v.length > max ? `${v.slice(0, max - 1)}…` : v;
        return (
          <>
            <div className="text-xs text-muted-foreground">
              Candidates: {attemptInfo.artifactsAttempted} · Located: {attemptInfo.artifactsInserted}
              {attemptInfo.cropperAttempted > 0 && ` · Cropper: ${attemptInfo.cropperUsed}/${attemptInfo.cropperAttempted}`}
              {attemptInfo.oversizeGuardCount > 0 && ` · Oversize Fallbacks: ${attemptInfo.oversizeGuardCount}`}
              {attemptInfo.filteredByAttempt > 0 && ` · Filtered: ${attemptInfo.filteredByAttempt}`}
              {attemptInfo.inputMissing > 0 && ` · Input Missing: ${attemptInfo.inputMissing}`}
              {attemptInfo.selfHealed > 0 && ` · Self-healed: ${attemptInfo.selfHealed}`}
              {/* CR562: Show real error text instead of just the count */}
              {errorCount > 0 && (
                <>
                  {' · '}Error{errorCount > 1 ? 's' : ''}:{' '}
                  {firstError ? truncate(firstError, 80) : errorCount}
                </>
              )}
            </div>
            {/* CR562: Inline error list for S1.4 */}
            {errorCount > 0 && errors.length > 0 && (
              <div className="mt-1 text-[11px] text-destructive/80 space-y-0.5">
                {errors.map((e: { error: string; staging_item_id?: string }, i: number) => (
                  <div
                    key={e.staging_item_id ?? `${i}-${e.error.slice(0, 50)}`}
                    className="truncate"
                    title={e.error}
                  >
                    • {truncate(e.error, 120)}
                    {e.staging_item_id && (
                      <span className="ml-1 text-[10px] text-muted-foreground">
                        ({e.staging_item_id})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        );
      })()}
      {/* CR557: Attempt History Timeline */}
      <S14AttemptTimeline batchRunId={batchRunId} />
    </div>
  );
}

// Wrapper export that includes ErrorBoundary
export function FlyerPipelineStepsPanel(props: FlyerPipelineStepsPanelProps) {
  return (
    <FlyerPipelineErrorBoundary>
      <FlyerPipelineStepsPanelInner {...props} />
    </FlyerPipelineErrorBoundary>
  );
}

function FlyerPipelineStepsPanelInner({
  batchRunId,
  onRefresh,
  onRunCollect,
  collectStepInfo,
  autoModeEnabled = false,
  onAutoModeToggle,
  isParentOrchestrating = false,
  onS1Complete,
  flyerCaptureMode,
  hideLocalAutoControls = false,
  parentAutoStartSignal = 0,
}: FlyerPipelineStepsPanelProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const isManualS1Mode = !autoModeEnabled;
  const [s14NetworkTrace, setS14NetworkTrace] = useState<any>(null);
  // CR554: S1.4 state hook — must be at top level (unconditional)
  const [stats, setStats] = useState<FlyerPipelineStats>({
    flyersTotal: 0, pendingRenderFlyers: 0, renderedFlyers: 0, renderedPages: 0,
    ocrPages: 0, ocrFailed: 0, ocrQueued: 0, scoredPages: 0, signalHitPages: 0, droppedPages: 0, candidatePages: 0, aiFlyersComplete: 0, dealsExtracted: 0
  });
  const [loading, setLoading] = useState(true);
  const [runningAction, setRunningAction] = useState<"capture" | "collect" | "render" | "ocr" | "signals" | "ai" | null>(null);

  // CR450: Auto-run capture state removed — now uses standardized stepModes["S1_CAPTURE"]

  // CR092: Auto Mode state
  const [autoMode, setAutoMode] = useState<AutoModeState>({
    enabled: false,
    status: "IDLE",
    currentStepKey: null,
    lastCompletedStepKey: null,
    startedAt: null,
    stoppedAt: null,
    stopReason: null,
  });
  const autoModeRef = useRef(autoMode);
  autoModeRef.current = autoMode; // Keep ref in sync for async callbacks
  const parentAutoSignalRef = useRef(parentAutoStartSignal);

  // OCR engine config from batch run
  // CR088: Initialize with DEFAULT_SIGNALS so we never start with empty array
  const [ocrEngineConfig, setOcrEngineConfig] = useState<{
    ocrEngine: OcrEngineValue;
    candidateThreshold: number;
    minOcrScore: number;
    signals: CandidateSignal[];
  }>({
    ocrEngine: "tesseract",
    candidateThreshold: 12,
    minOcrScore: 25,
    signals: DEFAULT_SIGNALS, // CR088: Use defaults, not empty array
  });

  // CR226: S0 Prompt Pack state (single source of truth for 1.3)
  const [s0PromptPackId, setS0PromptPackId] = useState<string | null>(null);
  const [s0CropperPackId, setS0CropperPackId] = useState<string | null>(null);
  const [s0PromptPackName, setS0PromptPackName] = useState<string | null>(null);

  // CRxxx: Hoisted S1.5 pack IDs for eager loading
  const [s15LocatorPackId, setS15LocatorPackId] = useState<string | null>(null);
  const [s15CropperPackId, setS15CropperPackId] = useState<string | null>(null);
  const [s15SettingsRowFound, setS15SettingsRowFound] = useState<boolean | null>(null);

  // Render dialog state
  const [renderDialogOpen, setRenderDialogOpen] = useState(false);
  const [pendingFlyers, setPendingFlyers] = useState<Array<{
    id: string;
    batch_run_id: string;
    merchant_key: string;
    evidence_json: Record<string, unknown>;
  }>>([]);

  // CR081: Track OCR step status from DB - single source of truth for button states
  // MUST be defined before useClientSideOcr since it uses this state
  const [ocrStepDbStatus, setOcrStepDbStatus] = useState<string | null>(null);
  const [ocrNetworkTrace, setOcrNetworkTrace] = useState<OcrNetworkTrace | null>(null);
  const [signalsNetworkTrace, setSignalsNetworkTrace] = useState<any>(null);
  const [captureNetworkTrace, setCaptureNetworkTrace] = useState<any>(null);
  const [aiNetworkTrace, setAiNetworkTrace] = useState<AiNetworkTrace | null>(null);

  // CR471: Track AI step status from DB for button state (FAILED→Retry, COMPLETE→Re-run)
  const [signalsStepDbStatus, setSignalsStepDbStatus] = useState<string | null>(null);
  const [signalsBlockedUntil, setSignalsBlockedUntil] = useState<number>(0);
  const [signalsBlockedReason, setSignalsBlockedReason] = useState<string | null>(null);
  const [nowTs, setNowTs] = useState<number>(() => Date.now());
  const [canonicalS1Snapshot, setCanonicalS1Snapshot] = useState<{
    blockedReasons?: string[];
    ocr?: { terminal?: boolean; terminal_reason?: string | null; blocked_reasons?: string[] };
    signals?: { ready?: boolean; scope_matches_ocr?: boolean };
  } | null>(null);
  const [snapshotStabilityPhase, setSnapshotStabilityPhase] = useState<SnapshotStabilityPhase>("SETTLING");
  const snapshotStabilityRef = useRef(createInitialSnapshotStabilityState());
  const [signalsAttemptProgress, setSignalsAttemptProgress] = useState<{ processed: number; total: number } | null>(null);
  const [signalsRunRequestedAt, setSignalsRunRequestedAt] = useState<string | null>(null);
  const [ocrPaymentRequired, setOcrPaymentRequired] = useState<{ count: number; sampleMessage: string | null }>({
    count: 0,
    sampleMessage: null,
  });
  const [aiStepDbStatus, setAiStepDbStatus] = useState<string | null>(null);
  const [aiStepErrorMessage, setAiStepErrorMessage] = useState<string | null>(null);
  // CR472: Track salvage/repair counts from AI attempt diagnostics
  const [aiSalvageCount, setAiSalvageCount] = useState(0);
  const [aiRepairCount, setAiRepairCount] = useState(0);
  // CR476: Track last_updated_at for stuck detection
  const [aiLastUpdatedAt, setAiLastUpdatedAt] = useState<string | null>(null);

  // CRxxx: Standardized S1 Auto-Run Global state
  const [isS1AutoRunGlobal, setIsS1AutoRunGlobal] = useState(false);
  const [aiCandidatesProcessed, setAiCandidatesProcessed] = useState(0);
  const [aiCandidatesTotal, setAiCandidatesTotal] = useState(0);

  // CR471: Derive latest AI attempt ID (for S1.5/S1_4)
  // CR471: Derive latest AI attempt ID (for S1.5/S1_4)
  const latestAiAttemptId = aiStepDbStatus !== null && aiStepDbStatus !== "RUNNING" ? aiStepDbStatus : null;
  const aiStepComplete = (aiStepDbStatus === "COMPLETE" || aiStepDbStatus === "COMPLETE_WITH_ERRORS" || stats.aiFlyersComplete > 0) && stats.candidatePages > 0;

  // CR554: S1.4 state hook — must be at top level (unconditional)
  const s14State = useSnippetLocatorState(batchRunId, {
    aiComplete: aiStepComplete,
    latestAiAttemptId: latestAiAttemptId,
    defaultLocatorPackId: s15LocatorPackId,
    defaultCropperPackId: s15CropperPackId,
    onNetworkTrace: (trace) => setS14NetworkTrace(trace),
  });
  // CR508: Canonical deals extracted count from attempt diagnostics (drives pill)
  const [aiDealsExtractedFromDiag, setAiDealsExtractedFromDiag] = useState<number | null>(null);

  // CR095: Track Render step status from DB for visibility of blocked/skipped reasons
  const [renderStepDbInfo, setRenderStepDbInfo] = useState<{
    status: string | null;
    skippedReason: string | null;
    blockedReason: string | null;
  }>({ status: null, skippedReason: null, blockedReason: null });
  const [captureAttemptInfo, setCaptureAttemptInfo] = useState<{
    id: string | null;
    status: string | null;
    pagesTotal: number;
    merchantsFailed: number;
    merchantsSucceeded: number;
    merchantsTotal: number;
  }>({ id: null, status: null, pagesTotal: 0, merchantsFailed: 0, merchantsSucceeded: 0, merchantsTotal: 0 });
  const [captureExpectedPages, setCaptureExpectedPages] = useState<number>(0);
  const [captureScopeV1, setCaptureScopeV1] = useState<{
    capture_attempt_id?: string | null;
    final_page_count?: number;
    page_watermark?: string | null;
    frozen_at?: string | null;
  } | null>(null);

  // CR555: Track step modes for AUTO/MANUAL toggles
  const [stepModes, setStepModes] = useState<Record<string, { mode: string; status: string }>>({});

  const isStepActive = useCallback((
    stepKey: "ocr" | "signals" | "ai" | "render" | "collect"
  ): boolean => {
    if (runningAction === stepKey) return true;
    switch (stepKey) {
      case "ocr": return ocrStepDbStatus === "RUNNING";
      case "signals": return signalsStepDbStatus === "RUNNING";
      case "ai": return aiStepDbStatus === "RUNNING";
      case "render": return renderStepDbInfo.status === "RUNNING";
      case "collect": return collectStepInfo?.status === "RUNNING";
      default: return false;
    }
  }, [runningAction, ocrStepDbStatus, signalsStepDbStatus, aiStepDbStatus, renderStepDbInfo.status, collectStepInfo?.status]);

  const resolveOcrScopeAttemptIds = useCallback(async (renderedPagesTarget: number): Promise<string[]> => {
    const { data: attempts } = await supabase
      .from("cr075_batch_run_step_attempts")
      .select("id, diagnostics_json, attempt_no")
      .eq("batch_run_id", batchRunId)
      .eq("step_key", "S1_OCR")
      .in("status", ["COMPLETE", "PARTIAL", "RUNNING", "FAILED"])
      .not("status", "eq", "SUPERSEDED")
      .order("attempt_no", { ascending: false })
      .limit(12);

    if (!attempts || attempts.length === 0) return [];

    const seededFor = (attempt: { diagnostics_json?: unknown }) => {
      const diag = (attempt.diagnostics_json as Record<string, unknown> | null) ?? {};
      return Number(diag.pages_seeded ?? diag.pages_discovered_total ?? diag.pages_target_total ?? 0) || 0;
    };

    const latest = attempts[0];
    const latestSeeded = seededFor(latest);
    const ids = [latest.id as string];

    // Retry-failed OCR attempts only seed a subset; include the most recent full baseline attempt as well.
    if (renderedPagesTarget > 0 && latestSeeded > 0 && latestSeeded < renderedPagesTarget) {
      const baseline =
        attempts.find((a) => a.id !== latest.id && seededFor(a) >= renderedPagesTarget) ??
        attempts
          .filter((a) => a.id !== latest.id)
          .sort((a, b) => seededFor(b) - seededFor(a))[0];
      if (baseline?.id) ids.push(baseline.id as string);
    }

    return Array.from(new Set(ids));
  }, [batchRunId]);

  // CR555: Fetch step modes from DB
  const fetchStepModes = useCallback(async () => {
    const { data } = await supabase
      .from("cr075_batch_run_steps")
      .select("step_key, mode, status")
      .eq("batch_run_id", batchRunId)
      .in("step_key", ["S1_CAPTURE", "S1_COLLECT", "S1_RENDER", "S1_OCR", "S1_SIGNALS", "S1_AI", "S1_4", "S1_SNIPPET"]);

    if (data) {
      const modes: Record<string, { mode: string; status: string }> = {};
      for (const row of data) {
        // Normalize step keys so the UI always uses canonical group keys
        // CR502: S1_SNIPPET → S1_4 (S1.4 Deep Scan)
        // CRxxx: S1_COLLECT → S1_CAPTURE (S1.0C Image Capture)
        const key = row.step_key === "S1_SNIPPET" ? "S1_4" :
          row.step_key === "S1_COLLECT" ? "S1_CAPTURE" :
            row.step_key;
        modes[key] = { mode: row.mode || "MANUAL", status: row.status };
      }
      setStepModes(modes);
    }
  }, [batchRunId]);

  // CR555: Handle mode change from toggle
  const handleStepModeChanged = useCallback((stepKey: string, newMode: string, newStatus: string | null) => {
    setStepModes(prev => ({
      ...prev,
      [stepKey]: { mode: newMode, status: newStatus || prev[stepKey]?.status || "NOT_STARTED" },
    }));
    // Re-fetch all modes after a short delay to catch side effects
    setTimeout(() => fetchStepModes(), 500);
  }, [fetchStepModes]);

  // CR095: Fetch Render step status from DB
  const fetchRenderStepStatus = useCallback(async () => {
    const { data } = await supabase
      .from("cr075_batch_run_steps")
      .select("status, skipped_reason, config_json")
      .eq("batch_run_id", batchRunId)
      .eq("step_key", "S1_RENDER")
      .maybeSingle();

    if (data) {
      const configJson = data.config_json as { blocked_reason?: string } | null;
      setRenderStepDbInfo({
        status: data.status,
        skippedReason: data.skipped_reason,
        blockedReason: configJson?.blocked_reason || null,
      });
    } else {
      setRenderStepDbInfo({ status: null, skippedReason: null, blockedReason: null });
    }
  }, [batchRunId]);

  // CR081: Fetch OCR step status from DB - reusable callback
  const fetchOcrStepStatus = useCallback(async () => {
    const { data } = await supabase
      .from("cr075_batch_run_step_attempts")
      .select("status, diagnostics_json")
      .eq("batch_run_id", batchRunId)
      .eq("step_key", "S1_OCR")
      .not("status", "eq", "SUPERSEDED")
      .order("created_at", { ascending: false })
      .order("attempt_no", { ascending: false })
      .limit(1);

    if (data?.[0]) {
      const effectiveStatus = getEffectiveAttemptStatus(data[0] as any);
      setOcrStepDbStatus(effectiveStatus);
      const diag = data[0].diagnostics_json as Record<string, unknown> | null;
      const snapshot = (diag?.s1_state_snapshot as Record<string, unknown> | undefined) ?? null;
      if (snapshot && typeof snapshot === "object") {
        setCanonicalS1Snapshot(snapshot as any);
      }
    } else {
      setOcrStepDbStatus(null); // No attempts yet = NOT_STARTED
      setCanonicalS1Snapshot(null);
    }
  }, [batchRunId]);

  // CR471: Fetch AI step status from DB
  const fetchAiStepStatus = useCallback(async () => {
    const { data } = await supabase
      .from("cr075_batch_run_step_attempts")
      // CR480: Also fetch finished_at for "last update" fallback
      .select("status, error_message, diagnostics_json, started_at, finished_at")
      .eq("batch_run_id", batchRunId)
      .eq("step_key", "S1_AI")
      .not("status", "eq", "SUPERSEDED")
      .order("created_at", { ascending: false })
      .limit(1);

    if (data?.[0]) {
      const row = data[0] as any;
      const effectiveDbStatus = getEffectiveAttemptStatus(row);
      setAiStepDbStatus(effectiveDbStatus);
      setAiStepErrorMessage(row.error_message);
      // CR472: Extract salvage/repair counts from diagnostics
      const diag = row.diagnostics_json as Record<string, unknown> | null;
      setAiSalvageCount((diag?.salvage_used_count as number) || 0);
      setAiRepairCount((diag?.repair_used_count as number) || 0);
      // CR480: Fallback chain for last_updated_at: diag.last_updated_at → finished_at → started_at
      setAiLastUpdatedAt(
        (diag?.last_updated_at as string) || row.finished_at || row.started_at || null
      );
      setAiCandidatesProcessed((diag?.candidates_processed as number) || (diag?.pages_scanned as number) || 0);
      setAiCandidatesTotal((diag?.candidates_total as number) || 0);
      // CR508: Extract canonical deals count from attempt diagnostics for pill
      const sv2 = diag?.summary_v2 as Record<string, unknown> | undefined;
      const pageResults = Array.isArray(diag?.page_results) ? diag.page_results as Array<Record<string, unknown>> : [];
      const pageResultsSum = pageResults.reduce((sum, pr) => sum + ((pr.parsed_deals_count as number) || 0), 0);
      const diagDeals = (sv2?.extracted_count as number) ?? (diag?.deals_extracted as number) ?? (pageResultsSum > 0 ? pageResultsSum : null);
      setAiDealsExtractedFromDiag(diagDeals);
      // CR480: If DB shows terminal status, clear the local spinner
      const isTerminal = isTerminalAttemptStatus(effectiveDbStatus);
      if (isTerminal) {
        setRunningAction((prev) => prev === "ai" ? null : prev);
      }
    } else {
      setAiStepDbStatus(null);
      setAiStepErrorMessage(null);
      setAiSalvageCount(0);
      setAiRepairCount(0);
      setAiLastUpdatedAt(null);
      setAiCandidatesProcessed(0);
      setAiCandidatesTotal(0);
      setAiDealsExtractedFromDiag(null);
    }
  }, [batchRunId, getEffectiveAttemptStatus]);

  // Fetch S1_SIGNALS step status from DB
  const fetchSignalsStepStatus = useCallback(async () => {
    const { data } = await supabase
      .from("cr075_batch_run_step_attempts")
      .select("status, diagnostics_json, started_at, created_at, finished_at")
      .eq("batch_run_id", batchRunId)
      .eq("step_key", "S1_SIGNALS")
      .not("status", "eq", "SUPERSEDED")
      .order("created_at", { ascending: false })
      .limit(1);

    if (data?.[0]) {
      const dbStatus = data[0].status;
      const attemptStartedAt = data[0].started_at ?? data[0].created_at;
      const isTerminal = ["COMPLETE", "PARTIAL", "FAILED"].includes(dbStatus);
      const isOlderThanRequestedRun =
        !!signalsRunRequestedAt &&
        !!attemptStartedAt &&
        new Date(attemptStartedAt).getTime() < new Date(signalsRunRequestedAt).getTime();

      // Guard race: immediately after clicking Re-run, polling may still see previous COMPLETE attempt.
      if (runningAction === "signals" && isTerminal && isOlderThanRequestedRun) {
        return;
      }

      setSignalsStepDbStatus(dbStatus);
      const diag = data[0].diagnostics_json as Record<string, unknown> | null;
      const snapshot = (diag?.s1_state_snapshot as Record<string, unknown> | undefined) ?? null;
      if (snapshot && typeof snapshot === "object") {
        setCanonicalS1Snapshot(snapshot as any);
      }
      if (dbStatus === "BLOCKED") {
        const retryAfterMs = Math.max(1000, Number(diag?.retry_after_ms ?? 15000));
        const finishedAt = typeof data[0].finished_at === "string" ? Date.parse(data[0].finished_at) : Number.NaN;
        if (Number.isFinite(finishedAt)) {
          setSignalsBlockedUntil(Math.max(signalsBlockedUntil, finishedAt + retryAfterMs));
        }
        const latestErrorCode = Array.isArray(diag?.errors) && diag.errors.length > 0
          ? String((diag.errors[0] as Record<string, unknown>)?.code ?? "")
          : "";
        setSignalsBlockedReason(latestErrorCode || "S1.3 waiting on S1.2 terminal");
      } else if (dbStatus !== "RUNNING") {
        setSignalsBlockedReason(null);
      }
      const processed = Math.max(
        Number(diag?.pages_scanned ?? diag?.processed_pages ?? 0) || 0,
        0
      );
      const total = Math.max(
        Number(
          diag?.pages_target_total ??
          diag?.pages_discovered_total ??
          diag?.pages_total ??
          Math.max((stats.ocrPages ?? 0) - (stats.ocrFailed ?? 0), 0)
        ) || 0,
        0
      );
      if (isTerminal) {
        setSignalsAttemptProgress(null);
      } else if (total > 0) {
        setSignalsAttemptProgress({
          processed: Math.min(processed, total),
          total,
        });
      }
      // If terminal, clear the local spinner
      if (isTerminal) {
        setRunningAction((prev) => prev === "signals" ? null : prev);
        setSignalsRunRequestedAt(null);
      }
      if (!isOlderThanRequestedRun && attemptStartedAt && signalsRunRequestedAt) {
        setSignalsRunRequestedAt(null);
      }
    } else {
      setSignalsStepDbStatus(null);
      setSignalsAttemptProgress(null);
    }
  }, [batchRunId, stats.ocrPages, stats.ocrFailed, runningAction, signalsRunRequestedAt, signalsBlockedUntil]);

  // Client-side OCR hook - uses fetchOcrStepStatus defined above
  const {
    progress: clientOcrProgress,
    runOcr: runClientOcr,
    abort: abortClientOcr,
    isRunning: isClientOcrRunning,
  } = useClientSideOcr({
    batchRunId,
    config: ocrEngineConfig,
    onProgress: (progress) => {
      // Update local state for UI feedback
      if (progress.status === "RUNNING") {
        setRunningAction("ocr");
        setOcrStepDbStatus("RUNNING");
      }
    },
    onComplete: (result) => {
      toast({
        title: "OCR Scan Complete (Tesseract)",
        description: `OCR completed on ${result.pages_processed} pages.`,
      });
      setRunningAction(null);
      // CR081: Refresh from DB to get authoritative status
      fetchOcrStepStatus();
      fetchStats();
      onRefresh();
    },
  });

  // Fetch OCR config from batch run
  // CR088: If signals are missing from DB, use DEFAULT_SIGNALS (not empty array)
  const fetchOcrConfig = useCallback(async () => {
    try {
      const { data } = await supabase
        .from("cr075_batch_runs")
        .select("config_json")
        .eq("id", batchRunId)
        .single();

      if (data?.config_json) {
        const configJson = data.config_json as Record<string, unknown>;

        // CRxxx: Derive global S1 auto-run status
        const s1AutoRun = Boolean(
          configJson.s1_auto_run ||
          (configJson.s1 as any)?.auto_chain_enabled ||
          (configJson.auto_mode_config as any)?.auto_mode?.S1
        );
        setIsS1AutoRunGlobal(s1AutoRun);

        const flyerExtraction = configJson.flyerExtraction as Record<string, unknown> | undefined;
        if (flyerExtraction) {
          const dbCaptureScope = flyerExtraction.capture_scope_v1 as Record<string, unknown> | undefined;
          if (dbCaptureScope && typeof dbCaptureScope === "object") {
            setCaptureScopeV1({
              capture_attempt_id: typeof dbCaptureScope.capture_attempt_id === "string" ? dbCaptureScope.capture_attempt_id : null,
              final_page_count: typeof dbCaptureScope.final_page_count === "number" ? dbCaptureScope.final_page_count : undefined,
              page_watermark: typeof dbCaptureScope.page_watermark === "string" ? dbCaptureScope.page_watermark : null,
              frozen_at: typeof dbCaptureScope.frozen_at === "string" ? dbCaptureScope.frozen_at : null,
            });
          } else {
            setCaptureScopeV1(null);
          }

          // CR088: Check if signals exist in DB, use DEFAULT_SIGNALS if missing or empty
          const dbSignals = flyerExtraction.signals as CandidateSignal[] | undefined;
          const signals = (dbSignals && Array.isArray(dbSignals) && dbSignals.length > 0)
            ? dbSignals
            : DEFAULT_SIGNALS;

          console.log(`[FlyerPipeline] Config loaded: signals=${signals.length} (from DB: ${dbSignals?.length ?? 0})`);

          setOcrEngineConfig({
            ocrEngine: normalizeOcrEngine(flyerExtraction.ocrEngine),
            candidateThreshold: (flyerExtraction.candidateThreshold as number) ?? 12,
            minOcrScore: (flyerExtraction.minOcrScore as number) ?? 25,
            signals,
          });

          // CR226: Extract prompt/cropper pack IDs from S0 config
          const promptPackId = (flyerExtraction.promptPackId as string | null) ?? null;
          setS0PromptPackId(promptPackId);

          const cropperPackId = (flyerExtraction.s1_4_cropper_prompt_pack_id as string | null) ?? null;
          setS0CropperPackId(cropperPackId);

          // CR226: Fetch prompt pack name if ID is set
          if (promptPackId) {
            supabase
              .from("prompt_packs")
              .select("name, version")
              .eq("id", promptPackId)
              .single()
              .then(({ data: packData }) => {
                if (packData) {
                  setS0PromptPackName(`${packData.name} v${packData.version}`);
                } else {
                  setS0PromptPackName(`Pack ${promptPackId.slice(0, 8)}...`);
                }
              });
          } else {
            setS0PromptPackName(null);
          }
        } else {
          // CR088: No flyerExtraction in DB, use full defaults
          console.log(`[FlyerPipeline] No flyerExtraction in config, using defaults with ${DEFAULT_SIGNALS.length} signals`);
          setOcrEngineConfig({
            ocrEngine: "tesseract",
            candidateThreshold: 12,
            minOcrScore: 25,
            signals: DEFAULT_SIGNALS,
          });
          // CR226: No prompt pack configured
          setS0PromptPackId(null);
          setS0PromptPackName(null);
          setCaptureScopeV1(null);
        }
      }

      // CRxxx: Eager load S1.5 defaults from global settings
      try {
        const { data: s15Config } = await supabase
          .from("app_scan_settings")
          .select("s1_4_locator_prompt_pack_id, s1_4_cropper_prompt_pack_id")
          .eq("singleton_id", true)
          .maybeSingle();

        if (s15Config) {
          setS15SettingsRowFound(true);
          setS15LocatorPackId(s15Config.s1_4_locator_prompt_pack_id);
          setS15CropperPackId(s15Config.s1_4_cropper_prompt_pack_id);
        } else {
          setS15SettingsRowFound(false);
        }
      } catch (err) {
        console.warn("[FlyerPipeline] Failed to eager load S1.5 defaults:", err);
      }
    } catch (err) {
      console.error("[FlyerPipeline] Failed to fetch OCR config:", err);
      setCaptureScopeV1(null);
    }
  }, [batchRunId]);

  useEffect(() => {
    fetchOcrConfig();
  }, [fetchOcrConfig]);

  // Fetch flyer stats from staging items
  // CR450: Also query truth table for rendered pages in IMAGE_CAPTURE mode
  const fetchStats = useCallback(async (options?: { silent?: boolean }): Promise<FlyerPipelineStats | null> => {
    const silent = options?.silent ?? false;
    if (!silent) setLoading(true);
    try {
      const { data } = await supabase
        .from("cr075_staging_deal_items")
        .select("id, merchant_key, evidence_json, validated_payload_json")
        .eq("batch_run_id", batchRunId)
        .eq("source_type", "FLYER");

      if (!data) {
        setStats({
          flyersTotal: 0,
          pendingRenderFlyers: 0,
          renderedFlyers: 0,
          renderedPages: 0,
          ocrPages: 0,
          ocrFailed: 0,
          ocrQueued: 0,
          scoredPages: 0,
          signalHitPages: 0,
          droppedPages: 0,
          candidatePages: 0,
          aiFlyersComplete: 0,
          dealsExtracted: 0,
        });
        return;
      }

      let pendingRenderFlyers = 0;
      let renderedFlyers = 0;
      let renderedPages = 0;
      let ocrPages = 0;
      let candidatePages = 0;
      let aiFlyersComplete = 0;
      let dealsExtracted = 0;
      const pendingList: typeof pendingFlyers = [];

      for (const item of data) {
        const evidence = item.evidence_json as Record<string, unknown>;
        const dealType = getDealType(item.validated_payload_json);

        const isRendered = evidence?.render_status === "RENDERED" &&
          Array.isArray(evidence?.flyer_pages) && (evidence.flyer_pages as unknown[]).length > 0;

        const hasFlyerPdf = Boolean(evidence?.flyer_storage_path || evidence?.flyer_storage_key || evidence?.flyer_filename);

        if (isRendered) {
          renderedFlyers++;
          renderedPages += (evidence.flyer_pages as unknown[]).length;
        } else if (hasFlyerPdf && (dealType === "FLYER_SCAN_PENDING" || dealType === "PDF_NEEDS_RENDERING")) {
          pendingRenderFlyers++;
          pendingList.push({
            id: item.id,
            batch_run_id: batchRunId,
            merchant_key: item.merchant_key,
            evidence_json: evidence,
          });
        }

        if (Array.isArray(evidence?.ocr_pages)) {
          ocrPages += (evidence.ocr_pages as unknown[]).length;
        }

        if (Array.isArray(evidence?.candidate_pages)) {
          candidatePages += (evidence.candidate_pages as unknown[]).length;
        }

        if (Array.isArray(evidence?.ai_extracted_deals) && (evidence.ai_extracted_deals as unknown[]).length > 0) {
          aiFlyersComplete++;
          dealsExtracted += (evidence.ai_extracted_deals as unknown[]).length;
        }
      }

      // CR455/CR468/CR491: In IMAGE_CAPTURE mode, source page stats from truth table + registry fallback
      let nextStats: FlyerPipelineStats = {
        flyersTotal: data.length,
        pendingRenderFlyers,
        renderedFlyers,
        renderedPages,
        ocrPages,
        ocrFailed: 0,
        scoredPages: ocrPages,
        signalHitPages: 0,
        droppedPages: 0,
        candidatePages: 0,
        aiFlyersComplete,
        dealsExtracted,
        ocrQueued: 0, // Initial value, may be updated below
      };

      if (flyerCaptureMode === "IMAGE_CAPTURE") {
        const truthBase = supabase.from("cr075_flyer_page_truth" as any);
        const [renderedRes, registryRes, manifestRes, captureAttemptsRes] = await Promise.all([
          truthBase.select("id", { count: "exact", head: true }).eq("batch_run_id", batchRunId).eq("rendered", true).not("render_storage_path", "is", null),
          // CR491: Also check registry for rendered page count
          supabase.from("batch_run_page_assets").select("id", { count: "exact", head: true }).eq("batch_run_id", batchRunId).not("bucket_id", "is", null).not("object_path", "is", null),
          // CR494: Check manifest completeness (including attempt scoping)
          supabase.from("flyer_image_capture_manifests" as any).select("id, status, capture_attempt_id, page_count_expected, page_count_received").eq("batch_run_id", batchRunId),
          supabase
            .from("flyer_capture_attempts")
            .select("id, status, pages_total, merchants_total, merchants_failed, merchants_succeeded")
            .eq("batch_run_id", batchRunId)
            .order("created_at", { ascending: false })
            .limit(10),
        ]);

        const truthRendered = renderedRes.count ?? 0;
        const registryCount = registryRes.count ?? 0;
        const effectiveRenderedForScope = truthRendered > 0 ? truthRendered : registryCount;
        const ocrScanRunIds = await resolveOcrScopeAttemptIds(effectiveRenderedForScope);
        const latestAttemptId = ocrScanRunIds[0];
        const captureAttempts = (captureAttemptsRes.data ?? []) as Array<{
          id: string;
          status: string | null;
          pages_total: number | null;
          merchants_total: number | null;
          merchants_failed: number | null;
          merchants_succeeded: number | null;
        }>;
        const activeCaptureStatuses = new Set(["running", "queued", "in_progress", "pending"]);
        const canonicalCaptureAttempt =
          captureAttempts.find((attempt) => activeCaptureStatuses.has(String(attempt.status ?? "").toLowerCase())) ??
          captureAttempts[0] ??
          null;
        const manifestRowsFull = (manifestRes.data ?? []) as any[];
        // Filter by attempt ID to prevent stale entries from appearing in current attempt's stats
        const manifestRows = canonicalCaptureAttempt?.id
          ? manifestRowsFull.filter(m => m.capture_attempt_id === canonicalCaptureAttempt.id)
          : manifestRowsFull;

        const manifestSucceeded = manifestRows.filter(m => String(m.status ?? "").toUpperCase() === "COMPLETE").length;
        const manifestFailed = manifestRows.filter(m => ["FAILED", "CAPTURE_INCOMPLETE"].includes(String(m.status ?? "").toUpperCase())).length;
        const expectedMerchantsTotal = Number(canonicalCaptureAttempt?.merchants_total ?? 0) || 0;
        const attemptStatus = String(canonicalCaptureAttempt?.status ?? "").toLowerCase();
        const attemptTerminal = attemptStatus === "complete" || attemptStatus === "failed";
        // While running, missing manifests are "pending". Once terminal, missing manifests count as failed.
        const merchantsFailed = attemptTerminal
          ? Math.max(expectedMerchantsTotal - manifestSucceeded, 0)
          : manifestFailed;
        const merchantsSucceeded = manifestSucceeded;

        setCaptureAttemptInfo({
          id: canonicalCaptureAttempt?.id ?? null,
          status: canonicalCaptureAttempt?.status ?? null,
          pagesTotal: canonicalCaptureAttempt?.pages_total ?? 0,
          merchantsFailed,
          merchantsSucceeded,
          merchantsTotal: expectedMerchantsTotal,
        });
        // Prefer manifest-derived expected pages while running; attempt header is updated only after freeze/reconcile.
        const captureExpectedPagesFromManifests = manifestRows.reduce(
          (s: number, m: any) => s + Number(m?.page_count_expected ?? m?.page_count_received ?? 0),
          0,
        );
        setCaptureExpectedPages(captureExpectedPagesFromManifests);

        // CR660: OCR/candidate counters are canonical on flyer_scan_pages_v2 (run_id = S1_OCR attempt id).
        let truthOcr = 0;
        let truthOcrFailed = 0;
        let truthOcrQueued = 0;
        let truthScoring = 0;
        let truthSignalHits = 0;
        let truthCandidates = 0;
        let paymentRequiredCount = 0;
        let paymentRequiredSample: string | null = null;

        if (ocrScanRunIds.length > 0) {
          const { data: pageRows, error: pageRowsErr } = await supabase
            .from("flyer_scan_pages_v2")
            .select("page_number, ocr_status, candidate_score, candidate_for_ai, primary_ocr_error")
            .in("run_id", ocrScanRunIds);

          if (pageRowsErr) {
            throw new Error(`Failed to load OCR page stats: ${pageRowsErr.message}`);
          }

          const byPage = new Map<number, {
            succeeded: boolean;
            failed: boolean;
            queued: boolean;
            scored: boolean;
            signalHit: boolean;
            candidate: boolean;
            paymentRequired: boolean;
            paymentMessage: string | null;
          }>();

          for (const row of pageRows ?? []) {
            const pageNum = Number(row.page_number ?? 0);
            if (!Number.isFinite(pageNum) || pageNum <= 0) continue;
            const existing = byPage.get(pageNum) ?? {
              succeeded: false,
              failed: false,
              queued: false,
              scored: false,
              signalHit: false,
              candidate: false,
              paymentRequired: false,
              paymentMessage: null,
            };
            const ocrStatus = String(row.ocr_status ?? "").toUpperCase();
            const score = row.candidate_score == null ? null : Number(row.candidate_score);
            const primaryError = String(row.primary_ocr_error ?? "");
            const isPaymentRequired = /payment required/i.test(primaryError);

            existing.succeeded = existing.succeeded || ocrStatus === "SUCCEEDED";
            existing.failed = existing.failed || ocrStatus === "FAILED";
            existing.queued = existing.queued || ocrStatus === "QUEUED";
            existing.scored = existing.scored || score != null;
            existing.signalHit = existing.signalHit || (score != null && score > 0);
            existing.candidate = existing.candidate || row.candidate_for_ai === true;
            existing.paymentRequired = existing.paymentRequired || isPaymentRequired;
            if (!existing.paymentMessage && isPaymentRequired) existing.paymentMessage = primaryError;
            byPage.set(pageNum, existing);
          }

          for (const state of byPage.values()) {
            if (state.succeeded || state.failed) truthOcr += 1;
            if (!state.succeeded && state.failed) truthOcrFailed += 1;
            if (state.scored) truthScoring += 1;
            if (state.signalHit) truthSignalHits += 1;
            if (state.candidate) truthCandidates += 1;
            if (!state.succeeded && state.paymentRequired) {
              paymentRequiredCount += 1;
              if (!paymentRequiredSample && state.paymentMessage) paymentRequiredSample = state.paymentMessage;
            }
          }
        } else {
          // Fallback for older runs that only have truth-table projections.
          const [legacyOcrRes, legacyOcrFailedRes, legacyScoringRes, legacySignalHitsRes, legacyCandidateRes] = await Promise.all([
            truthBase.select("id", { count: "exact", head: true }).eq("batch_run_id", batchRunId).eq("ocr_done", true),
            truthBase.select("id", { count: "exact", head: true }).eq("batch_run_id", batchRunId).eq("outcome", "OCR_FAILED"),
            truthBase.select("id", { count: "exact", head: true }).eq("batch_run_id", batchRunId).not("scoring_completed_at", "is", null),
            truthBase.select("id", { count: "exact", head: true }).eq("batch_run_id", batchRunId).gt("candidate_score", 0),
            truthBase.select("id", { count: "exact", head: true }).eq("batch_run_id", batchRunId).eq("is_candidate", true),
          ]);
          truthOcr = legacyOcrRes.count ?? 0;
          truthOcrFailed = legacyOcrFailedRes.count ?? 0;
          truthScoring = legacyScoringRes.count ?? 0;
          truthSignalHits = legacySignalHitsRes.count ?? 0;
          truthCandidates = legacyCandidateRes.count ?? 0;
        }

        const truthDropped = Math.max(truthScoring - truthCandidates, 0);

        // Change 3 — Add truthRenderedPages count query in fetchStats
        const { count: truthRenderedCount } = await supabase
          .from("cr075_flyer_page_truth")
          .select("page_number", { count: "exact", head: true })
          .eq("batch_run_id", batchRunId)
          .not("rendered_at", "is", null);

        // CR494: Manifest completeness check (attempt-scoped)
        const manifests = manifestRows;
        const incompleteManifests = manifests.filter((m: any) =>
          String(m?.status ?? "").toUpperCase() === "CAPTURE_INCOMPLETE"
        );
        const captureExpectedPages = manifests.reduce(
          (s: number, m: any) => s + Number(m?.page_count_expected ?? m?.page_count_received ?? 0),
          0,
        );
        // Treat FAILED/CAPTURE_INCOMPLETE as capture failure, but keep the specific incomplete signal too.
        const isCaptureIncomplete = incompleteManifests.length > 0;

        // CR491: Use truth rendered count, but fall back to registry count if truth has 0
        const effectiveRendered = truthRendered > 0 ? truthRendered : registryCount;

        // CR_FIX: Allow renderedPages to be populated even if capture is incomplete (UI-only).
        if (effectiveRendered > 0) {
          renderedPages = effectiveRendered;
          // We still track isCaptureIncomplete for warnings, but we don't zero out the count
          if (!isCaptureIncomplete) {
            pendingRenderFlyers = 0;
            pendingList.length = 0;
          }
        }
        // Always prefer truth counts in IMAGE_CAPTURE mode.
        ocrPages = truthOcr;
        candidatePages = truthCandidates;

        // BEEFED UP DEBUG LOGS
        console.log(`[FlyerPipeline] CR_DEBUG: IMAGE_CAPTURE stats calculation:`, {
          batchRunId,
          truthRendered,
          registryCount,
          effectiveRendered,
          isCaptureIncomplete,
          manifestsCount: manifests.length,
          incompleteManifestsCount: incompleteManifests.length,
          captureExpectedPages,
          truthOcr,
          truthCandidates,
          renderedPagesSetTo: renderedPages
        });

        console.log(`[FlyerPipeline] CR494: IMAGE_CAPTURE stats — attempt=${latestAttemptId ?? 'none'}, scopeRunIds=${ocrScanRunIds.join(',') || 'none'}, truthRendered=${truthRendered}, registry=${registryCount}, effective=${effectiveRendered}, ocr_terminal=${truthOcr}, ocr_failed=${truthOcrFailed}, scoring=${truthScoring}, signalHits=${truthSignalHits}, dropped=${truthDropped}, candidates=${truthCandidates}, captureIncomplete=${isCaptureIncomplete}, expected=${captureExpectedPages}`);

        nextStats = {
          flyersTotal: data.length,
          pendingRenderFlyers,
          renderedFlyers,
          renderedPages,
          ocrPages,
          ocrFailed: truthOcrFailed,
          ocrQueued: truthOcrQueued,
          scoredPages: truthScoring,
          signalHitPages: truthSignalHits,
          droppedPages: truthDropped,
          candidatePages,
          aiFlyersComplete,
          dealsExtracted,
          captureIncomplete: isCaptureIncomplete,
          captureExpectedPages,
          truthRenderedPages: truthRenderedCount ?? 0,
          registryPages: registryCount,
        };
        setOcrPaymentRequired({ count: paymentRequiredCount, sampleMessage: paymentRequiredSample });
      }
      if (flyerCaptureMode !== "IMAGE_CAPTURE") {
        setCaptureAttemptInfo({ id: null, status: null, pagesTotal: 0, merchantsFailed: 0, merchantsSucceeded: 0, merchantsTotal: 0 });
        setOcrPaymentRequired({ count: 0, sampleMessage: null });
      }

      setStats(nextStats);
      setPendingFlyers(pendingList);
      return nextStats;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [batchRunId, flyerCaptureMode, resolveOcrScopeAttemptIds]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleRunCapture = async (force = false, retryFailedOnly = false) => {
    console.log(`[FlyerPipeline] handleRunCapture started (force=${force}, retry=${retryFailedOnly}) for batch=${batchRunId}`);
    setRunningAction("capture");
    const requestBody = {
      batch_run_id: batchRunId,
      mode: "manual",
      force,
      retry_failed_only: retryFailedOnly,
    };

    try {
      setCaptureNetworkTrace({
        capturedAt: new Date().toISOString(),
        request: requestBody,
      });

      const { data, error } = await supabase.functions.invoke("batch-run-flyer-image-capture", {
        body: requestBody,
      });

      setCaptureNetworkTrace({
        capturedAt: new Date().toISOString(),
        request: requestBody,
        response: data,
      });

      if (error) throw error;

      toast({
        title: retryFailedOnly ? "Retry Failed Capture Started" : force ? "Capture Re-run Started" : "Capture Started",
        description: `Enqueued capture for ${data?.merchants_total ?? "?"} merchants.${retryFailedOnly ? " (failed-only scope)" : ""}`,
      });

      fetchStats({ silent: true });
      onRefresh();
      onS1Complete?.();
    } catch (err) {
      console.error("[FlyerPipeline] Capture run failed:", err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setCaptureNetworkTrace({
        capturedAt: new Date().toISOString(),
        request: requestBody,
        error: {
          name: err instanceof Error ? err.name : "InvokeError",
          message: errorMessage,
          stack: err instanceof Error ? err.stack : undefined,
        },
      });
      toast({
        title: retryFailedOnly ? "Retry Failed Capture Failed" : force ? "Capture Re-run Failed" : "Capture Failed",
        description: errorMessage || "Unknown error",
        variant: "destructive",
      });
    } finally {
      setRunningAction(null);
    }
  };

  // CR553: openSteps/toggleStep removed — FlyerStepRow handles its own persistence
  // Run Collect step
  const handleRunCollect = async () => {
    if (!onRunCollect) {
      toast({
        title: "Collect Not Available",
        description: "No collect handler provided.",
        variant: "destructive",
      });
      return;
    }

    setRunningAction("collect");
    try {
      await onRunCollect();
      toast({
        title: "Collect Complete",
        description: "Flyer PDFs have been collected. Proceed to Render step.",
      });
      fetchStats();
      onRefresh();
    } catch (err) {
      console.error("[FlyerPipeline] Collect failed:", err);
      toast({
        title: "Collect Failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setRunningAction(null);
    }
  };

  // Open render dialog
  const handleOpenRenderDialog = () => {
    if (pendingFlyers.length === 0) {
      toast({
        title: "Nothing to Render",
        description: "All flyers have already been rendered.",
      });
      return;
    }
    setRenderDialogOpen(true);
  };

  // Handle render complete
  const handleRenderComplete = () => {
    setRenderDialogOpen(false);
    toast({
      title: "Render Complete",
      description: "Flyer pages have been rendered. Run OCR + Candidate Scan next.",
    });
    fetchStats();
    onRefresh();
  };

  // CR081: Initial fetch of OCR step status on mount
  // CR095: Also fetch render step status
  useEffect(() => {
    fetchOcrStepStatus();
    fetchRenderStepStatus();
    fetchAiStepStatus();
    fetchSignalsStepStatus();
    fetchStepModes(); // CR555
  }, [fetchOcrStepStatus, fetchRenderStepStatus, fetchAiStepStatus, fetchSignalsStepStatus, fetchStepModes]);

  // Poll OCR status while running
  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;

    const pollOcrStatus = async () => {
      const { data } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("id, status, diagnostics_json")
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S1_OCR")
        .not("status", "eq", "SUPERSEDED")
        .order("created_at", { ascending: false })
        .order("attempt_no", { ascending: false })
        .limit(1);

      const attempt = data?.[0];
      if (attempt) {
        const prevStatus = ocrStepDbStatus;
        const effectiveStatus = getEffectiveAttemptStatus(attempt as any);
        setOcrStepDbStatus(effectiveStatus);

        // Keep S1.2 summary chips live while OCR is active (avoid stale prior-attempt counts).
        if (effectiveStatus === "RUNNING") {
          fetchStats({ silent: true });
        }

        // If just completed, show toast and refresh
        // CR660: Use canonical S1.2 queue counts (flyer_scan_pages_v2) for accurate toast.
        if ((effectiveStatus === "COMPLETE" || effectiveStatus === "PARTIAL" || effectiveStatus === "FAILED") &&
          prevStatus === "RUNNING") {
          const processedRes = await supabase
            .from("flyer_scan_pages_v2")
            .select("id", { count: "exact", head: true })
            .eq("run_id", attempt.id)
            .in("ocr_status", ["SUCCEEDED", "FAILED"]);
          const processed = processedRes.count ?? 0;
          toast({
            title: effectiveStatus === "COMPLETE" ? "OCR Scan Complete" :
              effectiveStatus === "PARTIAL" ? "OCR Partially Complete" : "OCR Scan Failed",
            description: `OCR completed on ${processed} pages.`,
            variant: effectiveStatus === "FAILED" ? "destructive" : "default",
          });
          setRunningAction(null);
          fetchStats();
          fetchSignalsStepStatus(); // Refresh S1.3 status so Run button appears
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
        }
      }
    };

    // Only poll while we think OCR is running
    if (runningAction === "ocr" || ocrStepDbStatus === "RUNNING") {
      pollOcrStatus(); // Immediate check
      pollInterval = setInterval(pollOcrStatus, 2000);
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [batchRunId, runningAction, ocrStepDbStatus, fetchStats, onRefresh, fetchSignalsStepStatus, toast]);

  // Poll S1_SIGNALS status while running (auto-chain may trigger it without local user action)
  useEffect(() => {
    if (signalsStepDbStatus !== "RUNNING" && runningAction !== "signals") return;
    fetchSignalsStepStatus(); // Immediate check so reruns don't appear stale
    const interval = setInterval(() => { fetchSignalsStepStatus(); }, 1000);
    return () => clearInterval(interval);
  }, [signalsStepDbStatus, runningAction, fetchSignalsStepStatus]);

  // CR476: Poll AI step status while running for live progress + stuck detection
  useEffect(() => {
    if (aiStepDbStatus !== "RUNNING" && runningAction !== "ai") return;
    const interval = setInterval(() => { fetchAiStepStatus(); }, 5000);
    return () => clearInterval(interval);
  }, [aiStepDbStatus, runningAction, fetchAiStepStatus]);

  const fetchStatsRef = useRef(fetchStats);
  const fetchOcrConfigRef = useRef(fetchOcrConfig);
  const fetchRenderStepStatusRef = useRef(fetchRenderStepStatus);
  const fetchOcrStepStatusRef = useRef(fetchOcrStepStatus);
  const fetchSignalsStepStatusRef = useRef(fetchSignalsStepStatus);
  const fetchAiStepStatusRef = useRef(fetchAiStepStatus);
  const fetchStepModesRef = useRef(fetchStepModes);

  useEffect(() => { fetchStatsRef.current = fetchStats; }, [fetchStats]);
  useEffect(() => { fetchOcrConfigRef.current = fetchOcrConfig; }, [fetchOcrConfig]);
  useEffect(() => { fetchRenderStepStatusRef.current = fetchRenderStepStatus; }, [fetchRenderStepStatus]);
  useEffect(() => { fetchOcrStepStatusRef.current = fetchOcrStepStatus; }, [fetchOcrStepStatus]);
  useEffect(() => { fetchSignalsStepStatusRef.current = fetchSignalsStepStatus; }, [fetchSignalsStepStatus]);
  useEffect(() => { fetchAiStepStatusRef.current = fetchAiStepStatus; }, [fetchAiStepStatus]);
  useEffect(() => { fetchStepModesRef.current = fetchStepModes; }, [fetchStepModes]);
  useEffect(() => {
    const timer = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Keep state rehydrated from DB even when no step is actively running.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchStatsRef.current({ silent: true });
      fetchOcrConfigRef.current();
      fetchRenderStepStatusRef.current();
      fetchOcrStepStatusRef.current();
      fetchSignalsStepStatusRef.current();
      fetchAiStepStatusRef.current();
      fetchStepModesRef.current();
    }, 6000);
    return () => clearInterval(interval);
  }, []); // ← empty, intentional

  // Run OCR + Candidate Scan - routes to client-side or server-side based on engine selection
  // CR455: Gate on renderedPages, not renderedFlyers
  // CR494: Also gate on capture completeness
  const handleRunOcrScan = async (retryFailedOnly: boolean = false, forceReprocess: boolean = false, retryQueuedOnly: boolean = false) => {
    const bypassPrereqChecks = isManualS1Mode;
    // CR_FIX: Force refresh stats before checking prerequisites to avoid stale state/double-click issue
    const latestStats = await fetchStats({ silent: true });
    const currentStats = latestStats ?? stats;

    if (!bypassPrereqChecks && currentStats.captureIncomplete) {
      toast({
        title: "Cannot Run OCR",
        description: `Image Capture incomplete. Expected ${currentStats.captureExpectedPages ?? '?'} pages, registry has ${currentStats.registryPages ?? 0}. Wait for capture to finish.`,
        variant: "destructive",
      });
      return;
    }
    if (!bypassPrereqChecks && flyerCaptureMode === "IMAGE_CAPTURE") {
      const expectedPages = currentStats.captureExpectedPages ?? captureExpectedPages;
      const truthRendered = currentStats.truthRenderedPages ?? stats.truthRenderedPages ?? 0;
      const captureBlockedCode = (canonicalS1Snapshot?.blockedReasons ?? []).find((reason) => String(reason).startsWith("CAPTURE_")) ?? null;
      const hasFrozenScope =
        !!captureScopeV1 &&
        !!captureAttemptInfo.id &&
        captureScopeV1.capture_attempt_id === captureAttemptInfo.id;
      const expectedFromScope = Number(captureScopeV1?.final_page_count ?? 0);
      const captureSettled =
        captureAttemptInfo.status === "complete" &&
        hasFrozenScope &&
        !currentStats.captureIncomplete &&
        (
          expectedFromScope > 0
            ? truthRendered >= expectedFromScope
            : (expectedPages > 0 ? truthRendered >= expectedPages : (currentStats.renderedPages ?? 0) > 0)
        );
      if (!captureSettled) {
        const captureBlockedMessage =
          captureBlockedCode === "CAPTURE_SCOPE_NOT_FROZEN"
            ? "Waiting for Image Capture frozen scope (S1.0c terminal snapshot)."
            : captureBlockedCode === "CAPTURE_JOBS_PENDING"
              ? "Waiting for S1.0c capture jobs to reach terminal status."
              : captureBlockedCode === "CAPTURE_MANIFESTS_PENDING"
                ? "Waiting for S1.0c manifests to finish processing."
                : captureBlockedCode === "CAPTURE_PAGE_COUNT_MOVING"
                  ? "Waiting for S1.0c final page count to stabilize."
                  : captureBlockedCode === "CAPTURE_INCOMPLETE"
                    ? `Capture incomplete — expected ${currentStats.captureExpectedPages ?? '?'} pages, registry has ${currentStats.registryPages ?? 0}.`
                    : !hasFrozenScope
                      ? "Waiting for Image Capture frozen scope (S1.0c terminal snapshot)."
                      : expectedPages > 0
                        ? `Waiting for S1.0c to finish collecting pages (${truthRendered}/${Math.max(expectedPages, expectedFromScope)} rendered).`
                        : "Waiting for S1.0c to finish collecting pages before OCR starts.";
        toast({
          title: "Cannot Run OCR",
          description: captureBlockedMessage,
          variant: "destructive",
        });
        return;
      }
    }
    if (!bypassPrereqChecks && currentStats.renderedPages === 0) {
      toast({
        title: "Cannot Run OCR",
        description: flyerCaptureMode === "IMAGE_CAPTURE"
          ? "No rendered pages available yet. Wait for Image Capture to complete."
          : "No rendered pages available. Render PDFs first.",
        variant: "destructive",
      });
      return;
    }

    // Check selected OCR engine
    if (!bypassPrereqChecks && ocrEngineConfig.ocrEngine === "none") {
      toast({
        title: "OCR Disabled",
        description: "OCR engine is set to 'None'. Change in S0 Scan Settings to enable.",
        variant: "destructive",
      });
      return;
    }

    // ROUTE: Tesseract (Browser) = client-side OCR
    if (!bypassPrereqChecks && ocrEngineConfig.ocrEngine === "tesseract") {
      toast({
        title: "Starting Tesseract OCR (Browser)",
        description: `Processing ${currentStats.renderedPages} pages in your browser...`,
      });
      runClientOcr();
      return;
    }

    // ROUTE: AI Vision = server-side OCR via edge function
    // Default run/retry should resume. Explicit full re-run is opt-in from dedicated UI action.
    setRunningAction("ocr");
    let isBackgroundMode = false;
    const requestBody = {
      batch_run_id: batchRunId,
      force_reprocess: forceReprocess,
      retry_failed_only: retryFailedOnly,
      retry_queued_only: retryQueuedOnly,
    };

    try {
      setOcrNetworkTrace({
        capturedAt: new Date().toISOString(),
        request: requestBody,
      });

      const { data, error } = await supabase.functions.invoke("batch-run-flyer-ocr-scan", {
        body: requestBody,
      });

      setOcrNetworkTrace({
        capturedAt: new Date().toISOString(),
        request: requestBody,
        response: data,
      });

      if (error) throw error;

      // Background mode is expected for edge runtime.
      if (data?.status === "RUNNING") {
        isBackgroundMode = true;
        setOcrStepDbStatus("RUNNING");
        toast({
          title: retryQueuedOnly ? "OCR Retry Started (Queued Pages)" : retryFailedOnly ? "OCR Retry Started (Failed Pages)" : "OCR Scan Started",
          description: retryQueuedOnly
            ? "Retrying only previously queued (stranded) OCR pages in background."
            : retryFailedOnly
              ? "Retrying only previously failed OCR pages in background."
              : "Processing pages in background.",
        });
        // Poll loop will handle completion and clear runningAction
        return;
      }

      // Non-background responses: only mark terminal if explicitly terminal.
      const terminalStatus = data?.status === "COMPLETE" || data?.status === "PARTIAL" || data?.status === "FAILED"
        ? data.status
        : data?.diagnostics?.status === "COMPLETE" || data?.diagnostics?.status === "PARTIAL" || data?.diagnostics?.status === "FAILED"
          ? data.diagnostics.status
          : null;

      if (terminalStatus) {
        toast({
          title: terminalStatus === "FAILED"
            ? "OCR Scan Failed"
            : (retryFailedOnly || retryQueuedOnly) ? "OCR Retry Complete" : "OCR Scan Complete",
          description: terminalStatus === "FAILED"
            ? (data?.error || data?.diagnostics?.errors?.[0]?.message || "OCR scan failed.")
            : (retryFailedOnly || retryQueuedOnly)
              ? `Retried ${data?.total_pages || 0} pages.`
              : `OCR completed on ${data?.total_pages || 0} pages.`,
          variant: terminalStatus === "FAILED" ? "destructive" : "default",
        });
        setOcrStepDbStatus(terminalStatus);
        fetchStats();
      } else {
        // Defensive: treat unknown response as still running to avoid false "Complete" state.
        isBackgroundMode = true;
        setOcrStepDbStatus("RUNNING");
      }
    } catch (err) {
      console.error("[FlyerPipeline] OCR scan failed:", err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setOcrNetworkTrace({
        capturedAt: new Date().toISOString(),
        request: requestBody,
        error: {
          name: err instanceof Error ? err.name : "InvokeError",
          message: errorMessage,
          stack: err instanceof Error ? err.stack : undefined,
        },
      });
      toast({
        title: "OCR Scan Failed",
        description: errorMessage || "Unknown error",
        variant: "destructive",
      });
      setOcrStepDbStatus("FAILED");
    } finally {
      // Only clear running action if NOT running in background
      if (!isBackgroundMode) {
        setRunningAction(null);
      }
    }
  };

  const buildCaptureNetworkDebugBundle = useCallback(async () => {
    const generatedAt = new Date().toISOString();
    const errors: Record<string, string> = {};

    const [runRes, attemptRes, attemptsRes, jobsRes, manifestsRes, registryRes, truthRes, traceRes] = await Promise.all([
      supabase
        .from("cr075_batch_runs")
        .select("id, status, config_json, merchants_in_scope, created_at, updated_at")
        .eq("id", batchRunId)
        .maybeSingle(),
      supabase
        .from("flyer_capture_attempts")
        .select("*")
        .eq("batch_run_id", batchRunId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("flyer_capture_attempts")
        .select("*")
        .eq("batch_run_id", batchRunId)
        .order("created_at", { ascending: false })
        .limit(8),
      supabase
        .from("flyer_image_capture_test_runs")
        .select("id, capture_attempt_id, merchant_source_id, merchant_id, adapter_key, status, stage, error_code, error_message, pages_captured, attempt_no, started_at, finished_at, heartbeat_at, created_at, updated_at")
        .eq("batch_run_id", batchRunId)
        .order("created_at", { ascending: false })
        .limit(500),
      supabase
        .from("flyer_image_capture_manifests" as any)
        .select("id, merchant_source_id, flyer_merchant, status, page_count_expected, page_count_found, page_count_received, error, error_json, created_at")
        .eq("batch_run_id", batchRunId)
        .order("created_at", { ascending: false })
        .limit(500),
      supabase
        .from("batch_run_page_assets")
        .select("id", { count: "exact", head: true })
        .eq("batch_run_id", batchRunId)
        .not("bucket_id", "is", null)
        .not("object_path", "is", null),
      supabase
        .from("cr075_flyer_page_truth")
        .select("id", { count: "exact", head: true })
        .eq("batch_run_id", batchRunId)
        .eq("rendered", true)
        .not("render_storage_path", "is", null),
      supabase
        .from("flyer_image_capture_trace_events")
        .select("id, created_at, level, event, stage, message, merchant_source_id, data_json, capture_attempt_id, job_id")
        .eq("batch_run_id", batchRunId)
        .order("created_at", { ascending: false })
        .limit(120),
    ]);

    if (runRes.error) errors.run = runRes.error.message;
    if (attemptRes.error) errors.latest_attempt = attemptRes.error.message;
    if (attemptsRes.error) errors.attempt_history = attemptsRes.error.message;
    if (jobsRes.error) errors.jobs = jobsRes.error.message;
    if (manifestsRes.error) errors.manifests = manifestsRes.error.message;
    if (registryRes.error) errors.registry_count = registryRes.error.message;
    if (truthRes.error) errors.truth_count = truthRes.error.message;
    if (traceRes.error) errors.capture_trace_events = traceRes.error.message;

    const runRow = (runRes.data ?? null) as any;
    const latestAttempt = (attemptRes.data ?? null) as any;
    const attemptHistory = (attemptsRes.data ?? []) as any[];
    const allJobs = (jobsRes.data ?? []) as any[];
    const allManifests = (manifestsRes.data ?? []) as any[];
    const traceEvents = (traceRes.data ?? []) as any[];
    const attemptId = latestAttempt?.id ? String(latestAttempt.id) : null;
    const latestAttemptJobs = attemptId ? allJobs.filter((j) => String(j.capture_attempt_id ?? "") === attemptId) : allJobs;

    const jobStatusCounts = latestAttemptJobs.reduce<Record<string, number>>((acc, row) => {
      const key = String(row.status ?? "UNKNOWN");
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});

    const failedJobs = latestAttemptJobs
      .filter((j) => ["FAILED", "TIMED_OUT", "CANCELLED"].includes(String(j.status ?? "").toUpperCase()) || j.error_code || j.error_message)
      .map((j) => ({
        id: j.id,
        merchant_source_id: j.merchant_source_id,
        merchant_id: j.merchant_id,
        adapter_key: j.adapter_key,
        status: j.status,
        stage: j.stage,
        error_code: j.error_code,
        error_message: j.error_message,
        pages_captured: j.pages_captured,
        attempt_no: j.attempt_no,
        started_at: j.started_at,
        finished_at: j.finished_at,
      }));

    const latestManifestBySource = new Map<string, any>();
    for (const manifest of allManifests) {
      const sourceId = String(manifest?.merchant_source_id ?? manifest?.id ?? "");
      if (!sourceId) continue;
      const existing = latestManifestBySource.get(sourceId);
      if (!existing) {
        latestManifestBySource.set(sourceId, manifest);
        continue;
      }
      const existingTs = new Date(existing.created_at ?? 0).getTime();
      const nextTs = new Date(manifest.created_at ?? 0).getTime();
      if (Number.isFinite(nextTs) && nextTs >= existingTs) {
        latestManifestBySource.set(sourceId, manifest);
      }
    }

    const dedupedManifests = Array.from(latestManifestBySource.values());
    const manifestSummary = {
      total: dedupedManifests.length,
      complete: dedupedManifests.filter((m) => String(m.status ?? "").toUpperCase() === "COMPLETE").length,
      incomplete: dedupedManifests.filter((m) => String(m.status ?? "").toUpperCase() === "CAPTURE_INCOMPLETE").length,
      failed: dedupedManifests.filter((m) => String(m.status ?? "").toUpperCase() === "FAILED").length,
      expected_pages: dedupedManifests.reduce((sum, m) => sum + Number(m.page_count_expected ?? 0), 0),
      found_pages: dedupedManifests.reduce((sum, m) => sum + Number(m.page_count_found ?? 0), 0),
      received_pages: dedupedManifests.reduce((sum, m) => sum + Number(m.page_count_received ?? m.page_count_found ?? 0), 0),
    };

    const rawRegistryPages = registryRes.count ?? 0;
    const truthRenderedPages = truthRes.count ?? 0;
    const attemptPagesTotal = Number(latestAttempt?.pages_total ?? 0);
    const effectiveRegistryPages = Math.max(rawRegistryPages, truthRenderedPages, manifestSummary.received_pages, attemptPagesTotal);
    const mismatchDiff = effectiveRegistryPages - manifestSummary.expected_pages;

    const runConfig = (runRow?.config_json ?? {}) as Record<string, unknown>;
    const scopeMerchantUuids = Array.isArray(runConfig.merchant_uuids)
      ? runConfig.merchant_uuids.map((v) => String(v))
      : [];
    const scopeMerchantIds = Array.isArray(runConfig.merchant_ids)
      ? runConfig.merchant_ids.map((v) => String(v))
      : [];
    const selectedFromConfig = scopeMerchantUuids.length > 0
      ? scopeMerchantUuids.length
      : scopeMerchantIds.length;
    const selectedMerchants = selectedFromConfig > 0 ? selectedFromConfig : Number(runRow?.merchants_in_scope ?? 0);

    let missingCaptureEnabledMerchants: string[] = [];
    if (scopeMerchantUuids.length > 0) {
      const { data: eligibleSources, error: eligibleErr } = await supabase
        .from("merchant_sources")
        .select("merchant_uuid")
        .in("merchant_uuid", scopeMerchantUuids)
        .eq("is_active", true)
        .eq("status", "active")
        .eq("flyer_image_capture_enabled", true);

      if (eligibleErr) {
        errors.eligible_sources = eligibleErr.message;
      } else {
        const enabledSet = new Set((eligibleSources ?? []).map((r: any) => String(r.merchant_uuid)));
        missingCaptureEnabledMerchants = scopeMerchantUuids.filter((uuid) => !enabledSet.has(uuid));
      }
    }

    return {
      _meta: {
        generated_at: generatedAt,
        batch_run_id: batchRunId,
        step: "S1.0C_CAPTURE",
        source: "FlyerPipelineStepsPanel.DebugButton",
      },
      local_trace: captureNetworkTrace ?? null,
      run: runRow
        ? {
          id: runRow.id,
          status: runRow.status,
          started_at: (runRow as any).started_at ?? runRow.created_at ?? null,
          completed_at: (runRow as any).completed_at ?? null,
          updated_at: runRow.updated_at,
          selected_merchants: selectedMerchants,
        }
        : null,
      latest_attempt: latestAttempt,
      attempt_history: attemptHistory,
      capture_jobs: {
        total_rows_loaded: allJobs.length,
        rows_for_latest_attempt: latestAttemptJobs.length,
        by_status: jobStatusCounts,
        failed_rows: failedJobs,
      },
      manifests: {
        total_rows_loaded: allManifests.length,
        deduped_latest_by_source: dedupedManifests,
        summary: manifestSummary,
      },
      reconciliation: {
        registry_raw_pages: rawRegistryPages,
        truth_rendered_pages: truthRenderedPages,
        latest_attempt_pages_total: attemptPagesTotal,
        manifests_received_pages: manifestSummary.received_pages,
        effective_registry_pages: effectiveRegistryPages,
        manifests_expected_pages: manifestSummary.expected_pages,
        mismatch_diff: mismatchDiff,
      },
      scope_diagnostics: {
        selected_merchants: selectedMerchants,
        config_merchant_uuids_count: scopeMerchantUuids.length,
        config_merchant_ids_count: scopeMerchantIds.length,
        missing_capture_enabled_merchant_uuids: missingCaptureEnabledMerchants,
      },
      recent_trace_events: traceEvents,
      errors: Object.keys(errors).length ? errors : null,
    };
  }, [batchRunId, captureNetworkTrace]);

  const handleCopyOcrNetworkTrace = useCallback(async () => {
    let latestAttempt: any = null;
    const errors: Record<string, string> = {};
    try {
      const { data, error } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("id, attempt_no, status, started_at, finished_at, error_message, diagnostics_json, config_json")
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S1_OCR")
        .not("status", "eq", "SUPERSEDED")
        .order("created_at", { ascending: false })
        .order("attempt_no", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) errors.latest_step_attempt = error.message;
      latestAttempt = data ?? null;
    } catch (err) {
      errors.latest_step_attempt = err instanceof Error ? err.message : String(err);
    }

    const latestAttemptId =
      latestAttempt && typeof latestAttempt.id === "string" && latestAttempt.id.length > 0
        ? latestAttempt.id
        : null;
    const latestRunIdFromDiag =
      latestAttempt && typeof latestAttempt?.diagnostics_json?.flyer_scan_run_id === "string"
        ? latestAttempt.diagnostics_json.flyer_scan_run_id
        : null;

    let recentAttempts: any[] = [];
    let runCandidates: any[] = [];
    let failedPages: any[] = [];
    let statusRows: Array<{ ocr_status?: string | null }> = [];
    try {
      const [attemptsRes, runsRes, failedPagesRes, statusRes] = await Promise.all([
        supabase
          .from("cr075_batch_run_step_attempts")
          .select("id, attempt_no, status, started_at, finished_at, error_message, diagnostics_json")
          .eq("batch_run_id", batchRunId)
          .eq("step_key", "S1_OCR")
          .order("created_at", { ascending: false })
          .order("attempt_no", { ascending: false })
          .limit(10),
        supabase
          .from("flyer_scan_runs_v2")
          .select("id, status, pages_total, current_page, primary_ocr_engine, last_progress_at, created_at, discovery_debug")
          .eq("discovery_debug->>cr075_batch_run_id", batchRunId)
          .order("created_at", { ascending: false })
          .limit(10),
        latestAttemptId
          ? supabase
            .from("flyer_scan_pages_v2")
            .select("page_number, ocr_status, ocr_attempt_no, ocr_error_code, primary_ocr_error, ocr_started_at, ocr_finished_at")
            .eq("run_id", latestAttemptId)
            .eq("ocr_status", "FAILED")
            .order("page_number", { ascending: true })
            .limit(100)
          : Promise.resolve({ data: [], error: null } as any),
        latestAttemptId
          ? supabase
            .from("flyer_scan_pages_v2")
            .select("ocr_status")
            .eq("run_id", latestAttemptId)
            .in("ocr_status", ["SUCCEEDED", "FAILED", "RUNNING", "QUEUED"])
          : Promise.resolve({ data: [], error: null } as any),
      ]);

      if (attemptsRes.error) errors.recent_attempts = attemptsRes.error.message;
      if (runsRes.error) errors.run_candidates = runsRes.error.message;
      if (failedPagesRes.error) errors.failed_pages = failedPagesRes.error.message;
      if (statusRes.error) errors.status_rows = statusRes.error.message;

      recentAttempts = attemptsRes.data ?? [];
      runCandidates = runsRes.data ?? [];
      failedPages = failedPagesRes.data ?? [];
      statusRows = (statusRes.data ?? []) as Array<{ ocr_status?: string | null }>;
    } catch (err) {
      errors.debug_bundle = err instanceof Error ? err.message : String(err);
    }

    const failureClassMap = new Map<
      string,
      { count: number; sample_pages: number[]; first_seen_at: string | null; last_seen_at: string | null }
    >();
    for (const page of failedPages) {
      const rawText = String(page.primary_ocr_error ?? "");
      const embeddedCodeMatch = rawText.match(/"code"\s*:\s*"([A-Z0-9_]+)"/i);
      const embeddedCode = embeddedCodeMatch?.[1]?.toUpperCase() ?? null;
      const code = (embeddedCode || (page.ocr_error_code as string | null) || "UNKNOWN").toUpperCase();
      const finishedAt = (page.ocr_finished_at as string | null) ?? null;
      const existing = failureClassMap.get(code) ?? {
        count: 0,
        sample_pages: [],
        first_seen_at: null,
        last_seen_at: null,
      };
      existing.count += 1;
      if (existing.sample_pages.length < 5 && typeof page.page_number === "number") {
        existing.sample_pages.push(page.page_number);
      }
      if (!existing.first_seen_at || (finishedAt && finishedAt < existing.first_seen_at)) {
        existing.first_seen_at = finishedAt;
      }
      if (!existing.last_seen_at || (finishedAt && finishedAt > existing.last_seen_at)) {
        existing.last_seen_at = finishedAt;
      }
      failureClassMap.set(code, existing);
    }

    const failureClassSummary = Array.from(failureClassMap.entries())
      .map(([error_class, meta]) => ({ error_class, ...meta }))
      .sort((a, b) => b.count - a.count || a.error_class.localeCompare(b.error_class));

    const ocrStatusSummary = statusRows.reduce(
      (acc, row) => {
        const s = row.ocr_status ?? "UNKNOWN";
        if (s === "SUCCEEDED") acc.succeeded += 1;
        else if (s === "FAILED") acc.failed += 1;
        else if (s === "RUNNING") acc.running += 1;
        else if (s === "QUEUED") acc.queued += 1;
        else acc.unknown += 1;
        return acc;
      },
      { succeeded: 0, failed: 0, running: 0, queued: 0, unknown: 0 },
    );

    const payload = {
      _meta: {
        generated_at: new Date().toISOString(),
        batch_run_id: batchRunId,
        step: "S1_OCR",
        includes: ["network_trace", "attempt_details", "s12_error_bundle"],
      },
      local_trace: ocrNetworkTrace ?? null,
      latest_step_attempt: latestAttempt,
      s12_debug_bundle: {
        ids: {
          s1_ocr_attempt_id: latestAttemptId,
          flyer_scan_run_id: latestRunIdFromDiag,
        },
        recent_attempts: recentAttempts,
        run_candidates: runCandidates,
        ocr_status_summary: {
          ...ocrStatusSummary,
          completed_terminal: ocrStatusSummary.succeeded + ocrStatusSummary.failed,
          total: statusRows.length,
        },
        failure_classes: failureClassSummary,
        failed_pages: failedPages,
      },
      errors: Object.keys(errors).length ? errors : null,
      fallback_message: !ocrNetworkTrace
        ? "No OCR local request trace captured yet. Attempt/debug data is included."
        : undefined,
    };
    const traceJson = JSON.stringify(payload, null, 2);

    if (!navigator?.clipboard?.writeText) {
      toast({
        title: "Clipboard unavailable",
        description: "Your browser does not allow clipboard writes in this context.",
        variant: "destructive",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(traceJson);
      toast({
        title: "Debug trace copied",
        description: "Latest OCR request/response trace copied to clipboard.",
      });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: err instanceof Error ? err.message : "Could not copy trace to clipboard.",
        variant: "destructive",
      });
    }
  }, [batchRunId, ocrNetworkTrace, toast]);

  const handleCopySignalsNetworkTrace = useCallback(async () => {
    let latestAttempt: any = null;
    let attemptError: string | null = null;
    try {
      const { data, error } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("id, attempt_no, status, started_at, finished_at, error_message, diagnostics_json, config_json")
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S1_SIGNALS")
        .not("status", "eq", "SUPERSEDED")
        .order("created_at", { ascending: false })
        .order("attempt_no", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) attemptError = error.message;
      latestAttempt = data ?? null;
    } catch (err) {
      attemptError = err instanceof Error ? err.message : String(err);
    }

    const payload = {
      _meta: {
        generated_at: new Date().toISOString(),
        batch_run_id: batchRunId,
        step: "S1_SIGNALS",
      },
      local_trace: signalsNetworkTrace ?? null,
      latest_step_attempt: latestAttempt,
      errors: attemptError ? { latest_step_attempt: attemptError } : null,
      fallback_message: !signalsNetworkTrace
        ? "No Signals local request trace captured yet. Latest DB attempt is included."
        : undefined,
    };
    const traceJson = JSON.stringify(payload, null, 2);

    if (!navigator?.clipboard?.writeText) {
      toast({
        title: "Clipboard unavailable",
        description: "Your browser does not allow clipboard writes in this context.",
        variant: "destructive",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(traceJson);
      toast({
        title: "Debug trace copied",
        description: "Latest Signals request/response trace copied to clipboard.",
      });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: err instanceof Error ? err.message : "Could not copy trace to clipboard.",
        variant: "destructive",
      });
    }
  }, [batchRunId, signalsNetworkTrace, toast]);

  const handleCopyCaptureNetworkTrace = useCallback(async () => {
    const payload = await buildCaptureNetworkDebugBundle();
    const traceJson = JSON.stringify(payload, null, 2);

    if (!navigator?.clipboard?.writeText) {
      toast({
        title: "Clipboard unavailable",
        description: "Your browser does not allow clipboard writes in this context.",
        variant: "destructive",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(traceJson);
      toast({
        title: "Debug trace copied",
        description: "S1.0c debug bundle copied (attempts, jobs, manifests, reconciliation, trace events).",
      });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: err instanceof Error ? err.message : "Could not copy trace to clipboard.",
        variant: "destructive",
      });
    }
  }, [buildCaptureNetworkDebugBundle, toast]);

  const handleCopyAiNetworkTrace = useCallback(async () => {
    let latestAttempt: any = null;
    let latestStepRow: any = null;
    const errors: Record<string, string> = {};

    try {
      const { data, error } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("id, attempt_no, status, started_at, finished_at, error_message, diagnostics_json, config_json")
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S1_AI")
        .not("status", "eq", "SUPERSEDED")
        .order("created_at", { ascending: false })
        .order("attempt_no", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        errors.latest_step_attempt = error.message;
      }
      latestAttempt = data ?? null;
      if (latestAttempt) {
        latestAttempt = {
          ...latestAttempt,
          status: getEffectiveAttemptStatus(latestAttempt),
        };
      }
    } catch (err) {
      errors.latest_step_attempt = err instanceof Error ? err.message : String(err);
    }

    try {
      const { data, error } = await supabase
        .from("cr075_batch_run_steps")
        .select("id, status, started_at, finished_at, duration_ms, error_message, config_json")
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S1_AI")
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        errors.latest_step_row = error.message;
      }
      latestStepRow = data ?? null;
    } catch (err) {
      errors.latest_step_row = err instanceof Error ? err.message : String(err);
    }

    const latestAttemptDiag = (latestAttempt?.diagnostics_json ?? null) as Record<string, unknown> | null;
    const hasLatestAttempt = latestAttempt !== null && latestAttempt !== undefined;
    const liveAiStatus = hasLatestAttempt ? (latestAttempt.status ?? null) : aiStepDbStatus;
    const liveAiErrorMessage = hasLatestAttempt ? (latestAttempt.error_message ?? null) : aiStepErrorMessage;
    const liveCandidatesProcessed =
      (latestAttemptDiag?.candidates_processed as number) ||
      (latestAttemptDiag?.pages_scanned as number) ||
      aiCandidatesProcessed;
    const liveCandidatesTotal =
      (latestAttemptDiag?.candidates_total as number) ||
      aiCandidatesTotal;
    const liveLastUpdatedAt =
      (latestAttemptDiag?.last_updated_at as string) ||
      (latestAttempt?.finished_at as string | null) ||
      (latestAttempt?.started_at as string | null) ||
      aiLastUpdatedAt;
    const liveDealsExtracted =
      (latestAttemptDiag?.deals_extracted as number) ||
      aiDealsExtractedFromDiag ||
      stats.dealsExtracted ||
      0;

    const payload = {
      _meta: {
        generated_at: new Date().toISOString(),
        batch_run_id: batchRunId,
        step: "S1_AI",
      },
      local_trace: aiNetworkTrace ?? null,
      latest_step_attempt: latestAttempt,
      latest_step_row: latestStepRow,
      live_summary: {
        ai_status: liveAiStatus,
        ai_error_message: liveAiErrorMessage,
        running_action: runningAction,
        candidate_pages: stats.candidatePages ?? 0,
        deals_extracted: liveDealsExtracted,
        progress: {
          candidates_processed: liveCandidatesProcessed,
          candidates_total: liveCandidatesTotal,
          last_updated_at: liveLastUpdatedAt,
        },
      },
      errors: Object.keys(errors).length ? errors : null,
      fallback_message: !aiNetworkTrace
        ? "No AI local request trace captured yet. Latest DB step/attempt state is included."
        : undefined,
    };

    const traceJson = JSON.stringify(payload, null, 2);
    if (!navigator?.clipboard?.writeText) {
      toast({
        title: "Clipboard unavailable",
        description: "Your browser does not allow clipboard writes in this context.",
        variant: "destructive",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(traceJson);
      toast({
        title: "Debug trace copied",
        description: "Latest S1.4 request/response trace copied to clipboard.",
      });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: err instanceof Error ? err.message : "Could not copy trace to clipboard.",
        variant: "destructive",
      });
    }
  }, [
    aiCandidatesProcessed,
    aiCandidatesTotal,
    aiDealsExtractedFromDiag,
    aiLastUpdatedAt,
    aiNetworkTrace,
    aiStepDbStatus,
    aiStepErrorMessage,
    batchRunId,
    runningAction,
    stats.candidatePages,
    stats.dealsExtracted,
    toast,
  ]);

  const handleCopyS14NetworkTrace = useCallback(async () => {
    let latestAttempt: any = null;
    let latestStepRow: any = null;
    const errors: Record<string, string> = {};

    try {
      const { data, error } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("id, step_key, attempt_no, status, started_at, finished_at, error_message, diagnostics_json, config_json")
        .eq("batch_run_id", batchRunId)
        .in("step_key", ["S1_4", "S1_SNIPPET"])
        .not("status", "eq", "SUPERSEDED")
        .order("created_at", { ascending: false })
        .order("attempt_no", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) errors.latest_step_attempt = error.message;
      latestAttempt = data ?? null;
      if (latestAttempt) {
        latestAttempt = {
          ...latestAttempt,
          status: getEffectiveAttemptStatus(latestAttempt),
        };
      }
    } catch (err) {
      errors.latest_step_attempt = err instanceof Error ? err.message : String(err);
    }

    try {
      const { data, error } = await supabase
        .from("cr075_batch_run_steps")
        .select("id, status, started_at, finished_at, duration_ms, error_message, config_json")
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S1_4")
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) errors.latest_step_row = error.message;
      latestStepRow = data ?? null;
    } catch (err) {
      errors.latest_step_row = err instanceof Error ? err.message : String(err);
    }

    const payload = {
      _meta: {
        generated_at: new Date().toISOString(),
        batch_run_id: batchRunId,
        step: "S1_4",
      },
      local_trace: s14NetworkTrace ?? null,
      latest_step_attempt: latestAttempt,
      latest_download_decode_diagnostics:
        Array.isArray((latestAttempt?.diagnostics_json as any)?.download_decode_diagnostics)
          ? (latestAttempt?.diagnostics_json as any)?.download_decode_diagnostics
          : null,
      latest_step_row: latestStepRow,
      errors: Object.keys(errors).length ? errors : null,
      fallback_message: !s14NetworkTrace
        ? "No S1.5 local request trace captured yet. Latest DB step/attempt state is included."
        : undefined,
    };

    if (!navigator?.clipboard?.writeText) {
      toast({
        title: "Clipboard unavailable",
        description: "Your browser does not allow clipboard writes in this context.",
        variant: "destructive",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      toast({
        title: "Debug trace copied",
        description: "Latest S1.5 request/response trace copied to clipboard.",
      });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: err instanceof Error ? err.message : "Could not copy trace to clipboard.",
        variant: "destructive",
      });
    }
  }, [batchRunId, s14NetworkTrace, toast]);

  const handleOpenS14DebugImage = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("diagnostics_json")
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S1_AI")
        .not("status", "eq", "SUPERSEDED")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      const diagnostics = (data?.diagnostics_json as Record<string, unknown> | null) ?? null;
      const pageResults = Array.isArray(diagnostics?.page_results)
        ? (diagnostics.page_results as Array<Record<string, unknown>>)
        : [];
      const imageUrl = pageResults
        .map((row) => row.page_image_url_used)
        .find((value): value is string => typeof value === "string" && value.length > 0);

      if (!imageUrl) {
        toast({
          title: "No image found",
          description: "No candidate image URL is available yet for S1.5 debugging.",
          variant: "destructive",
        });
        return;
      }

      window.open(imageUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast({
        title: "Open image failed",
        description: err instanceof Error ? err.message : "Could not open debug image.",
        variant: "destructive",
      });
    }
  }, [batchRunId, toast]);

  // Run S1.3 Signals (Candidate Scoring)
  const handleRunSignals = async (forceReprocess = false) => {
    const bypassPrereqChecks = isManualS1Mode || ocrStepDbStatus === "FAILED";
    // CR_FIX: Force refresh stats before checking prerequisites
    const latestStats = await fetchStats({ silent: true });
    const currentStats = latestStats ?? stats;

    const signalsInputPages = Math.max((currentStats.ocrPages ?? 0) - (currentStats.ocrFailed ?? 0), 0);
    const canonicalSignalsReady =
      canonicalS1Snapshot == null
        ? null
        : Boolean(canonicalS1Snapshot.ocr?.terminal && canonicalS1Snapshot.signals?.ready);
    const canonicalBlockedReason =
      canonicalS1Snapshot?.ocr?.terminal_reason ||
      canonicalS1Snapshot?.ocr?.blocked_reasons?.[0] ||
      canonicalS1Snapshot?.blockedReasons?.[0] ||
      "S1.2 not terminal";
    const ocrActiveForSignalsGate = shouldLockSignalsWhileOcrRuns(isStepActive("ocr"), canonicalSignalsReady);
    if (!bypassPrereqChecks && canonicalSignalsReady === false) {
      toast({
        title: "Signals Waiting on OCR",
        description: `Waiting for canonical S1.2 terminal reconciliation (${canonicalBlockedReason}).`,
      });
      setSignalsBlockedReason(canonicalBlockedReason);
      setSignalsBlockedUntil(Date.now() + 15000);
      return;
    }
    if (!bypassPrereqChecks && ocrActiveForSignalsGate) {
      toast({
        title: "Wait for OCR to finish",
        description: "S1.3 run is blocked while S1.2 OCR is still running.",
        variant: "destructive",
      });
      return;
    }
    if (!bypassPrereqChecks && signalsInputPages === 0) {
      toast({
        title: "No OCR-complete pages",
        description: "S1.3 needs at least one OCR-succeeded page from S1.2.",
        variant: "destructive",
      });
      return;
    }
    const previousSignalsStatus = signalsStepDbStatus;
    const signalsTotal = signalsInputPages;
    const runRequestedAtIso = new Date().toISOString();
    setSignalsRunRequestedAt(runRequestedAtIso);
    setSignalsAttemptProgress({ processed: 0, total: signalsTotal });
    setRunningAction("signals");
    setSignalsStepDbStatus("RUNNING");
    setSignalsBlockedReason(null);
    let isBackgroundMode = false;
    const requestBody = {
      batch_run_id: batchRunId,
      flyer_scan_run_id: null as string | null,
      flyer_scan_run_ids: [] as string[],
      input_ocr_scope: null as {
        attempt_ids?: string[];
        page_watermark?: string | null;
        page_counts?: Record<string, number>;
      } | null,
      force_reprocess: forceReprocess,
    };
    const extractBlockedPayload = (raw: unknown): { blocked_reason?: string; missing_dependencies?: string[]; retry_after_ms?: number; diagnostics?: Record<string, unknown> } | null => {
      let payload: unknown = raw;
      if (typeof payload === "string") {
        const trimmed = payload.trim();
        if (trimmed.length === 0) return null;
        const jsonStart = trimmed.indexOf("{");
        if (jsonStart > 0) {
          payload = trimmed.slice(jsonStart);
        }
        try {
          payload = JSON.parse(payload as string);
        } catch {
          return null;
        }
      }
      if (!payload || typeof payload !== "object") return null;
      const obj = payload as Record<string, unknown>;
      if (obj.blocked !== true) return null;
      return {
        blocked_reason: typeof obj.blocked_reason === "string" ? obj.blocked_reason : undefined,
        missing_dependencies: Array.isArray(obj.missing_dependencies)
          ? obj.missing_dependencies.map((v) => String(v))
          : undefined,
        retry_after_ms: typeof obj.retry_after_ms === "number" ? obj.retry_after_ms : undefined,
        diagnostics: (obj.diagnostics && typeof obj.diagnostics === "object")
          ? (obj.diagnostics as Record<string, unknown>)
          : undefined,
      };
    };

    try {
      setSignalsNetworkTrace({
        capturedAt: new Date().toISOString(),
        request: requestBody,
      });

      const ocrScopeIds = await resolveOcrScopeAttemptIds(currentStats.renderedPages ?? 0);
      requestBody.flyer_scan_run_ids = ocrScopeIds;
      requestBody.flyer_scan_run_id = ocrScopeIds[0] ?? null;
      try {
        const { data: latestOcrAttempt } = await supabase
          .from("cr075_batch_run_step_attempts")
          .select("diagnostics_json")
          .eq("batch_run_id", batchRunId)
          .eq("step_key", "S1_OCR")
          .not("status", "eq", "SUPERSEDED")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        const outputScope = (latestOcrAttempt?.diagnostics_json as Record<string, unknown> | null)?.output_scope as
          | {
            attempt_ids?: string[];
            page_watermark?: string | null;
            page_counts?: Record<string, number>;
          }
          | undefined;
        if (outputScope) {
          requestBody.input_ocr_scope = {
            attempt_ids: Array.isArray(outputScope.attempt_ids) ? outputScope.attempt_ids : undefined,
            page_watermark: outputScope.page_watermark ?? null,
            page_counts: outputScope.page_counts ?? undefined,
          };
        }
      } catch {
        // Non-fatal fallback: S1.3 will still validate scope server-side.
      }

      const { data, error } = await supabase.functions.invoke("batch-run-flyer-signals-s13", {
        body: requestBody,
      });

      setSignalsNetworkTrace({
        capturedAt: new Date().toISOString(),
        request: requestBody,
        response: data,
      });

      const blockedFromData = extractBlockedPayload(data);
      if (blockedFromData) {
        const reason = blockedFromData.blocked_reason || "S1.3 blocked by strict S1 gate";
        const deps = (blockedFromData.missing_dependencies ?? []).join(", ");
        const retryAfterMs = Math.max(1000, Number(blockedFromData.retry_after_ms ?? 15000));
        toast({
          title: "Signals Waiting on OCR",
          description: deps ? `${reason} (${deps})` : reason,
        });
        setSignalsBlockedReason(reason);
        setSignalsStepDbStatus(previousSignalsStatus ?? "NOT_STARTED");
        setSignalsBlockedUntil(Date.now() + retryAfterMs);
        setRunningAction(null);
        setSignalsRunRequestedAt(null);
        fetchSignalsStepStatus();
        return;
      }
      if (error) throw error;
      if (data?.blocked) {
        const reason = String(data?.blocked_reason || "S1.3 blocked by strict S1 gate");
        const retryAfterMs = Math.max(1000, Number(data?.retry_after_ms ?? 15000));
        toast({
          title: "Signals Waiting on OCR",
          description: reason,
        });
        setSignalsBlockedReason(reason);
        setSignalsStepDbStatus(previousSignalsStatus ?? "NOT_STARTED");
        setSignalsBlockedUntil(Date.now() + retryAfterMs);
        setRunningAction(null);
        setSignalsRunRequestedAt(null);
        fetchSignalsStepStatus();
        return;
      }
      const diagnostics = data?.diagnostics as Record<string, unknown> | undefined;
      const status = String(data?.status ?? diagnostics?.status ?? "").toUpperCase();
      const terminalStatus = status === "COMPLETE" || status === "PARTIAL" || status === "FAILED";

      if (!terminalStatus) {
        isBackgroundMode = true;
        setSignalsStepDbStatus("RUNNING");
        toast({
          title: forceReprocess ? "Signals Re-run Started" : "Signals Scoring Started",
          description: "Scoring is running in background. Progress will update live.",
        });
        fetchSignalsStepStatus();
        fetchStats();
        return;
      }

      toast({
        title: status === "FAILED"
          ? "Signals Scoring Failed"
          : forceReprocess ? "Signals Re-run Complete" : "Signals Scoring Complete",
        description: status === "FAILED"
          ? (String(data?.error || diagnostics?.error || "Signals scoring failed."))
          : `Scored ${diagnostics?.pages_scanned ?? data?.pages_scored ?? 0} pages, found ${diagnostics?.candidates_found ?? data?.candidates_found ?? 0} candidates.`,
        variant: status === "FAILED" ? "destructive" : "default",
      });
      setSignalsBlockedReason(null);
      setSignalsStepDbStatus(status);
      fetchStats();
      onRefresh();
    } catch (err) {
      console.error("[FlyerPipeline] Signals scoring failed:", err);
      const rawMessage = err instanceof Error ? err.message : String(err ?? "Unknown error");
      const errObj = err as { context?: { status?: number; body?: unknown }; details?: unknown };
      const blockedFromError =
        extractBlockedPayload(errObj?.context?.body) ||
        extractBlockedPayload(errObj?.details) ||
        extractBlockedPayload(rawMessage);
      if ((errObj?.context?.status === 409 && blockedFromError) || blockedFromError) {
        const reason = blockedFromError?.blocked_reason || "S1.3 is waiting for S1.2 to reach terminal";
        const deps = (blockedFromError?.missing_dependencies ?? []).join(", ");
        const retryAfterMs = Math.max(1000, Number(blockedFromError?.retry_after_ms ?? 15000));
        toast({
          title: "Signals Waiting on OCR",
          description: deps ? `${reason} (${deps})` : reason,
        });
        setSignalsBlockedReason(reason);
        setSignalsStepDbStatus(previousSignalsStatus ?? "NOT_STARTED");
        setSignalsBlockedUntil(Date.now() + retryAfterMs);
        setRunningAction(null);
        setSignalsRunRequestedAt(null);
        fetchSignalsStepStatus();
        return;
      }
      const transportFailure = /failed to send a request to the edge function|fetch failed|network/i.test(rawMessage);

      setSignalsNetworkTrace({
        capturedAt: new Date().toISOString(),
        request: requestBody,
        error: {
          name: err instanceof Error ? err.name : "InvokeError",
          message: rawMessage,
          stack: err instanceof Error ? err.stack : undefined,
        },
      });

      toast({
        title: "Signals Scoring Failed",
        description: transportFailure
          ? "Could not reach the Edge Function. No Signals status was changed; retry in a moment."
          : rawMessage,
        variant: "destructive",
      });
      // Transport/invoke errors are not equivalent to a true S1.3 attempt failure.
      setSignalsStepDbStatus(previousSignalsStatus ?? null);
      setRunningAction(null);
      setSignalsRunRequestedAt(null);
    } finally {
      if (!isBackgroundMode) {
        setRunningAction((prev) => prev === "signals" ? null : prev);
      }
    }
  };

  const handleRunAiScan = async () => {
    const requestBody = { batch_run_id: batchRunId };
    const bypassPrereqChecks = isManualS1Mode;

    // CR_FIX: Force refresh stats before checking prerequisites
    const latestStats = await fetchStats({ silent: true });
    const currentStats = latestStats ?? stats;

    if (!bypassPrereqChecks && currentStats.candidatePages === 0) {
      setAiNetworkTrace({
        capturedAt: new Date().toISOString(),
        request: requestBody,
        response: {
          blocked: true,
          reason: "NO_CANDIDATES",
          message: currentStats.ocrPages > 0
            ? "No candidates found — nothing to send to AI. Try adjusting signal weights or threshold."
            : "Run S1.2 OCR first.",
        },
      });
      const msg = currentStats.ocrPages > 0
        ? "No candidates found — nothing to send to AI. Try adjusting signal weights or threshold."
        : "Run S1.2 OCR first.";
      toast({
        title: "Cannot Run AI Deep Scan",
        description: msg,
        variant: "destructive",
      });
      return;
    }

    setAiStepDbStatus("RUNNING");
    setAiStepErrorMessage(null);
    setRunningAction("ai");
    try {
      setAiNetworkTrace({
        capturedAt: new Date().toISOString(),
        request: requestBody,
      });

      const { data, error } = await supabase.functions.invoke("batch-run-flyer-ai-scan", {
        body: requestBody,
      });

      setAiNetworkTrace({
        capturedAt: new Date().toISOString(),
        request: requestBody,
        response: data,
      });

      if (error) throw error;

      if (data?.blocked) {
        setAiStepDbStatus("FAILED");
        setAiStepErrorMessage(data?.message || "No candidate pages found.");
        setRunningAction(null);
        toast({
          title: "AI Scan Blocked",
          description: data?.message || "No candidates available",
          variant: "destructive",
        });
        fetchAiStepStatus();
        return;
      }

      toast({
        title: "AI Deep Scan Complete",
        description: `Extracted ${data?.deals_extracted || 0} deals from ${data?.candidates_scanned || 0} candidate pages.`,
      });
      fetchStats();
      fetchAiStepStatus();
      onRefresh();
    } catch (err) {
      console.error("[FlyerPipeline] AI scan failed:", err);
      setAiNetworkTrace({
        capturedAt: new Date().toISOString(),
        request: requestBody,
        error: {
          name: err instanceof Error ? err.name : "InvokeError",
          message: err instanceof Error ? err.message : String(err ?? "Unknown error"),
          stack: err instanceof Error ? err.stack : undefined,
        },
      });
      toast({
        title: "AI Scan Failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
      fetchAiStepStatus();
      setRunningAction(null);
    }
  };

  // CR226: Scroll to S0 Scan Settings and open the accordion
  const handleScrollToS0 = useCallback(() => {
    // Find the S0 scan settings accordion trigger and click it to open
    const s0Trigger = document.querySelector('[data-s0-scan-settings-trigger]');
    if (s0Trigger) {
      s0Trigger.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // If it's a collapsible that's closed, click to open
      (s0Trigger as HTMLElement).click();
    } else {
      // Fallback: scroll to top of the flyer pipeline panel
      const pipelineCard = document.querySelector('[data-flyer-pipeline-panel]');
      if (pipelineCard) {
        pipelineCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, []);

  const autoModeStepSequence: AutoModeStepKey[] = getS1AutoModeStepSequence(flyerCaptureMode);

  const captureInputsSettledForOcr = useMemo(() => {
    if (flyerCaptureMode !== "IMAGE_CAPTURE") {
      return stats.renderedPages > 0;
    }
    const hasFrozenScope =
      !!captureScopeV1 &&
      !!captureAttemptInfo.id &&
      captureScopeV1.capture_attempt_id === captureAttemptInfo.id;
    if (captureAttemptInfo.status !== "complete" || stats.captureIncomplete) {
      return false;
    }
    if (!hasFrozenScope) {
      return false;
    }
    const expectedFromScope = Number(captureScopeV1?.final_page_count ?? 0);
    if (expectedFromScope > 0) {
      return (stats.truthRenderedPages ?? 0) >= expectedFromScope;
    }
    if (captureExpectedPages > 0) {
      return (stats.truthRenderedPages ?? 0) >= captureExpectedPages;
    }
    return stats.renderedPages > 0;
  }, [
    flyerCaptureMode,
    captureScopeV1,
    captureAttemptInfo.id,
    captureAttemptInfo.status,
    stats.captureIncomplete,
    stats.renderedPages,
    stats.truthRenderedPages,
    captureExpectedPages,
  ]);

  const canonicalSignalsReady = canonicalS1Snapshot == null
    ? null
    : Boolean(canonicalS1Snapshot.ocr?.terminal && canonicalS1Snapshot.signals?.ready);
  const canonicalSignalsBlockedReason =
    canonicalS1Snapshot?.ocr?.terminal_reason ||
    canonicalS1Snapshot?.ocr?.blocked_reasons?.[0] ||
    canonicalS1Snapshot?.blockedReasons?.[0] ||
    signalsBlockedReason ||
    null;
  const signalsCooldownMs = Math.max(0, signalsBlockedUntil - nowTs);
  const signalsCooldownSeconds = Math.ceil(signalsCooldownMs / 1000);
  const signalsWaitingState = (canonicalSignalsReady === false) || signalsCooldownMs > 0;
  const ocrActiveForSignalsLock = shouldLockSignalsWhileOcrRuns(isStepActive("ocr"), canonicalSignalsReady);
  const scopeSettlingActive = snapshotStabilityPhase === "SETTLING" && canonicalS1Snapshot !== null;



  const getStepStatus = useCallback((stepKey: AutoModeStepKey): "NOT_STARTED" | "RUNNING" | "COMPLETE" | "PARTIAL" | "FAILED" => {
    switch (stepKey) {
      case "S1_CAPTURE": {
        const captureRunning = runningAction === "capture" || captureAttemptInfo.status === "running" || captureAttemptInfo.status === "queued";
        // CR_FIX: Gate on Python script completion via truth table.
        // jobs flip SUCCEEDED when they hand off to the Python listener — not when
        // images land in storage. cr075_flyer_page_truth is written by the Python
        // script per rendered page. Only advance to S1.2 when all expected pages
        // are present with rendered_at populated.
        const pythonScriptComplete =
          captureExpectedPages === 0
            ? captureAttemptInfo.status === "complete" // no pages expected — trust attempt status
            : (stats.truthRenderedPages ?? 0) >= captureExpectedPages;
        const captureContractComplete = flyerCaptureMode === "IMAGE_CAPTURE" ? captureInputsSettledForOcr : pythonScriptComplete;

        const isStillProcessing =
          captureAttemptInfo.status === "complete" && !captureContractComplete;

        const captureFailed = captureAttemptInfo.status === "failed";
        const capturePartial = captureAttemptInfo.status === "complete" && captureAttemptInfo.merchantsFailed > 0 && !isStillProcessing;
        const captureComplete =
          captureAttemptInfo.status === "complete" &&
          !stats.captureIncomplete &&
          captureContractComplete;

        if (captureRunning || isStillProcessing) return "RUNNING";
        if (captureFailed) return "FAILED";
        if (capturePartial) return "PARTIAL";
        if (captureComplete) return "COMPLETE";
        return "NOT_STARTED";
      }
      case "S1_COLLECT":
        if (isStepActive("collect")) return "RUNNING";
        if (collectStepInfo?.status === "COMPLETE" || collectStepInfo?.status === "PARTIAL") return collectStepInfo.status;
        if (collectStepInfo?.status === "FAILED") return "FAILED";
        return stats.flyersTotal > 0 ? "COMPLETE" : "NOT_STARTED";
      case "S1_RENDER":
        if (runningAction === "render") return "RUNNING";
        if (stats.pendingRenderFlyers === 0 && stats.renderedFlyers > 0) return "COMPLETE";
        return "NOT_STARTED";
      case "S1_SIGNALS":
        if (isStepActive("signals")) return "RUNNING";
        if (signalsWaitingState) return "NOT_STARTED";
        if (signalsStepDbStatus === "COMPLETE") return "COMPLETE";
        if (signalsStepDbStatus === "PARTIAL") return "PARTIAL";
        if (signalsStepDbStatus === "FAILED" && (canonicalSignalsReady as boolean | null) !== false) return "FAILED";
        return "NOT_STARTED";
      case "S1_OCR":
        if (isStepActive("ocr")) return "RUNNING";
        if (ocrStepDbStatus === "COMPLETE") return "COMPLETE";
        if (ocrStepDbStatus === "PARTIAL") return "PARTIAL";
        if (ocrStepDbStatus === "FAILED") return "FAILED";
        return "NOT_STARTED";
      case "S1_AI":
        // CR480: Use DB status as authoritative — runningAction is just a local hint
        if (isStepActive("ai")) return "RUNNING";
        if (aiStepDbStatus === "FAILED" || aiStepDbStatus === "FAILED_MISMATCH") return "FAILED";
        if (aiStepDbStatus === "COMPLETE" || aiStepDbStatus === "COMPLETE_WITH_ERRORS") return "COMPLETE";
        if (stats.aiFlyersComplete > 0 && stats.dealsExtracted > 0) return "COMPLETE";
        return "NOT_STARTED";
      case "S1_4":
        if (s14State.status === "running") return "RUNNING";
        if (s14State.status === "failed") return "FAILED";
        if (s14State.status === "complete") return "COMPLETE";
        return "NOT_STARTED";
      default:
        return "NOT_STARTED";
    }
  }, [collectStepInfo, runningAction, stats, ocrStepDbStatus, signalsStepDbStatus, aiStepDbStatus, s14State.status, captureAttemptInfo.status, captureAttemptInfo.merchantsFailed, signalsWaitingState, canonicalSignalsReady, flyerCaptureMode, captureInputsSettledForOcr, captureExpectedPages]);

  const canRunStep = useCallback((stepKey: AutoModeStepKey): boolean => {
    if (isManualS1Mode) {
      return runningAction === null && !s14State.isRunning;
    }
    if (scopeSettlingActive && stepKey !== "S1_CAPTURE" && stepKey !== "S1_COLLECT" && stepKey !== "S1_RENDER") {
      return false;
    }
    const succeededOcrPages = Math.max((stats.ocrPages ?? 0) - (stats.ocrFailed ?? 0), 0);
    const ocrRunning = ocrActiveForSignalsLock;
    switch (stepKey) {
      case "S1_CAPTURE":
        return runningAction === null;
      case "S1_COLLECT":
        return !!onRunCollect && runningAction === null;
      case "S1_RENDER":
        return stats.pendingRenderFlyers > 0 && runningAction === null;
      case "S1_SIGNALS":
        return (
          succeededOcrPages > 0 &&
          !ocrRunning &&
          runningAction === null &&
          (canonicalSignalsReady === null
            ? (ocrStepDbStatus === "COMPLETE" || ocrStepDbStatus === "PARTIAL")
            : canonicalSignalsReady) &&
          Date.now() >= signalsBlockedUntil
        );
      case "S1_OCR":
        return captureInputsSettledForOcr && runningAction === null;
      case "S1_AI":
        return stats.candidatePages > 0 && runningAction === null;
      case "S1_4":
        return s14State.canRun && !s14State.isRunning && runningAction === null;
      default:
        return false;
    }
  }, [isManualS1Mode, onRunCollect, ocrStepDbStatus, runningAction, s14State.canRun, s14State.isRunning, stats, signalsBlockedUntil, captureInputsSettledForOcr, canonicalSignalsReady, scopeSettlingActive, ocrActiveForSignalsLock]);

  const triggerStep = useCallback(async (stepKey: AutoModeStepKey): Promise<void> => {
    switch (stepKey) {
      case "S1_CAPTURE":
        await handleRunCapture(false, false);
        break;
      case "S1_COLLECT":
        await handleRunCollect();
        break;
      case "S1_RENDER":
        // Render is dialog-based, so we open the dialog and wait for completion
        setRenderDialogOpen(true);
        break;
      case "S1_SIGNALS":
        await handleRunSignals();
        break;
      case "S1_OCR":
        await handleRunOcrScan();
        break;
      case "S1_AI":
        await handleRunAiScan();
        break;
      case "S1_4":
        await s14State.handleRun();
        break;
    }
  }, [handleRunCapture, handleRunOcrScan, handleRunSignals, handleRunAiScan, s14State, handleRunCollect]);

  const findNextStep = useCallback((): AutoModeStepKey | null => {
    for (const stepKey of autoModeStepSequence) {
      const status = getStepStatus(stepKey);
      // Skip completed steps
      if (status === "COMPLETE") continue;
      // Return current step if it needs to run
      return stepKey;
    }
    return null; // All steps complete
  }, [getStepStatus, autoModeStepSequence]);

  const stopAutoMode = useCallback((reason: AutoModeStopReason) => {
    setAutoMode(prev => ({
      ...prev,
      enabled: false,
      status: reason === null ? "COMPLETE" : "STOPPED",
      stoppedAt: new Date().toISOString(),
      stopReason: reason,
    }));

    if (reason && reason !== "USER_STOP") {
      toast({
        title: "Auto Mode Stopped",
        description: `Pipeline stopped at ${autoModeRef.current.currentStepKey ? AUTO_STEP_LABELS[autoModeRef.current.currentStepKey] : "unknown step"}: ${reason}`,
        variant: "destructive",
      });
    } else if (reason === "USER_STOP") {
      toast({
        title: "Auto Mode Stopped",
        description: "Pipeline stopped by user. Current step will complete.",
      });
    }
  }, [toast]);

  const startAutoMode = useCallback(async () => {
    const nextStep = findNextStep();
    if (!nextStep) {
      toast({
        title: "All Steps Complete",
        description: "Nothing to run - all flyer pipeline steps are already complete.",
      });
      return;
    }

    setAutoMode({
      enabled: true,
      status: "RUNNING",
      currentStepKey: nextStep,
      lastCompletedStepKey: null,
      startedAt: new Date().toISOString(),
      stoppedAt: null,
      stopReason: null,
    });

    toast({
      title: "Auto Mode Started",
      description: `Starting from ${AUTO_STEP_LABELS[nextStep]}...`,
    });

    // Trigger the first step
    if (canRunStep(nextStep)) {
      await triggerStep(nextStep);
    }
  }, [findNextStep, canRunStep, triggerStep, toast]);

  useEffect(() => {
    if (parentAutoSignalRef.current === parentAutoStartSignal) return;
    parentAutoSignalRef.current = parentAutoStartSignal;
    if (!autoModeEnabled) return;
    if (!isParentOrchestrating) return;
    if (autoModeRef.current.status === "RUNNING") return;
    void startAutoMode();
  }, [parentAutoStartSignal, autoModeEnabled, isParentOrchestrating, startAutoMode]);

  // CR092: Auto Mode step completion watcher
  useEffect(() => {
    if (!autoMode.enabled || autoMode.status !== "RUNNING" || !autoMode.currentStepKey) {
      return;
    }

    const currentStep = autoMode.currentStepKey;
    const currentStatus = getStepStatus(currentStep);

    // Check if current step has reached a terminal state
    if (currentStatus === "RUNNING") {
      return; // Still running, wait
    }

    // If next step has not started yet, only trigger when prerequisites are satisfied.
    if (currentStatus === "NOT_STARTED") {
      if (!canRunStep(currentStep)) return;
      void triggerStep(currentStep).catch((err) => {
        console.error("[FlyerPipeline] Auto Mode trigger failed:", err);
        stopAutoMode("FAILED");
      });
      return;
    }

    // Step completed - check result
    if (currentStatus === "FAILED") {
      stopAutoMode("FAILED");
      return;
    }

    if (currentStatus === "COMPLETE" || currentStatus === "PARTIAL") {
      // CRxxx: PARTIAL is accepted for S1 sub-steps to allow chaining even with minor failures
      if (currentStatus === "PARTIAL") {
        console.log(`[FlyerPipeline] Auto Mode: Step ${currentStep} PARTIAL — continuing chain.`);
      }
      // Check for zero output conditions
      const liveDeals = aiDealsExtractedFromDiag ?? stats.dealsExtracted ?? 0;
      if (currentStep === "S1_AI" && liveDeals === 0) {
        stopAutoMode("ZERO_OUTPUT");
        return;
      }

      // Update last completed and find next step
      const stepIndex = autoModeStepSequence.indexOf(currentStep);
      const nextStepIndex = stepIndex + 1;

      if (nextStepIndex >= autoModeStepSequence.length) {
        // All steps complete!
        setAutoMode(prev => ({
          ...prev,
          enabled: false,
          status: "COMPLETE",
          lastCompletedStepKey: currentStep,
          stoppedAt: new Date().toISOString(),
          stopReason: null,
        }));
        toast({
          title: "Auto Mode Complete",
          description: `Flyer pipeline finished! Extracted ${stats.dealsExtracted} deals.`,
        });

        // CR121: Notify parent that S1 is complete for cross-group chaining
        onS1Complete?.();
        return;
      }

      const nextStep = autoModeStepSequence[nextStepIndex];

      // Update state and trigger next step
      setAutoMode(prev => ({
        ...prev,
        currentStepKey: nextStep,
        lastCompletedStepKey: currentStep,
      }));
    }
  }, [autoMode.enabled, autoMode.status, autoMode.currentStepKey, getStepStatus, stopAutoMode, canRunStep, triggerStep, stats.dealsExtracted, aiDealsExtractedFromDiag, toast, autoModeStepSequence]);


  // CR092: Handle render dialog completion for Auto Mode
  const handleRenderCompleteWithAutoMode = useCallback(() => {
    setRenderDialogOpen(false);
    toast({
      title: "Render Complete",
      description: "Flyer pages have been rendered.",
    });
    fetchStats();
    onRefresh();
    // Auto Mode will detect completion via stats change
  }, [toast, fetchStats, onRefresh]);

  // Determine step states - Render/OCR/AI
  const stepFlags = useMemo(() => {
    const ocrTerminalPages = stats.ocrPages ?? 0;
    const renderedPages = stats.renderedPages ?? 0;
    const ocrFailedPages = stats.ocrFailed ?? 0;
    const ocrAllRenderedPagesTerminal = renderedPages > 0 && ocrTerminalPages >= renderedPages;

    return {
      renderStepComplete: stats.pendingRenderFlyers === 0 && stats.renderedFlyers > 0,
      hasRenderFailures: stats.pendingRenderFlyers > 0 && stats.renderedFlyers > 0,
      ocrStepComplete: ocrStepDbStatus === "COMPLETE" || ocrStepDbStatus === "PARTIAL",
      ocrAllRenderedPagesTerminal,
      ocrAllSuccessful: ocrStepDbStatus === "COMPLETE" && ocrAllRenderedPagesTerminal && ocrFailedPages === 0,
      collectStepComplete: collectStepInfo?.status === "COMPLETE" || collectStepInfo?.status === "PARTIAL",
      collectStepRunning: collectStepInfo?.status === "RUNNING" || runningAction === "collect",
      collectStepFailed: collectStepInfo?.status === "FAILED",
    };
  }, [
    stats.pendingRenderFlyers,
    stats.renderedFlyers,
    stats.ocrPages,
    stats.renderedPages,
    stats.ocrFailed,
    ocrStepDbStatus,
    collectStepInfo?.status,
    runningAction,
  ]);

  const collectStepEnabled = !!onRunCollect && !stepFlags.collectStepRunning;
  const captureStepRunning =
    runningAction === "capture" ||
    captureAttemptInfo.status === "running" ||
    captureAttemptInfo.status === "queued" ||
    (flyerCaptureMode === "IMAGE_CAPTURE" && captureAttemptInfo.status === "complete" && !captureInputsSettledForOcr);
  const captureStepPartialFailure = captureAttemptInfo.status === "complete" && captureAttemptInfo.merchantsFailed > 0;
  const captureStepFailed = captureAttemptInfo.status === "failed" || captureStepPartialFailure;
  const captureStepComplete = flyerCaptureMode === "IMAGE_CAPTURE"
    ? captureInputsSettledForOcr
    : (captureAttemptInfo.status === "complete" || stats.renderedPages > 0);
  const captureStepHasAttempt = captureAttemptInfo.status !== null;
  const captureMerchantsLabel = captureAttemptInfo.merchantsTotal > 0
    ? `${captureAttemptInfo.merchantsSucceeded}/${captureAttemptInfo.merchantsTotal} merchants`
    : captureAttemptInfo.merchantsSucceeded > 0
      ? `${captureAttemptInfo.merchantsSucceeded} merchants`
      : null;
  const captureBlockedCode = flyerCaptureMode === "IMAGE_CAPTURE"
    ? (canonicalS1Snapshot?.blockedReasons ?? []).find((reason) => String(reason).startsWith("CAPTURE_")) ?? null
    : null;
  const captureBlockedMessage =
    captureBlockedCode === "CAPTURE_SCOPE_NOT_FROZEN"
      ? "Waiting for S1.0c frozen scope snapshot."
      : captureBlockedCode === "CAPTURE_JOBS_PENDING"
        ? "Waiting for capture jobs to reach terminal status."
        : captureBlockedCode === "CAPTURE_MANIFESTS_PENDING"
          ? "Waiting for capture manifests to finish processing."
          : captureBlockedCode === "CAPTURE_PAGE_COUNT_MOVING"
            ? "Waiting for final page count to stabilize."
            : captureBlockedCode === "CAPTURE_INCOMPLETE"
              ? "Capture reported incomplete pages."
              : null;
  const ocrBlockedByCaptureContract = flyerCaptureMode === "IMAGE_CAPTURE" && !captureInputsSettledForOcr;

  const ocrTerminalPages = stats.ocrPages ?? 0;
  const renderedPages = stats.renderedPages ?? 0;
  const ocrFailedPages = stats.ocrFailed ?? 0;
  const ocrSucceededPages = Math.max(ocrTerminalPages - ocrFailedPages, 0);
  const ocrRemainingPages = Math.max(renderedPages - ocrTerminalPages, 0);

  const hasOcrFailures = (stats.ocrFailed ?? 0) > 0;
  const hasStrandedPages = (stats.ocrQueued ?? 0) > 0 && !isStepActive("ocr");
  const signalsHasAttempt = signalsStepDbStatus !== null;
  const signalFailedPages = signalsHasAttempt
    ? Math.max(ocrSucceededPages - (stats.scoredPages ?? 0), 0)
    : 0;
  const hasSignalFailures = signalFailedPages > 0;
  const signalsIsRunning = isStepActive("signals");
  const signalsProgressTotal = signalsIsRunning && (signalsAttemptProgress?.total ?? 0) > 0
    ? (signalsAttemptProgress?.total ?? 0)
    : ocrSucceededPages;
  const signalsProgressProcessed = signalsIsRunning && signalsAttemptProgress
    ? Math.min(Math.max(signalsAttemptProgress.processed, 0), signalsProgressTotal)
    : Math.min(Math.max(stats.scoredPages ?? 0, 0), signalsProgressTotal);
  const signalsProgressRemaining = Math.max(signalsProgressTotal - signalsProgressProcessed, 0);
  const signalsProgressPct = signalsProgressTotal > 0
    ? Math.round((signalsProgressProcessed / signalsProgressTotal) * 100)
    : 0;
  const signalsStepComplete = signalsStepDbStatus === "COMPLETE" || signalsStepDbStatus === "PARTIAL";
  // CR471: Use top-level aiStepComplete status
  const aiStepFailed = aiStepDbStatus === "FAILED" || aiStepDbStatus === "FAILED_MISMATCH";
  const s14StepModeStatus =
    s14State.status === "running" ? "RUNNING" :
      s14State.status === "complete" ? "COMPLETE" :
        s14State.status === "failed" ? "FAILED" :
          s14State.status === "ready" ? "READY" :
            null;

  const renderStepEnabled = stats.pendingRenderFlyers > 0;
  // CR455: Enable OCR if rendered pages exist
  const ocrStepEnabled = stats.renderedPages > 0;
  const aiStepEnabled = stats.candidatePages > 0;

  // CR092: Determine if Auto Mode can start
  const canStartAutoMode = runningAction === null && autoMode.status !== "RUNNING" && findNextStep() !== null;

  // CR111: Client-safe accordion state with localStorage persistence
  // Initialize with constant default to avoid SSR/hydration mismatch
  const STORAGE_KEY = `flyer-pipeline-expanded-${batchRunId}`;
  const [isExpanded, setIsExpanded] = useState(true); // Default expanded

  // CR111: Read persisted state only on client-side mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === "false" || stored === "0") {
        setIsExpanded(false);
      } else if (stored === "true" || stored === "1") {
        setIsExpanded(true);
      }
    } catch {
      // localStorage not available, use default
    }
  }, [STORAGE_KEY]);

  // CR111: Persist state changes to localStorage (client-side only)
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, isExpanded ? "1" : "0");
    } catch {
      // localStorage not available, ignore
    }
  }, [isExpanded, STORAGE_KEY]);

  // CR111: Handler for accordion state changes (must be before early returns)
  const handleAccordionChange = useCallback((value: string) => {
    setIsExpanded(value === "flyer");
  }, []);

  useEffect(() => {
    const nextStability = advanceSnapshotStability(
      snapshotStabilityRef.current,
      canonicalS1Snapshot,
      Date.now(),
    );
    if (nextStability.phase !== snapshotStabilityRef.current.phase) {
      console.log(`[S1 Snapshot Stability] ${nextStability.phase === "SETTLING" ? "SETTLING_ENTER" : "SETTLING_EXIT"} polls=${nextStability.stablePollCount} stableMs=${nextStability.stableMs}`);
    }
    snapshotStabilityRef.current = nextStability;
    setSnapshotStabilityPhase(nextStability.phase);
  }, [canonicalS1Snapshot]);

  const pipelineBlockedReasons = useMemo(() => {
    const reasons: string[] = [];
    if (scopeSettlingActive) reasons.push("SNAPSHOT_SETTLING");
    if (captureBlockedCode) reasons.push(captureBlockedCode);
    if (canonicalSignalsBlockedReason) reasons.push(canonicalSignalsBlockedReason);
    return Array.from(new Set(reasons.filter(Boolean)));
  }, [scopeSettlingActive, captureBlockedCode, canonicalSignalsBlockedReason]);

  const pipelineTerminalStatus = (() => {
    if (aiStepComplete && (stats?.dealsExtracted ?? 0) > 0) return "COMPLETE";
    if (stepFlags.collectStepFailed || ocrStepDbStatus === "FAILED" || aiStepFailed) return "FAILED";
    return null;
  })();

  const pipelineDisplayStatus: PipelineDisplayStatus = getPipelineDisplayStatus({
    status: pipelineTerminalStatus,
    isRunning: runningAction !== null || autoMode?.status === "RUNNING",
    blockedReasons: pipelineBlockedReasons,
  });
  const pipelineStatusHelperText = getPipelineStatusHelperText(pipelineDisplayStatus, {
    batchAutoRunEnabled: isS1AutoRunGlobal,
  });

  // Show panel even when total is 0 if collect is available (to run the first collect)
  const showPanel = stats.flyersTotal > 0 || !!onRunCollect;

  // CR111B: Replace silent return null with visible empty state
  if (!showPanel) {
    return (
      <Card className="border-dashed opacity-60">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-muted-foreground">
            <FileText className="h-4 w-4" />
            S1: Flyer Extraction
          </CardTitle>
          <CardDescription>
            No flyer sources enabled or no collect handler available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card>
        {/* CR111: Outer accordion for entire flyer section using shadcn Accordion */}
        <Accordion
          type="single"
          collapsible
          value={isExpanded ? "flyer" : ""}
          onValueChange={handleAccordionChange}
        >
          <AccordionItem value="flyer" className="border-0">
            {/* Custom trigger with header content */}
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <AccordionTrigger className="hover:no-underline p-0 [&[data-state=open]>svg]:rotate-180">
                    <div className="flex items-center gap-2 text-left">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        S1: Flyer Extraction
                      </CardTitle>
                      {/* CR111: Pipeline status badge for quick visibility */}
                      {pipelineDisplayStatus === "RUNNING" && (
                        <Badge variant="outline" className="bg-primary/10 text-primary gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Running
                        </Badge>
                      )}
                      {pipelineDisplayStatus === "COMPLETE" && (
                        <Badge variant="outline" className="bg-primary/10 text-primary gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Complete
                        </Badge>
                      )}
                      {pipelineDisplayStatus === "SETTLING" && (
                        <Badge variant="outline" className="border-amber-300 text-amber-700 gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Settling
                        </Badge>
                      )}
                      {pipelineDisplayStatus === "BLOCKED" && (
                        <Badge variant="outline" className="border-amber-300 text-amber-700 gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Blocked
                        </Badge>
                      )}
                      {pipelineDisplayStatus === "FAILED" && (
                        <Badge variant="destructive" className="gap-1">
                          <XCircle className="h-3 w-3" />
                          Failed
                        </Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  {/* CR111: Inline summary chips (always visible) */}
                  <div className="flex flex-wrap gap-2 text-sm mt-2">
                    <Badge variant={(stats?.renderedPages ?? 0) > 0 ? "default" : "secondary"}>
                      {stats?.renderedPages ?? 0} rendered
                    </Badge>
                    <Badge variant={(stats?.ocrPages ?? 0) > 0 ? "default" : "secondary"}>
                      {stats?.ocrPages ?? 0} OCRs
                    </Badge>
                    <Badge variant={(stats?.candidatePages ?? 0) > 0 ? "default" : "secondary"}>
                      {stats?.candidatePages ?? 0} {stats?.candidatePages === 1 ? 'candidate' : 'candidates'}
                    </Badge>
                    <Badge variant={(aiDealsExtractedFromDiag ?? stats?.dealsExtracted ?? 0) > 0 ? "default" : "secondary"}>
                      {aiDealsExtractedFromDiag ?? stats?.dealsExtracted ?? 0} {(aiDealsExtractedFromDiag ?? stats?.dealsExtracted) === 1 ? 'deal' : 'deals'}
                    </Badge>
                    <Badge variant={(s14State.locatedCount ?? 0) > 0 ? "default" : "secondary"}>
                      {s14State.locatedCount ?? 0} located
                    </Badge>
                    <Badge variant={(s14State.croppedCount ?? 0) > 0 ? "default" : "secondary"}>
                      {s14State.croppedCount ?? 0} cropped
                    </Badge>
                  </div>
                  {pipelineStatusHelperText && (
                    <p className="text-xs text-amber-700 mt-2">{pipelineStatusHelperText}</p>
                  )}
                </div>
                {/* CR121: Auto Mode toggle + Run button */}
                {!hideLocalAutoControls && (
                  <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                    {/* CR121: Persisted Auto toggle */}
                    <div className="flex items-center gap-2">
                      <Label
                        htmlFor="auto-s1"
                        className="text-xs text-muted-foreground cursor-pointer"
                      >
                        Step Auto (local convenience)
                      </Label>
                      <Switch
                        id="auto-s1"
                        checked={autoModeEnabled}
                        onCheckedChange={(checked) => onAutoModeToggle?.(checked)}
                        disabled={autoMode?.status === "RUNNING" || isParentOrchestrating}
                        className="data-[state=checked]:bg-primary"
                      />
                    </div>
                    <span className="text-[11px] text-muted-foreground">
                      Local-only; does not chain S2/S4/S5.
                    </span>

                    {/* Auto mode indicator */}
                    {autoModeEnabled && autoMode?.status !== "RUNNING" && !isParentOrchestrating && (
                      <Badge variant="secondary" className="text-xs gap-1">
                        <FastForward className="h-3 w-3" />
                        Auto
                      </Badge>
                    )}

                    {/* Stop / Run button */}
                    {autoMode?.status === "RUNNING" ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          stopAutoMode("USER_STOP");
                        }}
                        className="gap-2"
                      >
                        <Square className="h-4 w-4" />
                        Stop
                      </Button>
                    ) : (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (autoModeEnabled) {
                            startAutoMode();
                          } else {
                            // Manual run - just trigger the next step
                            const nextStep = findNextStep();
                            if (nextStep) {
                              triggerStep(nextStep);
                            }
                          }
                        }}
                        className="gap-2"
                      >
                        <Play className="h-4 w-4" />
                        {autoModeEnabled ? "Run Step Auto" : "Run Step"}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>

            <AccordionContent>
              <CardContent className="space-y-3 pt-0">

                {/* CR554: Duplicate CollapsibleStepSection S1.0C removed — single render in unified step stack below */}

                {/* CR092: Auto Mode Status Banner */}
                {!hideLocalAutoControls && autoMode.status === "RUNNING" && autoMode.currentStepKey && (
                  <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                    <FastForward className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <AlertTitle className="text-blue-800 dark:text-blue-200">Auto Mode Running</AlertTitle>
                    <AlertDescription className="text-blue-700 dark:text-blue-300">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Current step: <strong>{AUTO_STEP_LABELS[autoMode.currentStepKey]}</strong></span>
                        {autoMode.lastCompletedStepKey && (
                          <span className="text-sm opacity-75">
                            (completed: {AUTO_STEP_LABELS[autoMode.lastCompletedStepKey]})
                          </span>
                        )}
                      </div>
                      {runningAction === "ocr" && isClientOcrRunning && (
                        <div className="mt-2">
                          <Progress
                            value={clientOcrProgress.total_pages > 0
                              ? (clientOcrProgress.pages_processed / clientOcrProgress.total_pages) * 100
                              : 0
                            }
                            className="h-2"
                          />
                          <p className="text-xs mt-1">
                            {clientOcrProgress.pages_processed}/{clientOcrProgress.total_pages} pages processed
                          </p>
                        </div>
                      )}
                    </AlertDescription>
                  </Alert>
                )}

                {/* CR092: Auto Mode Stopped Banner */}
                {!hideLocalAutoControls && autoMode.status === "STOPPED" && autoMode.stopReason && (
                  <Alert variant="destructive" className="bg-red-50 dark:bg-red-900/20">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Auto Mode Stopped</AlertTitle>
                    <AlertDescription>
                      Pipeline stopped at {autoMode.currentStepKey ? AUTO_STEP_LABELS[autoMode.currentStepKey] : "unknown"}: {autoMode.stopReason}
                    </AlertDescription>
                  </Alert>
                )}

                {/* CR092: Auto Mode Complete Banner */}
                {!hideLocalAutoControls && autoMode.status === "COMPLETE" && (
                  <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <AlertTitle className="text-green-800 dark:text-green-200">Pipeline Complete</AlertTitle>
                    <AlertDescription className="text-green-700 dark:text-green-300">
                      All steps completed successfully. Extracted {stats.dealsExtracted} deals.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">

                  {/* Step 1.0c: Image Capture — shown in IMAGE_CAPTURE mode, auto-run in header */}
                  {flyerCaptureMode === "IMAGE_CAPTURE" && (
                    <FlyerStepRow
                      stepNumber="1.0c"
                      stepLabel="Image Capture"
                      title=""
                      status={
                        captureStepRunning ? "running" :
                          captureStepFailed ? "failed" :
                            captureStepComplete ? "complete" :
                              "ready"
                      }
                      badges={[
                        ...(captureMerchantsLabel ? [{ label: captureMerchantsLabel }] : []),
                        ...(stats.renderedPages > 0 ? [{ label: `${stats.renderedPages} pages` }] : []),
                        ...(captureAttemptInfo.merchantsFailed > 0 ? [{ label: `${captureAttemptInfo.merchantsFailed} failed`, variant: "destructive" as const }] : []),
                      ]}
                      collapsible={true}
                      defaultOpen={true}
                      persistKey={`cr553_step_${batchRunId}_S1_0C`}
                      primaryAction={
                        captureStepRunning ? {
                          label: "Running...",
                          icon: <Loader2 className="h-3 w-3 animate-spin" />,
                          onClick: () => { },
                          disabled: true,
                          variant: "outline",
                        } :
                          captureStepFailed ? {
                            label: "Retry Failed",
                            icon: <RefreshCw className="h-3 w-3" />,
                            onClick: (e) => { e.stopPropagation(); handleRunCapture(false, true); },
                            variant: "destructive",
                            disabled: runningAction !== null || autoMode.status === "RUNNING",
                          } :
                            captureStepComplete ? {
                              label: "Re-run",
                              icon: <RefreshCw className="h-3 w-3" />,
                              onClick: (e) => { e.stopPropagation(); handleRunCapture(true); },
                              variant: "outline",
                              disabled: runningAction !== null || autoMode.status === "RUNNING",
                            } :
                              (!captureStepHasAttempt || (isS1AutoRunGlobal ? false : (stepModes["S1_CAPTURE"]?.mode || "MANUAL") !== "AUTO")) ? {
                                label: "Run",
                                icon: <Play className="h-3 w-3" />,
                                onClick: (e) => { e.stopPropagation(); handleRunCapture(false); },
                                disabled: runningAction !== null || autoMode.status === "RUNNING",
                              } : undefined
                      }
                      secondaryActions={
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleCopyCaptureNetworkTrace(); }}
                            className="h-7 px-2 text-[11px]"
                          >
                            <Bug className="h-3 w-3 mr-1" />
                            Debug
                          </Button>
                          <StepModeToggle
                            batchRunId={batchRunId}
                            stepKey="S1_CAPTURE"
                            currentMode={isS1AutoRunGlobal ? "AUTO" : (stepModes["S1_CAPTURE"]?.mode || null)}
                            currentStatus={stepModes["S1_CAPTURE"]?.status || (captureAttemptInfo.status === "complete" ? "COMPLETE" : captureAttemptInfo.status === "failed" ? "FAILED" : captureAttemptInfo.status === "running" ? "RUNNING" : null)}
                            onModeChanged={(mode, status) => handleStepModeChanged("S1_CAPTURE", mode, status)}
                            disabled={isS1AutoRunGlobal || runningAction === "capture"}
                            lockReason={isS1AutoRunGlobal ? "Controlled by Batch Auto-Run (chains steps) setting" : null}
                          />
                        </div>
                      }
                    >
                      <FlyerImageCapturePanel
                        batchRunId={batchRunId}
                        ocrEngine={ocrEngineConfig.ocrEngine}
                        onRefresh={onRefresh}
                        onS1Complete={onS1Complete}
                        onRunCaptureParent={(trace) => {
                          setCaptureNetworkTrace(trace);
                        }}
                        onCaptureComplete={() => {
                          console.log("[FlyerPipeline] Capture reported complete by child panel");
                          fetchStats();
                          onRefresh();
                          onS1Complete?.();
                        }}
                      />
                    </FlyerStepRow>
                  )}

                  {/* Step 1.0: Collect Flyers — hidden in IMAGE_CAPTURE mode */}
                  {flyerCaptureMode !== "IMAGE_CAPTURE" && (
                    <FlyerStepRow
                      stepNumber="1.0"
                      stepLabel="Collect"
                      title="Collect Flyers (PDFs)"
                      status={
                        stepFlags.collectStepRunning ? "running" :
                          stepFlags.collectStepFailed ? "failed" :
                            stepFlags.collectStepComplete ? "complete" :
                              "not_started"
                      }
                      badges={stepFlags.collectStepComplete ? [{ label: `${collectStepInfo?.flyersQueued || stats.flyersTotal} flyers` }] : []}
                      collapsible={true}
                      defaultOpen={false}
                      persistKey={`cr553_step_${batchRunId}_S1_0`}
                      primaryAction={
                        stepFlags.collectStepRunning ? {
                          label: "Running...",
                          icon: <Loader2 className="h-3 w-3 animate-spin" />,
                          onClick: () => { },
                          disabled: true,
                          variant: "outline",
                        } :
                          collectStepEnabled ? {
                            label: stepFlags.collectStepFailed ? "Retry Failed" : stepFlags.collectStepComplete ? "Re-run" : "Run",
                            icon: (stepFlags.collectStepFailed || stepFlags.collectStepComplete) ? <RefreshCw className="h-3 w-3" /> : <Play className="h-3 w-3" />,
                            variant: stepFlags.collectStepFailed ? "destructive" : stepFlags.collectStepComplete ? "outline" : "default",
                            onClick: (e) => { e.stopPropagation(); handleRunCollect(); },
                            disabled: runningAction !== null || autoMode.status === "RUNNING",
                          } : undefined
                      }
                    >
                      <div className="text-sm text-muted-foreground">
                        Uploads/attaches flyer PDFs and creates staging placeholders (one per flyer).
                      </div>
                    </FlyerStepRow>
                  )}

                  {/* Step 1.1: Render Flyer Pages — hidden in IMAGE_CAPTURE mode */}
                  {flyerCaptureMode !== "IMAGE_CAPTURE" && (
                    <FlyerStepRow
                      stepNumber="1.1"
                      stepLabel="Render"
                      title="Render Flyer Pages"
                      status={
                        renderStepDbInfo.status === "SKIPPED" ? "skipped" :
                          renderStepDbInfo.status === "QUEUED" ? "queued" :
                            runningAction === "render" ? "running" :
                              stepFlags.hasRenderFailures ? "failed" :
                                stepFlags.renderStepComplete ? "complete" :
                                  renderStepEnabled ? "not_started" :
                                    "not_started"
                      }
                      badges={[
                        ...(renderStepEnabled && renderStepDbInfo.status !== "SKIPPED" ? [{ label: `${stats.pendingRenderFlyers} pending` }] : []),
                      ]}
                      collapsible={true}
                      defaultOpen={false}
                      persistKey={`cr553_step_${batchRunId}_S1_1`}
                      secondaryActions={
                        flyerCaptureMode !== "IMAGE_CAPTURE" && renderStepDbInfo.status !== "SKIPPED" ? (
                          <StepModeToggle
                            batchRunId={batchRunId}
                            stepKey="S1_RENDER"
                            currentMode={isS1AutoRunGlobal ? "AUTO" : (stepModes["S1_RENDER"]?.mode || null)}
                            currentStatus={stepModes["S1_RENDER"]?.status || renderStepDbInfo.status}
                            onModeChanged={(mode, status) => handleStepModeChanged("S1_RENDER", mode, status)}
                            disabled={isS1AutoRunGlobal || runningAction === "render"}
                            lockReason={isS1AutoRunGlobal ? "Controlled by Batch Auto-Run (chains steps) setting" : null}
                          />
                        ) : undefined
                      }
                      primaryAction={
                        renderStepDbInfo.status === "SKIPPED" || (!renderStepEnabled && !stepFlags.renderStepComplete && stats.flyersTotal === 0) ? undefined :
                          runningAction === "render" ? {
                            label: "Running...",
                            icon: <Loader2 className="h-3 w-3 animate-spin" />,
                            onClick: () => { },
                            disabled: true,
                            variant: "outline",
                          } :
                            stepFlags.hasRenderFailures ? {
                              label: "Retry Failed",
                              icon: <RefreshCw className="h-3 w-3" />,
                              variant: "destructive",
                              onClick: (e) => { e.stopPropagation(); handleOpenRenderDialog(); },
                              disabled: runningAction !== null || autoMode.status === "RUNNING",
                            } :
                              (stepFlags.renderStepComplete || stats.renderedFlyers > 0) ? {
                                label: "Re-run",
                                icon: <RefreshCw className="h-3 w-3" />,
                                variant: "outline",
                                onClick: (e) => { e.stopPropagation(); handleOpenRenderDialog(); },
                                disabled: runningAction !== null || autoMode.status === "RUNNING",
                              } : {
                                label: "Run",
                                icon: <Play className="h-3 w-3" />,
                                variant: "default",
                                onClick: (e) => { e.stopPropagation(); handleOpenRenderDialog(); },
                                disabled: runningAction !== null || autoMode.status === "RUNNING",
                              }
                      }
                    >
                      <div className="text-sm text-muted-foreground space-y-2">
                        <p>Convert flyer PDFs into page images (client-side). No AI is used in this step.</p>
                        {renderStepDbInfo.status === "SKIPPED" && (renderStepDbInfo.skippedReason || renderStepDbInfo.blockedReason) && (
                          <div className="flex items-start gap-2 p-2 bg-muted rounded-md">
                            <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                            <span className="text-xs">{renderStepDbInfo.skippedReason || renderStepDbInfo.blockedReason}</span>
                          </div>
                        )}
                        {stats.flyersTotal === 0 && !renderStepDbInfo.status && (
                          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                            <AlertCircle className="h-4 w-4" />
                            <span>No flyers collected yet. Run Collect step first.</span>
                          </div>
                        )}
                      </div>
                    </FlyerStepRow>
                  )}

                  {/* Step 1.2: OCR Scan - hidden in DOWNLOAD mode */}
                  {flyerCaptureMode !== "DOWNLOAD" && (
                    <FlyerStepRow
                      stepNumber="1.2"
                      stepLabel="OCR Scan"
                      title=""
                      status={
                        runningAction === "ocr" ? "running" :
                          ocrStepDbStatus === "FAILED" ? "failed" :
                            stepFlags.ocrStepComplete ? "complete" :
                              "not_started"
                      }
                      badges={[
                        ...(ocrTerminalPages > 0 ? [{
                          label: ocrRemainingPages > 0 ? `${ocrTerminalPages} OCRs (${ocrRemainingPages} total)` : `${ocrTerminalPages} OCRs`,
                          variant: "default" as const,
                          className: stepFlags.ocrAllSuccessful ? "bg-green-600 hover:bg-green-600 border-green-600 text-white" : undefined
                        }] : []),
                        ...(stats.ocrFailed > 0 ? [{ label: `${stats.ocrFailed} failed`, variant: "destructive" as const }] : []),
                      ]}
                      collapsible={true}
                      defaultOpen={false}
                      persistKey={`cr553_step_${batchRunId}_S1_2`}
                      secondaryActions={
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleCopyOcrNetworkTrace(); }}
                            className="h-7 px-2 text-[11px]"
                          >
                            <Bug className="h-3 w-3 mr-1" />
                            Debug
                          </Button>
                          <StepModeToggle
                            batchRunId={batchRunId}
                            stepKey="S1_OCR"
                            currentMode={isS1AutoRunGlobal ? "AUTO" : (stepModes["S1_OCR"]?.mode || null)}
                            currentStatus={preferCanonicalStepStatus(ocrStepDbStatus, stepModes["S1_OCR"]?.status)}
                            onModeChanged={(mode, status) => handleStepModeChanged("S1_OCR", mode, status)}
                            disabled={isS1AutoRunGlobal || isStepActive("ocr")}
                            lockReason={isS1AutoRunGlobal ? "Controlled by Batch Auto-Run (chains steps) setting" : null}
                          />
                        </div>
                      }
                      primaryAction={
                        ocrStepDbStatus === "RUNNING" ? {
                          label: "Running...",
                          icon: <Loader2 className="h-3 w-3 animate-spin" />,
                          onClick: () => { },
                          disabled: true,
                          variant: "outline",
                        } :
                          // Show Retry Failed whenever there are failed pages—even if status is COMPLETE.
                          // The OCR edge function can mark COMPLETE while individual pages still failed (e.g. 502s).
                          hasOcrFailures ? {
                            label: "Retry Failed",
                            icon: <RefreshCw className="h-3 w-3" />,
                            onClick: (e) => { e.stopPropagation(); handleRunOcrScan(true); },
                            variant: "destructive",
                            disabled: runningAction !== null || autoMode.status === "RUNNING" || ocrBlockedByCaptureContract,
                          } :
                            (ocrStepDbStatus === "COMPLETE" || ocrStepDbStatus === "PARTIAL") ? {
                              label: "Re-run",
                              icon: <RefreshCw className="h-3 w-3" />,
                              onClick: (e) => { e.stopPropagation(); handleRunOcrScan(); },
                              variant: "outline",
                              disabled: runningAction !== null || autoMode.status === "RUNNING" || ocrBlockedByCaptureContract,
                            } :
                              {
                                label: "Run",
                                icon: <Play className="h-3 w-3" />,
                                onClick: (e) => { e.stopPropagation(); handleRunOcrScan(); },
                                disabled: runningAction !== null || autoMode.status === "RUNNING" || ocrBlockedByCaptureContract,
                              }
                      }
                    >
                      <div className="text-xs text-muted-foreground space-y-1.5">
                        <p className="leading-5">Run OCR on rendered pages and extract text for downstream scoring in S1.3.</p>
                        <div className="flex items-center gap-1.5 text-[11px]">
                          <span className="font-medium">Engine:</span>
                          <Badge variant="outline" className="text-[11px] h-5">
                            {getOcrEngineLabel(ocrEngineConfig.ocrEngine, "None")}
                          </Badge>
                        </div>
                        {stats.captureIncomplete && flyerCaptureMode === "IMAGE_CAPTURE" && (
                          <div className="flex items-center gap-2 text-destructive">
                            <XCircle className="h-4 w-4" />
                            <span>Capture incomplete — expected {stats.captureExpectedPages ?? '?'} pages, registry has {stats.registryPages ?? 0}. S1.2 blocked.</span>
                          </div>
                        )}
                        {ocrBlockedByCaptureContract && !stats.captureIncomplete && (
                          <Alert className="mt-2 border-amber-500/40 bg-amber-50 dark:bg-amber-900/20">
                            <AlertCircle className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                            <AlertTitle className="text-amber-800 dark:text-amber-200">S1.2 waiting on S1.0c terminal contract</AlertTitle>
                            <AlertDescription className="text-amber-700 dark:text-amber-300">
                              {captureBlockedMessage ??
                                "Waiting for capture jobs/manifests/scope freeze to finalize before OCR starts."}
                            </AlertDescription>
                          </Alert>
                        )}
                        {hasStrandedPages && (
                          <div className="pt-0.5">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={(e) => { e.stopPropagation(); handleRunOcrScan(false, false, true); }}
                              disabled={runningAction !== null || autoMode.status === "RUNNING" || ocrBlockedByCaptureContract}
                              className="h-7 text-[11px]"
                            >
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Retry Queued (Stranded) Pages
                            </Button>
                          </div>
                        )}
                        {hasOcrFailures && (
                          <div className="pt-0.5">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); handleRunOcrScan(false, true); }}
                              disabled={runningAction !== null || autoMode.status === "RUNNING" || ocrBlockedByCaptureContract}
                              className="h-7 text-[11px]"
                            >
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Force Re-run All Pages
                            </Button>
                          </div>
                        )}
                        {ocrPaymentRequired.count > 0 && (
                          <Alert variant="destructive" className="mt-2">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Upstream OCR billing block</AlertTitle>
                            <AlertDescription>
                              {ocrPaymentRequired.count} page(s) failed with “Payment required”. Add credits, then click “Retry Failed”.
                              {ocrPaymentRequired.sampleMessage ? ` (${ocrPaymentRequired.sampleMessage})` : ""}
                            </AlertDescription>
                          </Alert>
                        )}
                        {!stats.captureIncomplete && stats.renderedPages === 0 && (
                          <div className="flex items-center gap-2 text-amber-600">
                            <AlertCircle className="h-4 w-4" />
                            <span>{flyerCaptureMode === "IMAGE_CAPTURE" ? "No rendered pages available yet. Wait for Image Capture to complete." : "Render PDFs first"}</span>
                          </div>
                        )}
                        {stats.renderedPages > 0 && (
                          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <CheckCircle className="h-3 w-3 text-primary" />
                            <span>Rendered pages: {stats.renderedPages}{stats.registryPages != null && flyerCaptureMode === "IMAGE_CAPTURE" && ` (Registry: ${stats.registryPages})`}</span>
                          </div>
                        )}
                        {isClientOcrRunning && ocrEngineConfig.ocrEngine === "tesseract" && (
                          <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg space-y-2">
                            <div className="flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin text-primary" />
                              <span className="text-sm font-medium">Tesseract OCR: {clientOcrProgress.pages_processed}/{clientOcrProgress.total_pages} pages</span>
                            </div>
                            <Progress
                              value={clientOcrProgress.total_pages > 0 ? (clientOcrProgress.pages_processed / clientOcrProgress.total_pages) * 100 : 0}
                              className="h-2"
                            />
                            {clientOcrProgress.current_page && <p className="text-xs text-primary/70">Processing: {clientOcrProgress.current_page}</p>}
                            <p className="text-xs text-muted-foreground">OCR text extraction in progress</p>
                            <Button size="sm" variant="outline" onClick={abortClientOcr}>Cancel</Button>
                          </div>
                        )}

                        {ENABLE_FLYER_DIAGNOSTICS && (
                          <div className="pt-2 mt-1 border-t border-muted/20 space-y-1.5">
                            <div className="flex items-center justify-between gap-2">
                              <h4 className="text-[12px] font-semibold tracking-wide uppercase text-foreground/90 flex items-center gap-1.5">
                                <Search className="h-3.5 w-3.5" />
                                Diagnostics
                              </h4>
                            </div>
                            <S12DiagnosticsPanel
                              batchRunId={batchRunId}
                              isRunning={isStepActive("ocr") || ocrStepDbStatus === "IN_PROGRESS"}
                            />
                          </div>
                        )}
                      </div>
                    </FlyerStepRow>
                  )}

                  {/* Step 1.3: Signals - Candidate Scoring */}
                  {flyerCaptureMode !== "DOWNLOAD" && (
                    <FlyerStepRow
                      stepNumber="1.3"
                      stepLabel="Signals"
                      title=""
                      status={
                        signalsIsRunning ? "running" :
                          signalsWaitingState ? "not_started" :
                            signalsStepDbStatus === "FAILED" ? "failed" :
                              signalsStepDbStatus === "COMPLETE" || signalsStepDbStatus === "PARTIAL" ? "complete" :
                                "not_started"
                      }
                      badges={[
                        ...(ocrSucceededPages > 0 ? [{ label: `${ocrSucceededPages} OCR input` }] : []),
                        ...(stats.scoredPages > 0 ? [{ label: `${stats.scoredPages} pages scored` }] : []),
                        ...(signalFailedPages > 0 ? [{ label: `${signalFailedPages} failed`, variant: "destructive" as const }] : []),
                        ...(stats.candidatePages > 0 ? [{ label: `${stats.candidatePages} candidates` }] : []),
                      ]}
                      collapsible={true}
                      defaultOpen={false}
                      persistKey={`cr553_step_${batchRunId}_S1_3_SIGNALS`}
                      primaryAction={
                        runningAction === "signals" ? {
                          label: "Running...",
                          icon: <Loader2 className="h-3 w-3 animate-spin" />,
                          onClick: () => { },
                          disabled: true,
                          variant: "outline",
                        } :
                          hasSignalFailures ? {
                            label: "Retry Failed",
                            icon: <RefreshCw className="h-3 w-3" />,
                            onClick: (e) => { e.stopPropagation(); handleRunSignals(false); },
                            variant: "destructive",
                            disabled: runningAction !== null || signalsWaitingState,
                          } :
                            signalsStepComplete ? {
                              label: "Re-run",
                              icon: <RefreshCw className="h-3 w-3" />,
                              onClick: (e) => { e.stopPropagation(); handleRunSignals(true); },
                              variant: "outline",
                              disabled: runningAction !== null || signalsWaitingState,
                            } :
                              {
                                label: "Run",
                                icon: <Play className="h-3 w-3" />,
                                onClick: (e) => { e.stopPropagation(); handleRunSignals(false); },
                                disabled: runningAction !== null || signalsWaitingState,
                              }
                      }
                      secondaryActions={
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleCopySignalsNetworkTrace(); }}
                            className="h-7 px-2 text-[11px]"
                          >
                            <Bug className="h-3 w-3 mr-1" />
                            Debug
                          </Button>
                          <StepModeToggle
                            batchRunId={batchRunId}
                            stepKey="S1_SIGNALS"
                            currentMode={isS1AutoRunGlobal ? "AUTO" : (stepModes["S1_SIGNALS"]?.mode || null)}
                            currentStatus={preferCanonicalStepStatus(signalsStepDbStatus, stepModes["S1_SIGNALS"]?.status)}
                            onModeChanged={(mode, status) => handleStepModeChanged("S1_SIGNALS", mode, status)}
                            disabled={isS1AutoRunGlobal || signalsIsRunning}
                            lockReason={isS1AutoRunGlobal ? "Controlled by Batch Auto-Run (chains steps) setting" : null}
                          />
                        </div>
                      }
                    >
                      <div className="text-sm text-muted-foreground space-y-2">
                        <p>Use OCR-complete pages as input, apply signal scoring, and output candidate pages for downstream processing.</p>
                        <div className="flex flex-wrap gap-2 text-xs">
                          <Badge variant="secondary">{ocrSucceededPages} OCR input</Badge>
                          <Badge variant="secondary">{stats.scoredPages} pages scored</Badge>
                          {signalFailedPages > 0 && <Badge variant="destructive">{signalFailedPages} failed</Badge>}
                          <Badge variant="secondary">{stats.signalHitPages} signal-hit pages</Badge>
                          <Badge variant="secondary">{stats.droppedPages} dropped (below threshold)</Badge>
                          <Badge variant="default">{stats.candidatePages} candidates out</Badge>
                        </div>
                        {ocrActiveForSignalsLock && (
                          <Alert className="border-amber-500/40 bg-amber-50 dark:bg-amber-900/20">
                            <AlertCircle className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                            <AlertTitle className="text-amber-800 dark:text-amber-200">Signals locked while OCR runs</AlertTitle>
                            <AlertDescription className="text-amber-700 dark:text-amber-300">
                              S1.3 is blocked until S1.2 reaches terminal status to avoid partial-input race conditions.
                            </AlertDescription>
                          </Alert>
                        )}
                        {!ocrActiveForSignalsLock && signalsWaitingState && (
                          <Alert className="border-blue-500/40 bg-blue-50 dark:bg-blue-900/20">
                            <AlertCircle className="h-4 w-4 text-blue-700 dark:text-blue-300" />
                            <AlertTitle className="text-blue-800 dark:text-blue-200">Signals waiting on canonical S1.2 readiness</AlertTitle>
                            <AlertDescription className="text-blue-700 dark:text-blue-300">
                              {canonicalSignalsBlockedReason ?? "Waiting for S1.2 to reach terminal scope reconciliation."}
                              {signalsCooldownSeconds > 0 ? ` Retry in ~${signalsCooldownSeconds}s.` : ""}
                            </AlertDescription>
                          </Alert>
                        )}
                        {ocrPaymentRequired.count > 0 && (
                          <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>OCR credit issue detected</AlertTitle>
                            <AlertDescription>
                              {ocrPaymentRequired.count} OCR failures returned “Payment required”. Resolve credits and retry failed OCR pages before rerunning S1.3.
                              {ocrPaymentRequired.sampleMessage ? ` (${ocrPaymentRequired.sampleMessage})` : ""}
                            </AlertDescription>
                          </Alert>
                        )}
                        {signalsProgressTotal > 0 && (
                          <div className="space-y-1 pt-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="font-medium">
                                Progress: {signalsProgressProcessed} / {signalsProgressTotal}
                                {signalsProgressRemaining > 0 ? ` (${signalsProgressRemaining} remaining)` : " (complete)"}
                              </span>
                              <span className="text-muted-foreground">{signalsProgressPct}%</span>
                            </div>
                            <Progress value={signalsProgressPct} className="h-2" />
                            {ocrActiveForSignalsLock && (
                              <p className="text-[11px] text-muted-foreground">Scoring OCR pages into candidate inputs…</p>
                            )}
                          </div>
                        )}
                        {hasSignalFailures && (
                          <div className="pt-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); handleRunSignals(true); }}
                              disabled={runningAction !== null || autoMode.status === "RUNNING" || signalsWaitingState}
                            >
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Re-run All OCR Pages
                            </Button>
                          </div>
                        )}
                        {(() => {
                          const truthPersistKey = `cr553_step_${batchRunId}_TRUTH`;
                          const truthDefaultOpen = (() => {
                            try {
                              return localStorage.getItem(truthPersistKey) === "1";
                            } catch {
                              return false;
                            }
                          })();
                          return (
                            <Accordion type="single" collapsible defaultValue={truthDefaultOpen ? "truth" : undefined}>
                              <AccordionItem value="truth" className="border rounded-lg bg-muted/20">
                                <AccordionTrigger
                                  className="px-3 py-2 text-sm hover:no-underline"
                                  onClick={() => {
                                    try {
                                      const cur = localStorage.getItem(truthPersistKey) === "1";
                                      localStorage.setItem(truthPersistKey, cur ? "0" : "1");
                                    } catch {
                                      // Ignore localStorage write failures.
                                    }
                                  }}
                                >
                                  <div className="flex items-center gap-2 flex-wrap text-left">
                                    <span className="font-medium text-sm">Signal Scan Table</span>
                                    <span className="text-[10px] text-muted-foreground">Read-only</span>
                                    {ocrSucceededPages > 0 && (
                                      <span className="text-[10px] text-muted-foreground">
                                        — OCR Input {ocrSucceededPages} · Scored {stats.scoredPages} · Signal hits {stats.signalHitPages} · Dropped {stats.droppedPages} · Candidates {stats.candidatePages}
                                      </span>
                                    )}
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent className="px-3 pb-3">
                                  <FlyerTruthTable
                                    batchRunId={batchRunId}
                                    ocrStepStatus={runningAction === "signals" || signalsStepDbStatus === "RUNNING" ? "RUNNING" : signalsStepDbStatus || undefined}
                                    embedded={true}
                                    hideProgress={true}
                                    hideDataSourceBanner={true}
                                    hideAiReferences={true}
                                  />
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          );
                        })()}
                      </div>
                    </FlyerStepRow>
                  )}

                  {/* Step 1.4: Deep Scan - hidden in DOWNLOAD mode */}
                  {flyerCaptureMode !== "DOWNLOAD" && (
                    <FlyerStepRow
                      stepNumber="1.4"
                      stepLabel="Deep Scan"
                      title=""
                      subtitle="Pass A + Pass B across all candidates"
                      status={
                        runningAction === "ai" || aiStepDbStatus === "RUNNING" ? "running" :
                          aiStepFailed ? "failed" :
                            aiStepComplete ? "complete" :
                              "not_started"
                      }
                      badges={[
                        ...(aiStepComplete && !aiStepFailed ? [{ label: `${aiDealsExtractedFromDiag ?? stats.dealsExtracted} deals` }] : []),
                      ]}
                      collapsible={true}
                      defaultOpen={false}
                      persistKey={`cr553_step_${batchRunId}_S1_3`}
                      secondaryActions={
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleCopyAiNetworkTrace(); }}
                            className="h-7 px-2 text-[11px]"
                          >
                            <Bug className="h-3 w-3 mr-1" />
                            Debug
                          </Button>
                          <StepModeToggle
                            batchRunId={batchRunId}
                            stepKey="S1_AI"
                            currentMode={isS1AutoRunGlobal ? "AUTO" : (stepModes["S1_AI"]?.mode || null)}
                            currentStatus={preferCanonicalStepStatus(aiStepDbStatus, stepModes["S1_AI"]?.status)}
                            onModeChanged={(mode, status) => handleStepModeChanged("S1_AI", mode, status)}
                            disabled={isS1AutoRunGlobal || runningAction === "ai"}
                            lockReason={isS1AutoRunGlobal ? "Controlled by Batch Auto-Run (chains steps) setting" : null}
                          />
                        </div>
                      }
                      primaryAction={
                        {
                          label: aiStepFailed ? "Retry Failed" : aiStepComplete ? "Re-run" : runningAction === "ai" ? "Running..." : "Run",
                          icon: runningAction === "ai" ? <Loader2 className="h-3 w-3 animate-spin" /> :
                            (aiStepFailed || aiStepComplete) ? <RefreshCw className="h-3 w-3" /> : <Play className="h-3 w-3" />,
                          variant: aiStepFailed ? "destructive" : aiStepComplete ? "outline" : "default",
                          onClick: (e) => { e.stopPropagation(); handleRunAiScan(); },
                          disabled: runningAction !== null,
                        }
                      }
                    >
                      <div className="text-sm text-muted-foreground space-y-2">
                        {aiStepFailed && aiStepErrorMessage && (
                          <Alert variant="destructive" className="py-2">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle className="text-xs font-medium">AI Scan Failed</AlertTitle>
                            <AlertDescription className="text-xs">{aiStepErrorMessage}</AlertDescription>
                          </Alert>
                        )}
                        {aiStepDbStatus === "RUNNING" && aiLastUpdatedAt && (() => {
                          const lastUpdate = new Date(aiLastUpdatedAt).getTime();
                          const isStuck = Date.now() - lastUpdate > 90_000;
                          if (!isStuck) return null;
                          return (
                            <Alert className="py-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700">
                              <AlertCircle className="h-4 w-4 text-amber-600" />
                              <AlertTitle className="text-xs font-medium text-amber-800 dark:text-amber-300">AI scan appears stuck</AlertTitle>
                              <AlertDescription className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-2">
                                <span>Progress: {aiCandidatesProcessed}/{aiCandidatesTotal} pages — last update {Math.round((Date.now() - lastUpdate) / 1000)}s ago</span>
                                <Button size="sm" variant="outline" className="h-6 text-xs border-amber-400"
                                  onClick={(e) => { e.stopPropagation(); handleRunAiScan(); }}
                                  disabled={runningAction !== null}
                                >
                                  <RefreshCw className="h-3 w-3 mr-1" />Force Retry
                                </Button>
                              </AlertDescription>
                            </Alert>
                          );
                        })()}
                        {aiStepDbStatus === "RUNNING" && aiCandidatesTotal > 0 && (
                          <div className="text-xs text-muted-foreground">Processing: {aiCandidatesProcessed}/{aiCandidatesTotal} candidate pages</div>
                        )}
                        {aiStepComplete && !aiStepFailed && (aiRepairCount > 0 || aiSalvageCount > 0) && (
                          <div className="flex flex-wrap gap-2">
                            {aiRepairCount > 0 && <Badge variant="warning" className="text-xs gap-1">⚡ Recovered via AI Repair ({aiRepairCount} page{aiRepairCount > 1 ? "s" : ""})</Badge>}
                            {aiSalvageCount > 0 && <Badge variant="warning" className="text-xs gap-1">🔧 Recovered via Salvage ({aiSalvageCount} page{aiSalvageCount > 1 ? "s" : ""})</Badge>}
                          </div>
                        )}
                        <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="flex-1">
                            <span className="text-xs font-medium">Prompt Pack: </span>
                            {s0PromptPackName ? <span className="text-xs">{s0PromptPackName}</span> : <span className="text-xs text-destructive">Not configured</span>}
                          </div>
                          <button className="text-xs text-primary hover:underline shrink-0" onClick={(e) => { e.stopPropagation(); handleScrollToS0(); }}>Configure in S0 →</button>
                        </div>
                        {!s0PromptPackId && (
                          <Alert variant="destructive" className="py-2">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription className="text-xs">No prompt pack configured in S0 Scan Settings. Set a prompt pack in S0 to enable AI Deep Scan.</AlertDescription>
                          </Alert>
                        )}
                        {aiStepEnabled && !s0PromptPackId && <Badge variant="destructive" className="text-xs">No prompt pack</Badge>}
                        {!aiStepEnabled && stats.candidatePages === 0 && stats.ocrPages > 0 && (
                          <div className="flex items-center gap-2 text-amber-600"><AlertCircle className="h-4 w-4" /><span>No candidates found — nothing to send to AI.</span></div>
                        )}
                        {stats.candidatePages === 0 && stats.ocrPages === 0 && (
                          <div className="flex items-center gap-2 text-amber-600"><AlertCircle className="h-4 w-4" /><span>Run S1.2 OCR first</span></div>
                        )}
                        <p>Send candidate pages only to the selected AI model and prompt pack to extract deals.</p>
                        {ENABLE_FLYER_DIAGNOSTICS && <FlyerAiDiagnosticsPanel batchRunId={batchRunId} isRunning={runningAction === "ai"} />}
                      </div>
                    </FlyerStepRow>
                  )}

                  {/* Step 1.5: Locate & Crop — CR554: header-integrated - hidden in DOWNLOAD mode */}
                  {flyerCaptureMode !== "DOWNLOAD" && (
                    <FlyerStepRow
                      stepNumber="1.5"
                      stepLabel="Locate & Crop"
                      title="(2-pass)"
                      subtitle="Pass A + Pass B across all candidates"
                      status={s14State.status}
                      badges={s14State.badges}
                      collapsible={true}
                      defaultOpen={false}
                      persistKey={`cr553_step_${batchRunId}_S1_4`}
                      secondaryActions={
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleOpenS14DebugImage(); }}
                            className="h-7 px-2 text-[11px]"
                          >
                            <ImageIcon className="h-3 w-3 mr-1" />
                            Image
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleCopyS14NetworkTrace(); }}
                            className="h-7 px-2 text-[11px]"
                          >
                            <Bug className="h-3 w-3 mr-1" />
                            Debug
                          </Button>
                          <StepModeToggle
                            batchRunId={batchRunId}
                            stepKey="S1_4"
                            currentMode={isS1AutoRunGlobal ? "AUTO" : (stepModes["S1_4"]?.mode || null)}
                            currentStatus={preferCanonicalStepStatus(s14StepModeStatus, stepModes["S1_4"]?.status)}
                            onModeChanged={(mode, status) => handleStepModeChanged("S1_4", mode, status)}
                            disabled={isS1AutoRunGlobal || s14State.isRunning}
                            lockReason={isS1AutoRunGlobal ? "Controlled by Batch Auto-Run (chains steps) setting" : null}
                          />
                        </div>
                      }
                      primaryAction={{
                        label: s14State.runLabel,
                        icon: s14State.isRunning ? <Loader2 className="h-3 w-3 animate-spin" /> :
                          (s14State.runVariant === "destructive" || s14State.runVariant === "outline") ? <RefreshCw className="h-3 w-3" /> : <Play className="h-3 w-3" />,
                        variant: s14State.runVariant,
                        onClick: (e) => { e.stopPropagation(); s14State.handleRun(); },
                        disabled: s14State.isRunning,
                      }}
                    >
                      <SnippetLocatorBody batchRunId={batchRunId} stateRef={(s14State as any)._stateRef} />
                    </FlyerStepRow>
                  )}
                </div> {/* end unified step stack */}
              </CardContent>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card >

      {/* Render Dialog */}
      < RenderAllAndContinueDialog
        open={renderDialogOpen}
        onOpenChange={setRenderDialogOpen}
        batchRunId={batchRunId}
        stepId=""
        pendingFlyers={pendingFlyers}
        onComplete={handleRenderCompleteWithAutoMode}
      />
    </>
  );
};
